import React, { useState, useEffect, useCallback, useRef } from 'react';
import OidcSettings from './OidcSettings';
import { Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import flags from 'country-flag-icons/react/3x2';
import { 
  Camera, Upload, Save, Share2, Phone, Mail, Globe, 
  Linkedin, Twitter, Instagram, Github, Edit3, Eye, 
  X, Check, User, MapPin, Briefcase, Lock, LogIn, AlertCircle, 
  Plus, Trash2, ArrowLeft, Users, ExternalLink, RefreshCw,
  Download, FileText, Calendar, Video, Music, ShoppingCart, 
  Link as LinkIcon, Youtube, Facebook, MessageCircle, Sun, Moon,
  ChevronUp, ChevronDown, GripVertical, Settings, Move, ZoomIn
} from 'lucide-react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { arrayMove } from '@dnd-kit/sortable';

const API_ENDPOINT = '/api';
const APP_VERSION = require('../package.json').version; // Automatically read from package.json
const GITHUB_URL = 'https://github.com/MrCrin/swiish';

// Try to read branch info from active-branch.json (generated at build time)
let GIT_BRANCH = null;
try {
  const branchInfo = require('./active-branch.json');
  GIT_BRANCH = branchInfo.branch;
} catch (e) {
  // active-branch.json doesn't exist yet (first run before build)
  GIT_BRANCH = null;
}
const swiishTheme = require('./theme/swiish');
const minimalTheme = require('./theme/minimal');
const THEME_FILES = { swiish: swiishTheme, minimal: minimalTheme };

// Theme persistence is optional: browsers can deny access to site storage.
const readDarkModePreference = () => {
  try {
    return localStorage.getItem('darkMode');
  } catch {
    return null;
  }
};

const THEME_PRESETS = {
  swiish: [
    { name: "indigo", gradient: "from-indigo-600 to-purple-600", button: "bg-indigo-600 hover:bg-indigo-700", link: "text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-100", text: "text-indigo-600" },
    { name: "blue", gradient: "from-blue-600 to-cyan-600", button: "bg-blue-600 hover:bg-blue-700", link: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-600" },
    { name: "rose", gradient: "from-rose-500 to-orange-500", button: "bg-rose-600 hover:bg-rose-700", link: "text-rose-600 bg-rose-50 border-rose-100 hover:bg-rose-100", text: "text-rose-600" },
    { name: "emerald", gradient: "from-emerald-500 to-teal-500", button: "bg-emerald-600 hover:bg-emerald-700", link: "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-emerald-600" },
    { name: "slate", gradient: "from-slate-700 to-slate-900", button: "bg-slate-800 hover:bg-slate-900", link: "text-slate-700 bg-border-subtle border-slate-200 hover:bg-slate-200", text: "text-slate-800" }
  ],
  minimal: [
    { name: "mono-dark", gradient: "from-neutral-900 to-neutral-700", button: "bg-neutral-900 hover:bg-neutral-800", link: "text-neutral-900 bg-neutral-100 border-neutral-200 hover:bg-neutral-200", text: "text-neutral-900" },
    { name: "mono-mid", gradient: "from-neutral-700 to-neutral-500", button: "bg-neutral-700 hover:bg-neutral-600", link: "text-neutral-700 bg-neutral-50 border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" },
    { name: "mono-light", gradient: "from-neutral-300 to-neutral-200", button: "bg-neutral-200 hover:bg-neutral-300 text-neutral-900", link: "text-neutral-700 bg-white border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" }
  ]
};

// Define applyThemeCssVars outside component to ensure it's always available
// This function sets all CSS custom properties from the selected theme file
const applyThemeCssVars = (variant) => {
  const root = document.documentElement;
  const theme = THEME_FILES[variant] || swiishTheme;
  const colors = theme.colors || {};
  
  // Helper to convert camelCase to kebab-case
  const toKebabCase = (str) => str.replace(/([A-Z])/g, (match) => '-' + match).toLowerCase();
  
  // Set all color CSS variables from theme
  Object.keys(colors).forEach((colorKey) => {
    const colorValue = colors[colorKey];
    if (colorValue && typeof colorValue === 'object' && colorValue.light !== undefined) {
      const cssVarName = `--color-${toKebabCase(colorKey)}`;
      root.style.setProperty(`${cssVarName}-light`, colorValue.light);
      root.style.setProperty(`${cssVarName}-dark`, colorValue.dark);
    }
  });
  
  // Set texture variables
  const textures = theme.textures?.main || {};
  root.style.setProperty('--texture-main-light', textures.light ? `url(${textures.light})` : 'none');
  root.style.setProperty('--texture-main-dark', textures.dark ? `url(${textures.dark})` : 'none');
  root.style.setProperty('--texture-main-size', textures.size || '540px 540px');
  root.style.setProperty('--texture-main-blend-light', textures.blendLight || 'multiply');
  root.style.setProperty('--texture-main-blend-dark', textures.blendDark || 'overlay');
  root.style.setProperty('--texture-main-opacity-light', textures.opacityLight ?? 0.08);
  root.style.setProperty('--texture-main-opacity-dark', textures.opacityDark ?? 0.1);
};

// --- QR PAYLOAD STORAGE KEYS ---
const QR_STORAGE_KEY = 'swiish:lastQrPayload';

// Build a vCard string for QR code encoding (simplified for reliable scanning)
// When scanned, this will add the contact directly to the phone
const buildQrPayload = (shortCode, data) => {
  const { personal = {}, contact = {} } = data || {};

  const safe = (v, maxLen = 120) => sanitizeText(v || '').substring(0, maxLen);
  
  const firstName = safe(personal.firstName || '', 40);
  const lastName = safe(personal.lastName || '', 40);
  const nativeName = safe(personal.nativeName || '', 80);
  const fullName = `${firstName} ${lastName}`.trim();
  const company = safe(personal.company || '', 80);
  const email = safe(contact.email || '', 120);
  const phone = safe(contact.phone || '', 50);
  const website = safe(contact.website || '', 240);

  // Use short code for QR URL (always use short code for simpler QR)
  const cardUrl = typeof window !== 'undefined' && shortCode
    ? `${window.location.origin}/${shortCode}`
    : '';

  // Build vCard 3.0 format (minimal for QR scanning reliability)
  let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';
  
  if (fullName) {
    vcard += `FN:${fullName}\n`;
    vcard += `N:${lastName};${firstName};;;\n`;
  }

  if (nativeName) {
    // NICKNAME is the portable vCard 3.0 field most contact apps preserve/display
    // for an additional name. Keep our explicit extension as well for semantics.
    vcard += `NICKNAME:${nativeName}\n`;
    vcard += `X-NATIVE-NAME:${nativeName}\n`;
  }
  
  if (company) {
    vcard += `ORG:${company}\n`;
  }
  
  if (email) {
    vcard += `EMAIL;TYPE=WORK:${email}\n`;
  }
  
  if (phone) {
    vcard += `TEL;TYPE=CELL:${phone}\n`;
  }
  
  // Keep the person's/company website first, followed by the canonical Swiish card URL.
  if (website) {
    vcard += `URL:${website}\n`;
  }
  if (cardUrl && cardUrl !== website) {
    vcard += `URL:${cardUrl}\n`;
  }
  
  vcard += 'END:VCARD';

  return vcard;
};

const saveQrPayloadToStorage = (payload) => {
  try {
    if (!payload) return;
    const record = {
      payload,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(QR_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.warn('Failed to save QR payload', e);
  }
};

const loadQrPayloadFromStorage = () => {
  try {
    const raw = localStorage.getItem(QR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.payload || null;
  } catch (e) {
    console.warn('Failed to load QR payload', e);
    return null;
  }
};

// Helper function to sanitize text content
const sanitizeText = (text) => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

// Helper function to sanitize HTML content (for bio)
const sanitizeHTML = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, { 
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u'],
    ALLOWED_ATTR: []
  });
};

// --- ICONS MAPPING ---
const ICON_MAP = {
  link: LinkIcon,
  download: Download,
  file: FileText,
  calendar: Calendar,
  video: Video,
  music: Music,
  shop: ShoppingCart,
  youtube: Youtube,
  facebook: Facebook,
  whatsapp: MessageCircle,
  globe: Globe
};

function LinkGlyph({ link, className = "w-5 h-5", inheritColor = false }) {
  const [failedIconUrl, setFailedIconUrl] = useState(null);
  if (link.iconUrl && link.iconUrl !== failedIconUrl) {
    return (
      <img
        src={link.iconUrl}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailedIconUrl(link.iconUrl)}
        className={`${className} object-contain`}
      />
    );
  }
  const Icon = ICON_MAP[link.icon] || LinkIcon;
  return <Icon className={`${className} ${inheritColor ? '' : 'text-text-secondary dark:text-text-secondary-dark'}`} />;
}

// --- HIROSE LOCATION PRESETS ---
// These are editor conveniences only. Cards continue to store the original address string.
const HIROSE_OFFICE_ADDRESS = '200 Cantonment Road, #06-06/07, Southpoint, Singapore 089763';
const HIROSE_FACTORY_ADDRESS = 'No.16 Sungei Kadut Street 4, Singapore 729044';
const HIROSE_LOCATION_PRESETS = [HIROSE_OFFICE_ADDRESS, HIROSE_FACTORY_ADDRESS];

// --- DATA TEMPLATE ---
const getDefaultTemplate = (settings) => ({
  personal: {
    firstName: "New",
    lastName: "User",
    nativeName: "",
    title: "Role Title",
    company: settings?.default_organisation || "My Organisation",
    bio: "Welcome to the team.",
    location: HIROSE_OFFICE_ADDRESS
  },
  contact: {
    email: "",
    phone: "",
    website: "",
  },
  social: { linkedin: "", twitter: "", instagram: "", github: "" },
  theme: { color: "indigo", style: "modern", cardPreset: "inherit", bodyBackgroundMode: "theme", bodyBackgroundColor: "#ffffff" },
  images: {
    avatar: null,
    banner: null,
    bannerPositionX: 50,
    bannerPositionY: 50,
    bannerZoom: 1,
    bodyBackground: null,
    bodyBackgroundPositionX: 50,
    bodyBackgroundPositionY: 50,
    bodyBackgroundZoom: 1
  },
  links: [],
  privacy: {
    requireInteraction: true,  // ON by default
    clientSideObfuscation: false,  // OFF by default
    blockRobots: false  // OFF by default
  },
  sendDetails: {
    enabled: true,  // ON by default (master switch)
    whatsapp: true,  // ON by default
    email: true,  // ON by default
    call: true  // ON by default
  },
  // Typography stores overrides only. Missing values deliberately fall back to
  // Swiish's current theme/classes so future theme changes still flow through.
  typography: {},
  buttonStyles: {}
});

const TYPOGRAPHY_SIZE_OPTIONS = [12, 14, 16, 18, 20, 24, 30, 36, 42, 48];
const TYPOGRAPHY_FIELDS = [
  { key: 'name', label: 'Name', defaultSize: 30, defaultHint: 'Swiish heading · 30px' },
  { key: 'nativeName', label: 'Native Name', defaultSize: 18, defaultHint: 'Secondary text · 18px' },
  { key: 'title', label: 'Job Title', defaultSize: 18, defaultHint: 'Accent colour · 18px' },
  { key: 'company', label: 'Organisation', defaultSize: 14, defaultHint: 'Muted text · 14px' },
  { key: 'location', label: 'Location', defaultSize: 14, defaultHint: 'Subtle text · 14px' },
  { key: 'bio', label: 'Bio', defaultSize: 14, defaultHint: 'Body text · 14px' }
];


const CARD_BUTTON_GROUPS = [
  { key: 'general', label: 'General Buttons', description: 'See my details, Email, Call, custom links and social buttons.' },
  { key: 'saveContact', label: 'Save Contact', description: 'The primary Save Contact button.' },
  { key: 'sendDetails', label: 'Send your details', description: 'The main Send your details call-to-action.' }
];

const getDefaultCardAppearance = () => ({
  preset: 'light',
  bodyBackground: { mode: 'theme', color: '#ffffff', image: '', positionX: 50, positionY: 50, zoom: 1 },
  typography: {},
  buttonStyles: {}
});

const normalizeCardAppearance = (input) => {
  const defaults = getDefaultCardAppearance();
  const appearance = input && typeof input === 'object' ? input : {};
  const body = appearance.bodyBackground && typeof appearance.bodyBackground === 'object' ? appearance.bodyBackground : {};
  const typography = appearance.typography && typeof appearance.typography === 'object' ? appearance.typography : {};
  const buttonStyles = appearance.buttonStyles && typeof appearance.buttonStyles === 'object' ? appearance.buttonStyles : {};
  return {
    preset: ['light', 'dark'].includes(appearance.preset) ? appearance.preset : defaults.preset,
    bodyBackground: {
      mode: ['theme', 'color', 'image'].includes(body.mode) ? body.mode : defaults.bodyBackground.mode,
      color: /^#[0-9a-fA-F]{6}$/.test(body.color || '') ? body.color : defaults.bodyBackground.color,
      image: body.image || '',
      positionX: Number.isFinite(Number(body.positionX)) ? Number(body.positionX) : 50,
      positionY: Number.isFinite(Number(body.positionY)) ? Number(body.positionY) : 50,
      zoom: Number.isFinite(Number(body.zoom)) ? Number(body.zoom) : 1
    },
    typography,
    buttonStyles
  };
};

const mergeSparseAppearanceGroup = (organisationGroup = {}, cardGroup = {}, allowCardOverrides = true) => {
  if (!allowCardOverrides) return { ...organisationGroup };
  const output = { ...organisationGroup };
  Object.entries(cardGroup || {}).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      output[key] = { ...(organisationGroup?.[key] || {}), ...value };
    } else if (value !== undefined && value !== null && value !== '') {
      output[key] = value;
    }
  });
  return output;
};

// --- STYLES ---
// Helper functions to get color classes from settings
const getThemeGradient = (colorName, settings) => {
  if (!settings?.theme_colors) return "linear-gradient(135deg, #4f46e5, #7c3aed)";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.gradientStyle || "linear-gradient(135deg, #4f46e5, #7c3aed)";
};

const getButtonColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.buttonStyle || "#4f46e5";
};

const getLinkColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.linkStyle || "#4f46e5";
};

const getTextColor = (colorName, settings) => {
  if (!settings?.theme_colors) return "#4f46e5";
  const color = settings.theme_colors.find(c => c.name === colorName);
  return color?.textStyle || "#4f46e5";
};

// --- COLOR GENERATION UTILITIES ---
const TAILWIND_COLORS = ['indigo', 'blue', 'rose', 'emerald', 'slate', 'purple', 'cyan', 'teal', 'orange', 'pink', 'violet', 'fuchsia', 'amber', 'lime', 'green', 'yellow', 'red'];

const COMPLEMENTARY_MAP = {
  indigo: 'purple',
  blue: 'cyan',
  rose: 'orange',
  emerald: 'teal',
  slate: 'slate',
  purple: 'indigo',
  cyan: 'blue',
  teal: 'emerald',
  orange: 'rose',
  pink: 'fuchsia',
  violet: 'purple',
  fuchsia: 'pink',
  amber: 'orange',
  lime: 'green',
  green: 'emerald',
  yellow: 'amber',
  red: 'rose'
};

const getComplementaryColor = (baseColor) => {
  return COMPLEMENTARY_MAP[baseColor] || 'purple';
};

const getTailwindShades = () => {
  return [400, 500, 600, 700, 800];
};

// Tailwind color to hex mapping (for common shades)
const TAILWIND_TO_HEX = {
  indigo: { 400: '#818cf8', 500: '#6366f1', 600: '#4f46e5', 700: '#4338ca', 800: '#3730a3' },
  blue: { 400: '#60a5fa', 500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af' },
  rose: { 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 800: '#9f1239' },
  emerald: { 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46' },
  slate: { 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b' },
  purple: { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  cyan: { 400: '#22d3ee', 500: '#06b6d4', 600: '#0891b2', 700: '#0e7490', 800: '#155e75' },
  teal: { 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59' },
  orange: { 400: '#fb923c', 500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412' },
  pink: { 400: '#f472b6', 500: '#ec4899', 600: '#db2777', 700: '#be185d', 800: '#9f1239' },
  violet: { 400: '#a78bfa', 500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6' },
  fuchsia: { 400: '#f0abfc', 500: '#d946ef', 600: '#c026d3', 700: '#a21caf', 800: '#86198f' },
  amber: { 400: '#fbbf24', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e' },
  lime: { 400: '#a3e635', 500: '#84cc16', 600: '#65a30d', 700: '#4d7c0f', 800: '#365314' },
  green: { 400: '#4ade80', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534' },
  yellow: { 400: '#facc15', 500: '#eab308', 600: '#ca8a04', 700: '#a16207', 800: '#854d0e' },
  red: { 400: '#f87171', 500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b' }
};

const getTailwindColorHex = (colorName, shade = 600) => {
  return TAILWIND_TO_HEX[colorName]?.[shade] || '#4f46e5';
};

// Extract base color from existing gradient class (for migration)
const extractBaseColorFromGradient = (gradient) => {
  if (!gradient) return null;
  const match = gradient.match(/from-(\w+)-(\d+)/);
  if (match) {
    return { baseColor: match[1], shade: parseInt(match[2]) };
  }
  return null;
};

// Modal Component
function Modal({ isOpen, onClose, type = 'info', title, message, onConfirm, confirmText = 'OK', cancelText = 'Cancel', inputLabel, inputPlaceholder, inputValue, onInputChange }) {
  if (!isOpen) return null;

  const typeStyles = {
    info: { icon: AlertCircle, iconColor: 'text-info-text dark:text-info-text-dark', bgColor: 'bg-info-bg dark:bg-info-bg-dark', borderColor: 'border-info-border dark:border-info-border-dark' },
    success: { icon: Check, iconColor: 'text-success-text dark:text-success-text-dark', bgColor: 'bg-success-bg dark:bg-success-bg-dark', borderColor: 'border-success-border dark:border-success-border-dark' },
    error: { icon: AlertCircle, iconColor: 'text-error-text dark:text-error-text-dark', bgColor: 'bg-error-bg dark:bg-error-bg-dark', borderColor: 'border-error-border dark:border-error-border-dark' },
    confirm: { icon: AlertCircle, iconColor: 'text-text-muted dark:text-text-muted-dark', bgColor: 'bg-surface dark:bg-surface-dark', borderColor: 'border-border dark:border-border-dark' }
  };

  const style = typeStyles[type] || typeStyles.info;
  const Icon = style.icon;
  const hasInput = inputLabel || inputPlaceholder;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    // onClose will handle calling the onClose callback
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && hasInput) {
      handleConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-card dark:bg-card-dark rounded-card shadow-2xl max-w-sm w-full p-6 animate-in fade-in duration-200" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-12 h-12 rounded-full ${style.bgColor} flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-6 h-6 ${style.iconColor}`} />
        </div>
        {title && <h3 className="text-xl font-bold text-text-primary dark:text-text-primary-dark text-center mb-2">{title}</h3>}
        {message && <p className="text-text-secondary dark:text-text-secondary-dark text-center mb-6">{message}</p>}
        {hasInput && (
          <div className="mb-6">
            {inputLabel && <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">{inputLabel}</label>}
            <input
              type="text"
              value={inputValue || ''}
              onChange={(e) => onInputChange && onInputChange(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={inputPlaceholder}
              className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              autoFocus
            />
          </div>
        )}
        <div className="flex gap-3">
          {(type === 'confirm' || hasInput) && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-full font-medium text-text-secondary dark:text-text-secondary-dark bg-surface dark:bg-surface-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`flex-1 px-4 py-2.5 rounded-full font-bold text-white transition-colors ${
              type === 'error' ? 'bg-error dark:bg-error-dark hover:bg-error-hover dark:hover:bg-error-hover-dark' :
              type === 'success' ? 'bg-success dark:bg-success-dark hover:bg-success-hover dark:hover:bg-success-hover-dark' :
              type === 'confirm' ? 'bg-confirm dark:bg-confirm-dark hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark text-confirm-text dark:text-confirm-text-dark' :
              'bg-info dark:bg-info-dark hover:bg-info-hover dark:hover:bg-info-hover-dark'
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionBadge() {
  const [isOutdated, setIsOutdated] = useState(false);
  const [isAhead, setIsAhead] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  
  // Determine if we're on a non-release branch
  // Show branch name if it's not main, master, or a version tag pattern (e.g., v0.3.1)
  const isReleaseBranch = !GIT_BRANCH || 
    GIT_BRANCH === 'main' || 
    GIT_BRANCH === 'master' || 
    /^v?\d+\.\d+/.test(GIT_BRANCH) ||
    GIT_BRANCH === 'HEAD'; // Detached HEAD state (common in CI/Docker)
  
  const showBranch = GIT_BRANCH && !isReleaseBranch;

  useEffect(() => {
    // Check GitHub for latest version
    const checkVersion = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/MrCrin/swiish/releases/latest', {
          headers: {
            'Accept': 'application/vnd.github.v3+json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const latestVersion = data.tag_name?.replace(/^v/, '') || data.tag_name; // Remove 'v' prefix if present
          const currentVersion = APP_VERSION;
          
          // SemVer-compliant version comparison
          // Follows SemVer precedence rules: pre-release versions have lower precedence than stable versions
          const compareVersions = (v1, v2) => {
            // Parse versions into base version and pre-release identifier
            const parseVersion = (version) => {
              const dashIndex = version.indexOf('-');
              if (dashIndex === -1) {
                return {
                  base: version,
                  prerelease: null
                };
              }
              return {
                base: version.substring(0, dashIndex),
                prerelease: version.substring(dashIndex + 1)
              };
            };

            const parsed1 = parseVersion(v1);
            const parsed2 = parseVersion(v2);

            // Compare base versions numerically
            const base1 = parsed1.base.split('.').map(Number);
            const base2 = parsed2.base.split('.').map(Number);

            for (let i = 0; i < Math.max(base1.length, base2.length); i++) {
              const part1 = base1[i] || 0;
              const part2 = base2[i] || 0;
              if (part1 < part2) return -1;
              if (part1 > part2) return 1;
            }

            // Base versions are equal, now check pre-release identifiers
            // Rule: A version without a pre-release identifier has higher precedence
            if (parsed1.prerelease === null && parsed2.prerelease === null) {
              return 0; // Both are stable, equal
            }
            if (parsed1.prerelease === null) {
              return 1; // v1 is stable, v2 is pre-release, v1 > v2
            }
            if (parsed2.prerelease === null) {
              return -1; // v1 is pre-release, v2 is stable, v1 < v2
            }

            // Both have pre-release identifiers, compare lexicographically
            const prerelease1 = parsed1.prerelease.split('.');
            const prerelease2 = parsed2.prerelease.split('.');

            for (let i = 0; i < Math.max(prerelease1.length, prerelease2.length); i++) {
              const part1 = prerelease1[i];
              const part2 = prerelease2[i];

              if (part1 === undefined) return -1; // v1 has fewer parts, v1 < v2
              if (part2 === undefined) return 1; // v2 has fewer parts, v1 > v2

              // Try numeric comparison first, fall back to string comparison
              const num1 = Number(part1);
              const num2 = Number(part2);

              if (!isNaN(num1) && !isNaN(num2)) {
                // Both are numeric
                if (num1 < num2) return -1;
                if (num1 > num2) return 1;
              } else {
                // At least one is non-numeric, compare as strings
                if (part1 < part2) return -1;
                if (part1 > part2) return 1;
              }
            }

            return 0; // Pre-release identifiers are equal
          };
          
          if (latestVersion) {
            const comparison = compareVersions(currentVersion, latestVersion);
            if (comparison < 0) {
              setIsOutdated(true);
            } else if (comparison > 0) {
              setIsAhead(true);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check version:', error);
        // Silently fail - don't show error to user
      } finally {
        setIsChecking(false);
      }
    };

    checkVersion();
  }, []);

  // Build the display string: "v0.3.1" or "v0.3.1 (feature-branch)"
  const versionDisplay = showBranch 
    ? `v${APP_VERSION} (${GIT_BRANCH})`
    : `v${APP_VERSION}`;
  
  // Build the tooltip
  const tooltip = (!isOutdated && !isAhead && !showBranch)
    ? "You are on the latest release"
    : (isAhead || showBranch)
      ? "You are on an unreleased development version - things may break"
      : isOutdated
        ? "Update available on GitHub"
        : "View on GitHub";

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <a
        href={GITHUB_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-xs font-medium transition-all bg-card dark:bg-card-dark border-2 border-border dark:border-border-dark px-2 py-1 rounded shadow-sm hover:shadow-md ${
          showBranch
            ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:border-amber-300 dark:hover:border-amber-700'
            : isOutdated 
              ? 'text-error-text dark:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark hover:border-error-border dark:hover:border-error-border-dark' 
              : isAhead
                ? 'text-info-text dark:text-info-text-dark hover:bg-info-bg dark:hover:bg-info-bg-dark hover:border-info-border dark:hover:border-info-border-dark'
                : 'text-text-primary dark:text-text-primary-dark hover:text-action dark:hover:text-action-dark hover:border-success-border dark:hover:border-success-border-dark'
        }`}
        title={tooltip}
      >
        {versionDisplay}
      </a>
    </div>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // Note: useParams() doesn't work at App level (Routes are children), so we extract params from location.pathname
  const [view, setView] = useState(() => {
    // Initialize view based on current path - don't default to 'loading' for public routes
    const initialPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const pathParts = initialPath.substring(1).split('/').filter(p => p);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isPublicRoute = isShortCode || isOrgScoped || (pathParts.length === 1 && pathParts[0] && !initialPath.startsWith('/people') && !initialPath.startsWith('/login') && !initialPath.startsWith('/setup') && !initialPath.startsWith('/settings') && !initialPath.startsWith('/users') && !initialPath.startsWith('/cards') && initialPath !== '/');
    return isPublicRoute ? 'public-loading' : 'loading';
  }); 
  const [data, setData] = useState(() => getDefaultTemplate(null));
  const [currentSlug, setCurrentSlug] = useState('');
  const [isPublicLoading, setIsPublicLoading] = useState(false);
  const [cardList, setCardList] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'owner' or 'member'
  const [csrfToken, setCsrfToken] = useState('');
  const [error, setError] = useState('');
  const [oidcProviders, setOidcProviders] = useState([]);
  const [localLoginEnabled, setLocalLoginEnabled] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null, onClose: null, confirmText: 'OK', cancelText: 'Cancel' });
  const [createCardModal, setCreateCardModal] = useState({ isOpen: false, slug: '', userId: null });
  const [targetUserIdForNewCard, setTargetUserIdForNewCard] = useState(null);
  const editInProgressRef = useRef(false);
  const [actionSelectionModal, setActionSelectionModal] = useState({ isOpen: false });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'member' });
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'member' });
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupRetryCount, setSetupRetryCount] = useState(0);
  const [setupData, setSetupData] = useState({ organisationName: '', adminEmail: '', adminPassword: '' });
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSuccessSetup, setIsSuccessSetup] = useState(false);
  const [isSuccessCreateUser, setIsSuccessCreateUser] = useState(false);
  const [isSuccessInvite, setIsSuccessInvite] = useState(false);
  
  // Demo mode state management
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoResetInterval, setDemoResetInterval] = useState(60);
  
  // Dark mode state management
  const [darkMode, setDarkMode] = useState(() => {
    const stored = readDarkModePreference();
    if (stored !== null) {
      const isDark = stored === 'true';
      // Sync with document class immediately
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return isDark;
    }
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    return prefersDark;
  });

  // Apply dark mode class to document whenever state changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Listen for system preference changes (only if no manual preference set)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const stored = readDarkModePreference();
      if (stored === null) {
        setDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Check for demo mode on app load
  useEffect(() => {
    fetch(`${API_ENDPOINT}/demo/status`)
      .then(res => res.json())
      .then(data => {
        if (data.demoMode) {
          setIsDemoMode(true);
          setDemoResetInterval(data.resetInterval || 60);
        }
      })
      .catch(err => {
        // Silently fail - demo endpoint may not exist if demo mode is off
      });
  }, []);


  // Discover enabled OIDC providers only on the login page.
  useEffect(() => {
    if (location.pathname !== '/login') return;
    fetch(`${API_ENDPOINT}/auth/oidc/providers`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : { providers: [] })
      .then(data => {
        setOidcProviders(Array.isArray(data.providers) ? data.providers : []);
        setLocalLoginEnabled(data.localLoginEnabled === true);
      })
      .catch(() => {
        setOidcProviders([]);
        setLocalLoginEnabled(false);
        setError('Unable to load sign-in options. Please refresh the page.');
      });

    const oidcError = new URLSearchParams(location.search).get('oidc_error');
    if (oidcError) setError(oidcError);
  }, [location.pathname, location.search]);

  const toggleDarkMode = () => {
    const currentDark = document.documentElement.classList.contains('dark');
    const newValue = !currentDark;

    if (newValue) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save to localStorage
    try {
      localStorage.setItem('darkMode', newValue.toString());
    } catch {
      // Keep the toggle working for this session when storage is unavailable.
    }

    // Update React state
    setDarkMode(newValue);

    setTimeout(() => {
      const sampleEl = document.querySelector('.bg-main');
      if (sampleEl) {
        void sampleEl.offsetHeight;
      }
    }, 100);
  };

  // Demo Mode Banner Component
  const DemoModeBanner = () => {
    if (!isDemoMode) return null;

    return (
      <div className="sticky top-0 left-0 right-0 z-50 bg-amber-100 dark:bg-amber-900 border-b-2 border-amber-400 dark:border-amber-700 px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-3">
          <span className="text-2xl">🛠️</span>
          <span className="font-semibold text-amber-900 dark:text-amber-100">
            Demo Mode
          </span>
          <span className="text-sm text-amber-700 dark:text-amber-300">
            All changes reset every {demoResetInterval} minutes
          </span>
        </div>
      </div>
    );
  };

  // Helper functions to show modals
  const showAlert = (message, type = 'info', title = '', onClose = null) => {
    setModal({ isOpen: true, type, title, message, onConfirm: null, onClose, confirmText: 'OK', cancelText: 'Cancel' });
  };

  const showConfirm = (message, onConfirm, title = 'Confirm', confirmText = 'Confirm', cancelText = 'Cancel') => {
    setModal({ isOpen: true, type: 'confirm', title, message, onConfirm, onClose: null, confirmText, cancelText });
  };

  const closeModal = () => {
    const currentModal = modal;
    setModal(prev => ({ ...prev, isOpen: false }));
    // Call onClose callback after state update
    if (currentModal.onClose) {
      setTimeout(() => currentModal.onClose(), 0);
    }
  };

const THEME_PRESETS = {
  swiish: [
    { name: "indigo", gradient: "from-indigo-600 to-purple-600", button: "bg-indigo-600 hover:bg-indigo-700", link: "text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-100", text: "text-indigo-600" },
    { name: "blue", gradient: "from-blue-600 to-cyan-600", button: "bg-blue-600 hover:bg-blue-700", link: "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100", text: "text-blue-600" },
    { name: "rose", gradient: "from-rose-500 to-orange-500", button: "bg-rose-600 hover:bg-rose-700", link: "text-rose-600 bg-rose-50 border-rose-100 hover:bg-rose-100", text: "text-rose-600" },
    { name: "emerald", gradient: "from-emerald-500 to-teal-500", button: "bg-emerald-600 hover:bg-emerald-700", link: "text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100", text: "text-emerald-600" },
    { name: "slate", gradient: "from-slate-700 to-slate-900", button: "bg-slate-800 hover:bg-slate-900", link: "text-slate-700 bg-border-subtle border-slate-200 hover:bg-slate-200", text: "text-slate-800" }
  ],
  minimal: [
    { name: "mono-dark", gradient: "from-neutral-900 to-neutral-700", button: "bg-neutral-900 hover:bg-neutral-800", link: "text-neutral-900 bg-neutral-100 border-neutral-200 hover:bg-neutral-200", text: "text-neutral-900" },
    { name: "mono-mid", gradient: "from-neutral-700 to-neutral-500", button: "bg-neutral-700 hover:bg-neutral-600", link: "text-neutral-700 bg-neutral-50 border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" },
    { name: "mono-light", gradient: "from-neutral-300 to-neutral-200", button: "bg-neutral-200 hover:bg-neutral-300 text-neutral-900", link: "text-neutral-700 bg-white border-neutral-200 hover:bg-neutral-100", text: "text-neutral-700" }
  ]
};

const [settings, setSettings] = useState({
  default_organisation: 'My Organisation',
  theme_variant: 'swiish',
  theme_colors: THEME_PRESETS.swiish,
  organisation_banner: { banner: '', bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 },
  card_appearance: getDefaultCardAppearance(),
  allow_theme_customisation: true,
  allow_banner_override: true
  });

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const res = await fetch(`${API_ENDPOINT}/csrf-token`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCsrfToken(data.csrfToken);
        return data.csrfToken;
      }
    } catch (e) {
      console.error('Failed to fetch CSRF token:', e);
    }
    return null;
  };

  // Helper function to make authenticated API calls
  const apiCall = async (url, options = {}, retryCsrf = true) => {
    const method = (options.method || 'GET').toUpperCase();
    const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    let token = csrfToken;
    if (needsCsrf && !token) token = await fetchCsrfToken();

    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const headers = { ...options.headers };
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (needsCsrf && token) headers['X-CSRF-Token'] = token;

    const response = await fetch(url, {
      ...options,
      method,
      headers,
      credentials: 'include'
    });

    if (needsCsrf && retryCsrf && response.status === 403) {
      const errorData = await response.clone().json().catch(() => ({}));
      if (errorData.code === 'EBADCSRFTOKEN') {
        const freshToken = await fetchCsrfToken();
        if (freshToken) {
          return apiCall(url, {
            ...options,
            method,
            headers: { ...options.headers, 'X-CSRF-Token': freshToken }
          }, false);
        }
      }
    }

    return response;
  };
  
  // A failed or invalid status response cannot prove that setup is needed.
  const checkSetupStatus = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`${API_ENDPOINT}/setup/status`, {
        credentials: 'include',
        signal: controller.signal
      });
      if (!res.ok) throw new Error(`Setup status returned HTTP ${res.status}`);
      const data = await res.json();
      if (!data || typeof data.setupComplete !== 'boolean' ||
          !Number.isInteger(data.userCount) || data.userCount < 0 ||
          data.setupComplete !== (data.userCount > 0)) {
        throw new Error('Invalid setup status response');
      }
      setSetupStatus(data);
      return data;
    } catch (e) {
      console.error('Failed to check setup status:', e);
      return null;
    } finally {
      clearTimeout(timeout);
    }
  };

  const showSetupStatusError = () => {
    setView('setup-error');
    document.title = 'Connection Error - Hirose vCard';
  };

  const retrySetupStatus = () => {
    setView('loading');
    setSetupRetryCount(count => count + 1);
  };

  // Router Logic - handle route changes and set view based on route
  useEffect(() => {
    const path = location.pathname;
    // Handle editor route (/people/edit/:slug) - extract slug from pathname since useParams() doesn't work at App level
    if (path.startsWith('/people/edit/')) {
      // Extract slug from pathname (remove '/people/edit/' prefix)
      const slug = path.replace('/people/edit/', '');
      // Only load if this is a different slug or we're not already in editor view
      if (currentSlug !== slug || view !== 'admin-editor') {
        setCurrentSlug(slug);
        fetchCsrfToken();
        checkAuth().then((authResult) => {
          if (authResult.isAuthenticated) {
            // After auth check, load the card data — but skip if handleEdit is already
            // in progress (e.g. triggered by the admin clicking the Edit button, which
            // navigates here and would otherwise cause a second fetch without userId)
            if (!editInProgressRef.current) {
              handleEdit(slug);
            }
          } else {
            // If auth fails, redirect to login (explicit redirect for unauthorized access)
            navigate('/login');
          }
        }).catch(() => {
          // If auth fails, redirect to login
          navigate('/login');
        });
      }
      return;
    }
    
    // Skip public card route handling here - PublicCardRoute component handles it
    // This prevents duplicate fetches
    // Public routes: /:orgSlug/:cardSlug or /:slug (short code or legacy)
    const pathParts = path.substring(1).split('/').filter(p => p);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isPublicRoute = isShortCode || isOrgScoped || (pathParts.length === 1 && pathParts[0] && !path.startsWith('/people') && !path.startsWith('/login') && !path.startsWith('/setup') && !path.startsWith('/settings') && !path.startsWith('/users') && !path.startsWith('/cards') && path !== '/');
    
    if (isPublicRoute) {
      // Don't interfere with public card routes - let PublicCardRoute handle it
      // Only set initial state if view is still 'loading' (first render)
      // Once PublicCardRoute takes over (view is 'public-loading' or 'public-card'), don't touch it
      if (view === 'loading') {
        setView('public-loading');
      } else if (view === 'public-loading' || view === 'public-card' || view === '404') {
        // PublicCardRoute is managing state - don't interfere
      }
      return;
    }
    
    // Set view based on route
    if (path === '/setup') {
      // Direct /setup remains unavailable on an already configured installation.
      setView('loading');
      fetchCsrfToken();
      checkSetupStatus().then((status) => {
        if (!status) {
          showSetupStatusError();
        } else if (status.setupComplete) {
          navigate('/login', { replace: true });
        } else {
          // Only a confirmed fresh installation may see the setup wizard.
          setView('setup-wizard');
          document.title = 'Initial Setup';
        }
      }).catch(showSetupStatusError);
      return;
    } else if (path === '/login') {
      // Login page - check if already authenticated
      setView('loading');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (authResult.isAuthenticated) {
          // If authenticated, redirect to dashboard (explicit redirect)
          navigate('/people', { replace: true });
        } else {
          // Not authenticated, show login page
          setView('admin-login');
          document.title = "Admin Login";
        }
      }).catch(() => {
        // Not authenticated, show login page
        setView('admin-login');
        document.title = "Admin Login";
      });
    } else if (path === '/settings' || path === '/users') {
      // Direct URL entry must not expose owner-only management screens to members.
      setView('loading');
      fetchCsrfToken();
      checkAuth().then((authResult) => {
        if (!authResult.isAuthenticated) {
          navigate('/login', { replace: true });
        } else if (authResult.userData.role !== 'owner') {
          navigate('/people', { replace: true });
        } else {
          setView(path === '/settings' ? 'admin-settings' : 'user-management');
        }
      }).catch(() => navigate('/login', { replace: true }));
    } else if (path === '/admin' || path === '/' || path === '/people') {
      // Check demo mode status first (will be known from setup/status response)
      fetchCsrfToken();
      checkSetupStatus().then((status) => {
        if (!status) {
          showSetupStatusError();
          return;
        }
        // Check if demo mode is active (from status response or isDemoMode state)
        const demoModeActive = status.demoMode || isDemoMode;

        if (demoModeActive) {
          // Demo mode: skip setup, go directly to auth check
          if (path === '/' || path === '/admin') {
            // Redirect root/admin to /people (explicit redirect)
            navigate('/people', { replace: true });
            return; // Let next effect run handle /people
          }
          // Set view to loading while checking auth
          setView('loading');
          checkAuth().then((authResult) => {
            if (authResult.isAuthenticated) {
              // Demo mode: always show dashboard for demo user (owner role)
              setView('admin-dashboard');
              document.title = "Admin Dashboard";
            } else {
              // This shouldn't happen in demo mode, but fallback to login just in case
              navigate('/login', { replace: true });
            }
          }).catch((e) => {
            console.error('Auth check failed:', e);
            navigate('/login', { replace: true });
          });
          return;
        }

        // Normal mode: continue with regular auth flow
        // (status already checked above, setupComplete must be true to get here)
        if (!status.setupComplete) {
          // Confirmed fresh installation (setupComplete=false and userCount=0).
          navigate('/setup', { replace: true });
          document.title = "Initial Setup";
        } else {
          // Setup complete, check authentication
          if (path === '/' || path === '/admin') {
            // Redirect root/admin to /people (explicit redirect)
            navigate('/people', { replace: true });
            return; // Let next effect run handle /people
          }
          // Set view to loading while checking auth
          setView('loading');
          checkAuth().then((authResult) => {
            if (authResult.isAuthenticated) {
              // Members have their own cards dashboard; owners retain the organisation dashboard.
              setView(authResult.userData.role === 'member' ? 'member-dashboard' : 'admin-dashboard');
              document.title = authResult.userData.role === 'member' ? 'My Cards' : 'People';
            } else {
              // Not authenticated, redirect to login (explicit redirect)
              navigate('/login', { replace: true });
            }
          }).catch((e) => {
            console.error('Auth check failed:', e);
            navigate('/login', { replace: true });
          });
        }
      }).catch((e) => {
        console.error('Setup status check failed:', e);
        showSetupStatusError();
      });
    }
  }, [location.pathname, navigate, setupRetryCount]);


  // checkAuth: Only updates state, never navigates
  // Returns { isAuthenticated: boolean, userData: object | null, cardList: array }
  const checkAuth = async () => {
    try {
      // Fetch CSRF token first if not already fetched
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      // Fetch user info to get role
      const userRes = await apiCall(`${API_ENDPOINT}/auth/me`);
      if (userRes.ok) {
        const userData = await userRes.json();
        setUserRole(userData.role);
        setCurrentUserId(userData.id);
        setCurrentUserEmail(userData.email);
        
        // Fetch cards
        const res = await apiCall(`${API_ENDPOINT}/admin/cards`);
        if (res.ok) {
          const list = await res.json();
          setCardList(list);
          setIsAuthenticated(true);
          if (userData.role === 'member') {
            fetchPublicSettings(userData.orgSlug || 'default');
          } else {
            fetchSettings();
          }
          
          return { isAuthenticated: true, userData, cardList: list };
        } else {
          setIsAuthenticated(false);
          setUserRole(null);
          return { isAuthenticated: false, userData: null, cardList: [] };
        }
      } else {
        setIsAuthenticated(false);
        setUserRole(null);
        return { isAuthenticated: false, userData: null, cardList: [] };
      }
    } catch (e) {
      setIsAuthenticated(false);
      setUserRole(null);
      return { isAuthenticated: false, userData: null, cardList: [] };
    }
  };

  const fetchPublicCard = async (slug) => {
    setIsPublicLoading(true);
    setView('public-loading'); // Ensure view is set to loading while fetching
    try {
      // Legacy slug route: fetch the card first so we know which organisation settings to apply.
      const res = await fetch(`${API_ENDPOINT}/cards/${slug}`);
      if (res.ok) {
        const json = await res.json();
        await fetchPublicSettings(json._orgSlug || 'default');
        const defaultTemplate = getDefaultTemplate(settings);
        setData({ 
          ...defaultTemplate, 
          ...json, 
          links: json.links || [],
          privacy: json.privacy || defaultTemplate.privacy
        });
        setView('public-card');
        document.title = json.personal?.name ? `${json.personal.name} - Swiish Card` : "Swiish Card";
      } else {
        setError('Card not found');
        setView('404');
        document.title = "Card Not Found";
      }
    } catch (e) {
      console.error('Error fetching public card:', e);
      setError('Connection failed');
      setView('404');
      document.title = "Error Loading Card";
    } finally {
      setIsPublicLoading(false);
    }
  };

  const fetchCardByShortCode = useCallback(async (shortCode) => {
    setIsPublicLoading(true);
    try {
      // Fetch card first to get org slug, then fetch settings for that org
      const res = await fetch(`${API_ENDPOINT}/cards/short/${shortCode}`);
      if (res.ok) {
        const cardData = await res.json();
        // Fetch settings for the organization that owns this card
        const orgSlug = cardData._orgSlug || 'default';
        await fetchPublicSettings(orgSlug);
        
        const defaultTemplate = getDefaultTemplate(settings);
        const mergedData = { 
          ...defaultTemplate, 
          ...cardData, 
          links: cardData.links || [],
          privacy: cardData.privacy || defaultTemplate.privacy,
          _shortCode: cardData._shortCode // Preserve short code from backend
        };
        // Set isPublicLoading to false FIRST, then data, then view
        // This prevents renderAdminViews from showing loading when view='public-card'
        setIsPublicLoading(false);
        setData(mergedData);
        setView('public-card');
        const name = `${cardData.personal?.firstName || ''} ${cardData.personal?.lastName || ''}`.trim();
        document.title = name || 'Card';
      } else {
        setView('404');
        setError('Card not found');
        document.title = 'Card Not Found';
      }
    } catch (e) {
      console.error('[FRONTEND] Error fetching card by short code:', e);
      setView('404');
      setError('Failed to load card');
      document.title = 'Card Not Found';
    } finally {
      setIsPublicLoading(false);
    }
  }, [settings]);

  const fetchCardByOrgAndSlug = useCallback(async (orgSlug, cardSlug) => {
    setIsPublicLoading(true);
    try {
      // Fetch settings for the organization from the URL
      await fetchPublicSettings(orgSlug);
      const res = await fetch(`${API_ENDPOINT}/cards/${orgSlug}/${cardSlug}`);
      if (res.ok) {
        const cardData = await res.json();
        const defaultTemplate = getDefaultTemplate(settings);
        const mergedData = { 
          ...defaultTemplate, 
          ...cardData, 
          links: cardData.links || [],
          privacy: cardData.privacy || defaultTemplate.privacy,
          _shortCode: cardData._shortCode // Preserve short code from backend
        };
        // Set isPublicLoading to false FIRST, then data, then view
        // This prevents renderAdminViews from showing loading when view='public-card'
        setIsPublicLoading(false);
        setData(mergedData);
        setView('public-card');
        const name = `${cardData.personal?.firstName || ''} ${cardData.personal?.lastName || ''}`.trim();
        document.title = name || 'Card';
      } else {
        setView('404');
        setError('Card not found');
        document.title = 'Card Not Found';
      }
    } catch (e) {
      console.error('[FRONTEND] Error fetching card by org and slug:', e);
      setView('404');
      setError('Failed to load card');
      document.title = 'Card Not Found';
    } finally {
      setIsPublicLoading(false);
    }
  }, [settings]);

  // Helper to initialize color data for settings (shared between fetchSettings and SettingsView)
  // Note: SettingsView has its own initializeColorData function, but we need this one for fetchSettings
  const initializeColorDataForSettings = (colors) => {
    if (!colors || !Array.isArray(colors)) return [];
    return colors.map(color => {
      let hexBase, baseColor, colorType;
      
      // Determine if this is a standard color (has Tailwind gradient or baseColor) or custom hex
      const hasValidTailwindGradient = color.gradient && typeof color.gradient === 'string' && color.gradient.startsWith('from-');
      const hasHexBase = color.hexBase && typeof color.hexBase === 'string' && color.hexBase.startsWith('#');
      
      if (hasValidTailwindGradient) {
        // Convert from Tailwind gradient to hex
        const extracted = extractBaseColorFromGradient(color.gradient);
        if (extracted) {
          baseColor = extracted.baseColor;
          hexBase = getTailwindColorHex(extracted.baseColor, 600); // Always use shade 600
          colorType = 'standard';
        } else {
          // Fallback if extraction fails
          baseColor = color.baseColor || 'indigo';
          hexBase = hasHexBase ? color.hexBase : getTailwindColorHex(baseColor, 600);
          colorType = color.colorType === 'custom' ? 'custom' : 'standard';
        }
      } else if (hasHexBase) {
        // Has hexBase - determine if standard or custom
        if (color.baseColor && color.colorType !== 'custom') {
          // Standard color with hexBase
          baseColor = color.baseColor;
          hexBase = color.hexBase;
          colorType = 'standard';
        } else {
          // Custom hex color
          baseColor = null;
          hexBase = color.hexBase;
          colorType = 'custom';
        }
      } else if (color.gradientStyle) {
        // Has gradientStyle but no hexBase - extract from gradientStyle or use default
        baseColor = null;
        hexBase = '#4f46e5'; // Default
        colorType = 'custom';
      } else {
        // Fallback - treat as standard with default
        baseColor = color.baseColor || 'indigo';
        hexBase = getTailwindColorHex(baseColor, 600);
        colorType = color.colorType === 'custom' ? 'custom' : 'standard';
      }
      
      // Always auto-generate complementary secondary color
      const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
      const hexSecondary = color.hexSecondary || (complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase);
      
      // Generate all inline styles
      const gradientStyle = `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`;
      const buttonStyle = hexBase;
      const linkStyle = hexBase;
      const textStyle = hexBase;
      
      // Build clean hex-only color object
      return {
        name: color.name,
        colorType: colorType,
        baseColor: baseColor, // null for custom, Tailwind name for standard
        hexBase: hexBase,
        hexSecondary: hexSecondary,
        gradientStyle: gradientStyle,
        buttonStyle: buttonStyle,
        linkStyle: linkStyle,
        textStyle: textStyle
      };
    });
  };

  const fetchSettings = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/settings`);
      if (res.ok) {
        const settingsData = await res.json();
      const merged = {
        ...settingsData,
        theme_variant: settingsData.theme_variant || 'swiish',
        theme_colors: initializeColorDataForSettings(settingsData.theme_colors || [])
      };
      setSettings(merged);
      applyThemeCssVars(merged.theme_variant);
      document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
      document.body.classList.add(`theme-${merged.theme_variant}`);
      }
    } catch (e) {
      console.error('Failed to fetch settings:', e);
    }
  };

  const fetchPublicSettings = async (orgSlug = 'default') => {
    try {
      const url = orgSlug ? `${API_ENDPOINT}/settings?orgSlug=${encodeURIComponent(orgSlug)}` : `${API_ENDPOINT}/settings`;
      const res = await fetch(url);
      if (res.ok) {
        const settingsData = await res.json();
        const mergedVariant = settingsData.theme_variant || 'swiish';
        
        // Match admin pattern: create new object from API response
        // This ensures React sees it as a new object and triggers re-renders
        const merged = {
          default_organisation: settingsData.default_organisation || 'My Organisation',
          theme_variant: mergedVariant,
          theme_colors: (settingsData.theme_colors && Array.isArray(settingsData.theme_colors)) 
            ? initializeColorDataForSettings(settingsData.theme_colors)
            : THEME_PRESETS.swiish,
          organisation_banner: settingsData.organisation_banner || { banner: '', bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 },
          card_appearance: normalizeCardAppearance(settingsData.card_appearance),
          allow_theme_customisation: settingsData.allow_theme_customisation !== false,
          allow_image_customisation: settingsData.allow_image_customisation !== false,
          allow_banner_override: settingsData.allow_banner_override !== false
        };
        
        // Set state and apply theme immediately (same pattern as fetchSettings)
        setSettings(merged);
        applyThemeCssVars(mergedVariant);
        document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
        document.body.classList.add(`theme-${mergedVariant}`);
      }
    } catch (e) {
      console.error('Failed to fetch public settings:', e);
    }
  };

  // Group cards by user (userId or userEmail as fallback)
  const groupCardsByUser = (cards) => {
    const userMap = new Map();
    
    cards.forEach(card => {
      // Use userId as primary key, fallback to userEmail if userId not available
      const userKey = card.userId || card.userEmail || 'unknown';
      
      if (!userMap.has(userKey)) {
        userMap.set(userKey, {
          userId: card.userId || null,
          userEmail: card.userEmail || null,
          userRole: card.userRole || null,
          userCreatedAt: card.userCreatedAt || null,
          cards: []
        });
      }
      
      // Add card to user's cards array (even if slug is null, it represents a user without cards)
      userMap.get(userKey).cards.push(card);
    });
    
    // Convert map to array and sort by user creation date (newest first)
    return Array.from(userMap.values()).sort((a, b) => {
      if (!a.userCreatedAt && !b.userCreatedAt) return 0;
      if (!a.userCreatedAt) return 1;
      if (!b.userCreatedAt) return -1;
      return new Date(b.userCreatedAt) - new Date(a.userCreatedAt);
    });
  };

  const fetchCardList = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/cards`);
      if (res.ok) {
        const list = await res.json();
        setCardList(list);
        // Removed setView('admin-dashboard') - fetchCardList should only update data, not view
        // View should be determined by route, not by data fetching
        setIsAuthenticated(true);
        // Fetch settings when dashboard loads (only if we're actually on dashboard)
        if (location.pathname === '/people') {
        fetchSettings();
        }
      } else {
        setIsAuthenticated(false);
        // Only set view to login if we're on a protected route
        if (location.pathname !== '/login' && location.pathname !== '/setup') {
        setView('admin-login');
        }
      }
    } catch (e) {
      setIsAuthenticated(false);
      // Only set view to login if we're on a protected route
      if (location.pathname !== '/login' && location.pathname !== '/setup') {
      setView('admin-login');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINT}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.toLowerCase().trim(), password })
      });
      if (res.ok) {
        // Token is now in httpOnly cookie, fetch CSRF token and check auth
        await fetchCsrfToken();
        const authResult = await checkAuth();
        if (authResult.isAuthenticated) {
          // Navigate to dashboard after successful login (explicit user action)
          navigate('/people');
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setError(errorData.error || 'Invalid email or password');
      }
    } catch (e) { 
      setError('Login failed'); 
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_ENDPOINT}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.error('Logout API call failed:', e);
    }
    // Force hard redirect immediately - don't set state first as it causes race conditions
    // The hard redirect will reload the page and useEffect will handle the login view
    window.location.href = '/login';
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setIsSettingUp(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINT}/setup/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({
          organisationName: setupData.organisationName.trim(),
          adminEmail: setupData.adminEmail.toLowerCase().trim(),
          adminPassword: setupData.adminPassword
        })
      });
      if (res.ok) {
        // Setup successful, automatically log in
        setIsSuccessSetup(true);
        setTimeout(() => setIsSuccessSetup(false), 2000);
        const authResult = await checkAuth();
        if (authResult.isAuthenticated) {
          // Navigate to dashboard after successful setup (explicit user action)
          navigate('/people');
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        setError(errorData.error || 'Setup failed');
      }
    } catch (e) {
      setError('Setup failed. Please try again.');
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleCreateNew = () => {
    if (userRole === 'owner') {
      // Show action selection modal for owners
      setActionSelectionModal({ isOpen: true });
    } else {
      // Members go directly to card creation (for themselves, no userId needed)
      setCreateCardModal({ isOpen: true, slug: '', userId: null });
    }
  };

  const handleCreateCardConfirm = () => {
    const slug = createCardModal.slug.toLowerCase().trim().replace(/[^a-z0-9-]/g, '');
    if (!slug) {
      showAlert('Please enter a valid user URL (e.g., "sarah")', 'error', 'Invalid User URL');
      return;
    }
    if (slug.length < 1) {
      showAlert('User URL must be at least 1 character long', 'error', 'Invalid User URL');
      return;
    }
    // Prevent duplicate user URLs for the same user
    // Flatten all cards from grouped structure to check for duplicates
    const allCards = cardList.filter(c => c.slug === slug);
    if (allCards.length > 0) {
      showAlert(`A card with user URL "${slug}" already exists. Please choose another user URL.`, 'error', 'User URL already exists');
      return;
    }
    // Store userId for this new card if provided
    if (createCardModal.userId) {
      setTargetUserIdForNewCard(createCardModal.userId);
    }
    setCreateCardModal({ isOpen: false, slug: '', userId: null });
    setCurrentSlug(slug);
    setData(getDefaultTemplate(settings)); 
    setView('admin-editor');
    // Navigate to editor route, just like handleEdit does
    navigate(`/people/edit/${slug}`);
  };

  const handleCreateCardCancel = () => {
    setCreateCardModal({ isOpen: false, slug: '' });
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      if (showAlert) showAlert('Email and password are required', 'error');
      return;
    }

    if (newUser.password.length < 8) {
      if (showAlert) showAlert('Password must be at least 8 characters long', 'error');
      return;
    }

    setIsSavingUser(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`, {
        method: 'POST',
        body: JSON.stringify(newUser)
      });
      
      if (res.ok) {
        setIsSuccessCreateUser(true);
        setTimeout(() => setIsSuccessCreateUser(false), 2000);
        setShowCreateUserModal(false);
        setNewUser({ email: '', password: '', role: 'member' });
        checkAuth(); // Refresh card list
      } else {
        // Try to parse error response
        let errorMessage = 'Failed to create user';
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('User creation failed:', {
            status: res.status,
            statusText: res.statusText,
            error: errorData
          });
        } catch (parseError) {
          // Response is not JSON, use status text
          console.error('User creation failed - non-JSON response:', {
            status: res.status,
            statusText: res.statusText
          });
          errorMessage = `Failed to create user: ${res.status} ${res.statusText}`;
        }
        if (showAlert) showAlert(errorMessage, 'error');
        // Don't close modal on error - let user fix and retry
      }
    } catch (e) {
      console.error('Error creating user:', e);
      const errorMessage = e.message || 'Error creating user. Please try again.';
      if (showAlert) showAlert(errorMessage, 'error');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!newInvitation.email) {
      if (showAlert) showAlert('Email is required', 'error');
      return;
    }

    setIsSavingUser(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`, {
        method: 'POST',
        body: JSON.stringify(newInvitation)
      });
      if (res.ok) {
        setIsSuccessInvite(true);
        setTimeout(() => setIsSuccessInvite(false), 2000);
        setShowInviteModal(false);
        setNewInvitation({ email: '', role: 'member' });
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Failed to send invitation', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Error sending invitation', 'error');
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        if (showAlert) showAlert('User role updated successfully', 'success');
        checkAuth(); // Refresh card list
        setEditingUserId(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Failed to update role', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Error updating role', 'error');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (showConfirm) {
      showConfirm(
        `Are you sure you want to permanently delete ${userEmail}? This will delete the user and all their cards. This action cannot be undone.`,
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert('User deleted successfully', 'success');
              checkAuth(); // Refresh card list
            } else {
              const errorData = await res.json().catch(() => ({}));
              if (showAlert) showAlert(errorData.error || 'Failed to delete user', 'error');
            }
          } catch (e) {
            if (showAlert) showAlert('Error deleting user', 'error');
          }
        },
        'Delete User',
        'Delete',
        'Cancel'
      );
    }
  };

  const handleEdit = async (slug, userId) => {
    editInProgressRef.current = true;
    try {
      // User IDs are UUID strings (crypto.randomUUID()). Guard against null/undefined/non-string values.
      const validUserId = userId && typeof userId === 'string' && userId.trim() ? userId : null;
      const isOtherUser = validUserId !== null && validUserId !== currentUserId;
      const fetchUrl = isOtherUser
        ? `${API_ENDPOINT}/admin/cards/${validUserId}/${slug}`
        : `${API_ENDPOINT}/my/cards/${slug}`;
      const res = await apiCall(fetchUrl);
      if (res.ok) {
        const json = await res.json();
        const defaultTemplate = getDefaultTemplate(settings);
        setData({ 
          ...defaultTemplate, 
          ...json,
          // Enforce default_organisation from organization settings
          personal: {
            ...defaultTemplate.personal,
            ...json.personal,
            company: settings?.default_organisation || json.personal?.company || defaultTemplate.personal.company
          },
          links: json.links || [],
          privacy: json.privacy || defaultTemplate.privacy
        });
        // Track the owner so handleSave sends it to the correct user's card
        if (isOtherUser) {
          setTargetUserIdForNewCard(validUserId);
        } else {
          setTargetUserIdForNewCard(null);
        }
        setCurrentSlug(slug);
        setView('admin-editor');
        // Navigate to editor route only if not already there
        if (location.pathname !== `/people/edit/${slug}`) {
          navigate(`/people/edit/${slug}`);
        }
      } else {
        let errorMsg = 'Failed to load card';
        try {
          const errData = await res.json();
          errorMsg = errData.error || errorMsg;
        } catch {}
        showAlert(`${errorMsg} (${res.status})`, 'error');
      }
    } finally {
      editInProgressRef.current = false;
    }
  };

  const handleDelete = async (slug, userId) => {
    showConfirm(
      `Are you sure you want to delete ${slug}?`,
      async () => {
        const validUserId = userId && typeof userId === 'string' && userId.trim() ? userId : null;
        const isOtherUser = validUserId !== null && validUserId !== currentUserId;
        const deleteUrl = isOtherUser
          ? `${API_ENDPOINT}/cards/${slug}?userId=${encodeURIComponent(validUserId)}`
          : `${API_ENDPOINT}/cards/${slug}`;
        const res = await apiCall(deleteUrl, {
          method: 'DELETE'
        });
        if (res.ok) {
          showAlert('Card deleted successfully', 'success', '', () => {
            fetchCardList();
          });
        } else {
          showAlert('Failed to delete card', 'error');
        }
      },
      'Delete Card',
      'Delete',
      'Cancel'
    );
  };

  const handleSave = async () => {
    const performSave = async () => {
      setIsSaving(true);
      const startTime = Date.now();
      try {
        // Include userId in request body if creating card for another user
        const body = { ...data };
        if (targetUserIdForNewCard) {
          body.userId = targetUserIdForNewCard;
        }

        const res = await apiCall(`${API_ENDPOINT}/cards/${currentSlug}`, {
          method: 'POST',
          body: JSON.stringify(body)
        });

        // Ensure at least 500ms has passed
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < 500) {
          await new Promise(resolve => setTimeout(resolve, 500 - elapsedTime));
        }

        if (res.ok) {
          // Clear targetUserIdForNewCard after successful save
          setTargetUserIdForNewCard(null);
          setIsSuccess(true);
          fetchCardList();
          setTimeout(() => setIsSuccess(false), 2000);
        } else {
          showAlert('Save failed', 'error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const email = (data.contact?.email || '').trim().toLowerCase();
    if (email && cardList.length > 0) {
      // Only check against actual cards (those with slugs) - this excludes user entries without cards
      // Also exclude the current card being edited
      const duplicates = cardList.filter(c => 
        c.slug && // Must be an actual card
        c.email && // Must have a contact email
        c.email === email && // Must match the email being saved
        c.slug !== currentSlug // Must not be the current card being edited
      );
      if (duplicates.length > 0) {
        showConfirm(
          `Another card already uses this email address (${email}). Would you like to save anyway?`,
          performSave,
          'Email already in use',
          'Save anyway',
          'Cancel'
        );
        return;
      }
    }

    await performSave();
  };

  // Always use Routes for proper URL handling
  // Admin routes must be defined before /:slug to prevent matching
  // All admin views are consolidated here to avoid duplication
  const renderAdminViews = () => (
    <>
      {view === 'loading' && (
        <>
          <div className="h-screen flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark bg-main dark:bg-main-dark bg-main-texture">Loading...</div>
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'setup-error' && (
        <div className="min-h-screen bg-surface dark:bg-main-dark flex items-center justify-center p-4">
          <div className="bg-card dark:bg-card-dark max-w-sm w-full rounded-page shadow-xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-error-text dark:text-error-text-dark mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-3">Unable to Connect</h1>
            <p className="text-sm text-text-muted dark:text-text-muted-dark mb-6">
              Hirose vCard could not verify the organisation setup. Please check your connection and try again.
            </p>
            <button type="button" onClick={retrySetupStatus}
              className="w-full py-3 rounded-full bg-action dark:bg-action-dark text-white font-bold flex items-center justify-center gap-2 hover:bg-action-hover dark:hover:bg-action-hover-dark">
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          </div>
        </div>
      )}
      {view === '404' && (
        <>
          <div className="h-screen flex flex-col items-center justify-center bg-main dark:bg-main-dark bg-main-texture">
            <h1 className="text-4xl font-bold text-text-primary dark:text-text-primary-dark mb-2">404</h1>
            <p className="text-text-muted dark:text-text-muted-dark">Card not found.</p>
          </div>
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'setup-wizard' && (
        <>
          <div className="min-h-screen bg-surface dark:bg-main-dark flex items-center justify-center p-4">
            <div className="bg-card dark:bg-card-dark max-w-md w-full rounded-page shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-indigo-600 dark:text-indigo-400"><Settings className="w-8 h-8" /></div>
                <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">Initial Setup</h1>
                <p className="text-sm text-text-muted dark:text-text-muted-dark mt-2">Configure your organisation and create the first admin user</p>
              </div>
              <form onSubmit={handleSetup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">Organisation Name</label>
                  <input 
                    type="text" 
                    value={setupData.organisationName} 
                    onChange={e => setSetupData({ ...setupData, organisationName: e.target.value })} 
                    placeholder="My Organization" 
                    className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark" 
                    required 
                    autoFocus 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">Admin Email</label>
                  <input 
                    type="email" 
                    value={setupData.adminEmail} 
                    onChange={e => setSetupData({ ...setupData, adminEmail: e.target.value })} 
                    placeholder="admin@example.com" 
                    className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark" 
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2">Admin Password</label>
                  <input 
                    type="password" 
                    value={setupData.adminPassword} 
                    onChange={e => setSetupData({ ...setupData, adminPassword: e.target.value })} 
                    placeholder="Minimum 8 characters" 
                    className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark" 
                    required 
                    minLength={8}
                  />
                  <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Password must be at least 8 characters long</p>
                </div>
                {error && <div className="flex items-center gap-2 text-error-text dark:text-error-text-dark text-sm">{error}</div>}
                <button 
                  type="submit" 
                  disabled={isSettingUp}
                  className="w-full py-3.5 rounded-full bg-action dark:bg-action-dark text-white font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSettingUp ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : isSuccessSetup ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {isSettingUp ? 'Setting up...' : 'Complete Setup'}
                </button>
              </form>
              <div className="text-center mt-auto pt-8 pb-0 group relative z-10" style={{ boxSizing: 'content-box' }}>
                <div className="flex justify-center">
                  <img src="/graphics/Swiish_Logo.svg" alt="Swiish" className="h-4 w-auto dark:hidden swiish-logo" />
                  <img src="/graphics/Swiish_Logo_DarkBg.svg" alt="Swiish" className="h-4 w-auto hidden dark:block swiish-logo" />
                </div>
              </div>
            </div>
          </div>
          <VersionBadge />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'admin-login' && (
        <>
          <div className="min-h-screen bg-surface dark:bg-main-dark flex items-center justify-center p-4">
            <div className="bg-card dark:bg-card-dark max-w-sm w-full rounded-page shadow-xl p-8">
              <div className="text-center mb-8">
                <img src="/graphics/Hirose_Mark.png" alt="Hirose logo" className="h-20 w-auto max-w-full object-contain mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark">Hirose vCard</h1>
              </div>
              {oidcProviders.length > 0 && (
                <div className="space-y-3 mb-5">
                  {oidcProviders.map(provider => (
                    <a
                      key={`${provider.organisationSlug}:${provider.providerName}`}
                      href={provider.loginUrl}
                      className="w-full py-3.5 rounded-full bg-action dark:bg-action-dark text-white font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark transition-colors flex items-center justify-center gap-2"
                    >
                      <LogIn className="w-4 h-4" /> Continue with {provider.providerName}
                    </a>
                  ))}
                </div>
              )}
              {localLoginEnabled && (
                <>
                  {oidcProviders.length > 0 && (
                    <div className="flex items-center gap-3 mb-5 text-xs text-text-muted dark:text-text-muted-dark">
                      <div className="h-px bg-border dark:bg-border-dark flex-1" />
                      <span>or use a local account</span>
                      <div className="h-px bg-border dark:bg-border-dark flex-1" />
                    </div>
                  )}
                  <form onSubmit={handleLogin} className="space-y-4">
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark" autoFocus={oidcProviders.length === 0} />
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" className="w-full px-5 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark" />
                    <button type="submit" className="w-full py-3.5 rounded-full bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors">Login</button>
                  </form>
                </>
              )}
              {!localLoginEnabled && oidcProviders.length === 0 && !error && (
                <p className="text-sm text-text-muted dark:text-text-muted-dark">Single sign-on is currently unavailable. Please contact your administrator.</p>
              )}
              {error && <div className="flex items-center gap-2 text-error-text dark:text-error-text-dark text-sm mt-4">{error}</div>}
            <div className="text-center mt-8 group relative z-10">
              <div className="flex justify-center">
                <img src="/graphics/Swiish_Logo.svg" alt="Swiish" className="h-4 w-auto dark:hidden swiish-logo" />
                <img src="/graphics/Swiish_Logo_DarkBg.svg" alt="Swiish" className="h-4 w-auto hidden dark:block swiish-logo" />
              </div>
            </div>
            </div>
          </div>
          <VersionBadge />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'member-dashboard' && (
        <>
          <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture p-4 sm:p-8 flex flex-col">
            <div className="max-w-5xl w-full mx-auto flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-text-primary dark:text-text-primary-dark">My Cards</h1>
                  <p className="text-sm text-text-muted dark:text-text-muted-dark mt-1">View and manage your own digital business cards.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={toggleDarkMode} aria-label="Toggle dark mode" className="px-3 py-2 rounded-full bg-card dark:bg-card-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark">
                    {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button onClick={handleLogout} className="px-4 py-2 rounded-full bg-card dark:bg-card-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark font-medium">Logout</button>
                  <button onClick={handleCreateNew} className="px-4 py-2 rounded-full bg-action dark:bg-action-dark text-white font-bold flex items-center gap-2">
                    <Plus className="w-4 h-4" /> New Card
                  </button>
                </div>
              </div>
              {cardList.some(card => card.slug) ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cardList.filter(card => card.slug).map(card => (
                    <div key={card.slug} className="bg-card dark:bg-card-dark border border-border dark:border-border-dark rounded-card shadow-sm p-5 flex flex-col gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-16 h-16 flex-shrink-0 rounded-full overflow-hidden bg-surface dark:bg-surface-dark border border-border dark:border-border-dark">
                          {card.avatar ? <img src={card.avatar} alt="Profile" className="w-full h-full object-cover" /> : <User className="w-full h-full p-4 text-text-muted dark:text-text-muted-dark" />}
                        </div>
                        <div className="min-w-0">
                          <h2 className="font-semibold text-text-primary dark:text-text-primary-dark truncate">{card.name || card.slug}</h2>
                          {card.title && <p className="text-sm text-text-muted dark:text-text-muted-dark truncate">{card.title}</p>}
                          <p className="text-xs text-text-muted dark:text-text-muted-dark truncate">/{card.shortCode || `${card.orgSlug ? `${card.orgSlug}/` : ''}${card.slug}`}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-auto">
                        <a href={card.shortCode ? `/${card.shortCode}` : card.orgSlug ? `/${card.orgSlug}/${card.slug}` : `/${card.slug}`} target="_blank" rel="noreferrer" className="flex-1 py-2 rounded-button text-sm font-medium bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark border border-border dark:border-border-dark flex items-center justify-center gap-2">
                          <ExternalLink className="w-4 h-4" /> View
                        </a>
                        <button onClick={() => handleEdit(card.slug)} className="flex-1 py-2 rounded-button text-sm font-semibold bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark flex items-center justify-center gap-2">
                          <Edit3 className="w-4 h-4" /> Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-card dark:bg-card-dark border-2 border-dashed border-border dark:border-border-dark rounded-card p-8 sm:p-12 text-center">
                  <User className="w-12 h-12 mx-auto mb-4 text-text-muted dark:text-text-muted-dark" />
                  <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">You don't have a card yet</h2>
                  <p className="text-sm text-text-muted dark:text-text-muted-dark my-3">Create your first digital business card to get started.</p>
                  <button onClick={handleCreateNew} className="px-5 py-3 rounded-button bg-action dark:bg-action-dark text-white font-bold inline-flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Create Card
                  </button>
                </div>
              )}
            </div>
          </div>
          <VersionBadge />
          <Modal
            isOpen={createCardModal.isOpen}
            onClose={handleCreateCardCancel}
            type="info"
            title="Create New Card"
            message="Enter a user URL for the new card (e.g., 'sarah'):"
            inputLabel="User URL"
            inputPlaceholder="sarah"
            inputValue={createCardModal.slug}
            onInputChange={(value) => setCreateCardModal(prev => ({ ...prev, slug: value }))}
            onConfirm={handleCreateCardConfirm}
            confirmText="Create"
            cancelText="Cancel"
          />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'admin-dashboard' && (
        <>
          <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture p-6 md:p-12 flex flex-col">
          <div className="max-w-6xl mx-auto flex-1 w-full">
            {/* UPDATED HEADER: flex-wrap + gap adjustments for mobile */}
            <div className="flex flex-wrap justify-between items-center mb-8 gap-4 relative z-10">
               <div>
                 <h1 className="text-2xl md:text-3xl font-bold text-text-primary dark:text-text-primary-dark">People</h1>
                 <p className="text-sm md:text-base text-text-muted dark:text-text-muted-dark">Manage your people</p>
               </div>
               <div className="flex flex-wrap gap-2 md:gap-3 w-full md:w-auto">
                 <button onClick={toggleDarkMode} className="px-3 py-2 md:px-4 md:py-3 rounded-full font-medium text-text-muted dark:text-text-muted-dark bg-card dark:bg-card-dark border border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors whitespace-nowrap flex items-center gap-2 text-sm md:text-base">
                   {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                 </button>
                 <button onClick={handleLogout} className="px-3 py-2 md:px-4 md:py-3 rounded-full font-medium text-text-muted dark:text-text-muted-dark bg-card dark:bg-card-dark border border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors whitespace-nowrap text-sm md:text-base">Logout</button>
                 {userRole === 'owner' && (
                   <button onClick={() => navigate('/settings')} className="px-3 py-2 md:px-4 md:py-3 rounded-full font-medium text-text-secondary dark:text-text-secondary-dark bg-card dark:bg-card-dark border border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors whitespace-nowrap flex items-center gap-2 text-sm md:text-base">
                     <Settings className="w-4 h-4" /> <span className="hidden sm:inline">Organisation</span><span className="sm:hidden">Org</span>
                   </button>
                 )}
                 {userRole === 'owner' && (
                   <button onClick={() => navigate('/users')} className="px-3 py-2 md:px-4 md:py-3 rounded-full font-medium text-text-secondary dark:text-text-secondary-dark bg-card dark:bg-card-dark border border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors whitespace-nowrap flex items-center gap-2 text-sm md:text-base">
                     <Users className="w-4 h-4" /> Users
                   </button>
                 )}
                 <button onClick={handleCreateNew} className="bg-action dark:bg-action-dark text-white px-4 py-2 md:px-6 md:py-3 rounded-full font-bold flex items-center gap-2 hover:bg-action-hover dark:hover:bg-action-hover-dark transition-all whitespace-nowrap text-sm md:text-base">
                   <Plus className="w-4 h-4 md:w-5 md:h-5" /> New Person
                 </button>
               </div>
            </div>
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
              {groupCardsByUser(cardList).map(user => {
                // Filter out entries without slugs (these represent users without cards)
                const userCards = user.cards.filter(c => c.slug);
                const userKey = user.userId || user.userEmail || 'unknown';
                
                return (
                  <div key={userKey} className="bg-card dark:bg-card-dark rounded-card shadow-sm border border-border-subtle dark:border-border-dark hover:shadow-md transition-shadow flex flex-col p-[15px] h-fit break-inside-avoid mb-6">
                    {/* User Info Box (top) - only shown for owners */}
                    {userRole === 'owner' && user.userEmail && (
                      <div className="w-full mb-[15px]">
                        <div className="bg-surface dark:bg-surface-dark/50 rounded-t-container rounded-b-badge p-5 text-xs border border-border dark:border-border-dark min-h-[130px]">
                          <div className="text-text-secondary dark:text-text-muted-dark truncate mb-2 font-medium">{user.userEmail}</div>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                              user.userRole === 'owner' 
                                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' 
                                : 'bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark'
                            }`}>
                              {user.userRole === 'owner' ? 'Owner' : 'Member'}
                            </span>
                            {user.userCreatedAt && (
                              <span className="text-text-muted dark:text-text-muted-dark text-[10px]">
                                {new Date(user.userCreatedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {user.userId && user.userId !== currentUserId && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border dark:border-border-dark">
                              {editingUserId === user.userId ? (
                                <div className="flex items-center gap-2 w-full">
                                  <select
                                    value={user.userRole}
                                    onChange={(e) => handleUpdateRole(user.userId, e.target.value)}
                                    className="flex-1 px-2 py-1 text-[10px] rounded border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                                  >
                                    <option value="member">Member</option>
                                    <option value="owner">Owner</option>
                                  </select>
                                  <button
                                    onClick={() => setEditingUserId(null)}
                                    className="px-2 py-1 text-[10px] bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded hover:bg-surface dark:hover:bg-surface-dark"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setEditingUserId(user.userId)}
                                    className="flex-1 px-2 py-1 text-[10px] bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded hover:bg-surface dark:hover:bg-surface-dark flex items-center justify-center gap-1"
                                  >
                                    <Edit3 className="w-3 h-3" /> Role
                                  </button>
                                  <button
                                    onClick={() => handleRemoveUser(user.userId, user.userEmail)}
                                    className="flex-1 px-2 py-1 text-[10px] bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center justify-center gap-1"
                                  >
                                    <Trash2 className="w-3 h-3" /> Remove
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {user.userId === currentUserId && (
                            <div className="text-[10px] text-text-muted dark:text-text-muted-dark italic mt-2 pt-2 border-t border-border dark:border-border-dark">
                              Cannot modify yourself
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Cards for this user - stacked vertically */}
                    <div className="w-full space-y-[15px]">
                      {userCards.length > 0 ? (
                        <>
                          {userCards.map(card => (
                            <div key={card.slug} className="bg-surface dark:bg-surface-dark/50 rounded-badge p-5 border border-border dark:border-border-dark" style={{ aspectRatio: '1.586 / 1' }}>
                              <div className="w-full h-full flex flex-col">
                                <div className="flex-1 flex flex-row items-start gap-3 mb-3 relative">
                                  <div className="w-20 h-20 rounded-full bg-surface dark:bg-surface-dark overflow-hidden border-thick border-border-subtle dark:border-border-dark flex-shrink-0">
                                    {card.avatar ? <img src={card.avatar} className="w-full h-full object-cover" alt="avatar" /> : <User className="w-full h-full p-5 text-text-muted-subtle dark:text-text-muted-dark" />}
                                  </div>
                                   <button onClick={(e) => { e.stopPropagation(); handleDelete(card.slug, card.userId); }} className="absolute top-0 right-0 p-2 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text dark:hover:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark rounded-full transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                  <div className="flex-1 flex flex-col text-left min-w-0">
                                    <h3 className="font-bold text-text-primary dark:text-text-primary-dark text-base mb-0.5 truncate">{card.name}</h3>
                                    {card.title && <p className="text-text-muted dark:text-text-muted-dark text-xs mb-1 truncate">{card.title}</p>}
                                    <div className="space-y-0.5">
                                      {card.shortCode && (
                                        <div className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark font-mono truncate" title="Short Code URL">
                                          <span className="text-text-muted-subtle dark:text-text-muted-dark">Short:</span> /{card.shortCode}
                                        </div>
                                      )}
                                      {card.orgSlug && card.slug ? (
                                        <div className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark font-mono truncate" title="Org-scoped URL">
                                          <span className="text-text-muted-subtle dark:text-text-muted-dark">URL:</span> /{card.orgSlug}/{card.slug}
                                        </div>
                                      ) : card.slug ? (
                                        <div className="text-[10px] text-text-muted-subtle dark:text-text-muted-dark font-mono truncate" title="Legacy URL">
                                          <span className="text-text-muted-subtle dark:text-text-muted-dark">URL:</span> /{card.slug}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center justify-center gap-2 w-full mt-auto">
                                  <a 
                                    href={card.shortCode ? `/${card.shortCode}` : (card.orgSlug && card.slug ? `/${card.orgSlug}/${card.slug}` : `/${card.slug}`)} 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className="flex-1 py-2 text-xs font-medium text-confirm-text dark:text-confirm-text-dark bg-confirm dark:bg-confirm-dark rounded-button hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark flex items-center justify-center gap-1"
                                  >
                                    <ExternalLink className="w-3 h-3"/> View
                                  </a>
                                   <button onClick={() => handleEdit(card.slug, card.userId)} className="flex-1 py-2 text-xs font-medium text-confirm-text dark:text-confirm-text-dark bg-confirm dark:bg-confirm-dark rounded-button hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark flex items-center justify-center gap-1"><Edit3 className="w-3 h-3"/> Edit</button>
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* Create Card button at bottom of card list */}
                          <div className="bg-surface dark:bg-surface-dark/30 rounded-t-badge rounded-b-container border-thick border-dashed border-border dark:border-border-dark p-[15px] flex items-center justify-center">
                            <button onClick={() => setCreateCardModal({ isOpen: true, slug: '', userId: user.userId || user.userEmail })} className="px-4 py-2 text-sm font-medium text-white bg-action dark:bg-action-dark rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-2"><Plus className="w-4 h-4"/> Create Card</button>
                          </div>
                        </>
                      ) : (
                        /* No cards - show Create Card button in place */
                        <div className="bg-surface dark:bg-surface-dark/50 rounded-t-badge rounded-b-container p-5 border border-border dark:border-border-dark" style={{ aspectRatio: '1.586 / 1' }}>
                          <div className="w-full h-full bg-surface dark:bg-surface-dark/30 rounded-t-badge rounded-b-badge border-thick border-dashed border-border dark:border-border-dark flex flex-col items-center justify-center">
                            <button onClick={() => setCreateCardModal({ isOpen: true, slug: '', userId: user.userId || user.userEmail })} className="px-4 py-3 text-sm font-medium text-white bg-action dark:bg-action-dark rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-2"><Plus className="w-4 h-4"/> Create Card</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {cardList.length === 0 && (
                 <div className="col-span-full py-20 text-center text-text-muted-subtle dark:text-text-muted-dark bg-card dark:bg-card-dark rounded-card border-thick border-dashed border-border dark:border-border-dark">
                   No people yet. Click "New Person" to start.
                 </div>
              )}
          </div>
          </div>
          </div>
          <div className="fixed bottom-4 right-4 z-10 text-center group">
            <div className="flex justify-center">
              <img src="/graphics/Swiish_Logo.svg" alt="Swiish" className="h-4 w-auto dark:hidden swiish-logo" />
              <img src="/graphics/Swiish_Logo_DarkBg.svg" alt="Swiish" className="h-4 w-auto hidden dark:block swiish-logo" />
            </div>
          </div>
          {/* Modals */}
          {actionSelectionModal.isOpen && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">What would you like to do?</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setActionSelectionModal({ isOpen: false });
                      setShowInviteModal(true);
                    }}
                    className="w-full px-4 py-3 bg-action dark:bg-action-dark text-white rounded-button font-medium hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center justify-center gap-2"
                  >
                    <Users className="w-4 h-4" /> Invite User
                  </button>
                  <button
                    onClick={() => {
                      setActionSelectionModal({ isOpen: false });
                      setShowCreateUserModal(true);
                    }}
                    className="w-full px-4 py-3 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-medium hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark flex items-center justify-center gap-2"
                  >
                    <User className="w-4 h-4" /> Create User
                  </button>
                </div>
                <button
                  onClick={() => setActionSelectionModal({ isOpen: false })}
                  className="w-full mt-4 px-4 py-2 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {/* Invite User Modal */}
          {showInviteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Invite User</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Email</label>
                    <input
                      type="email"
                      value={newInvitation.email}
                      onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                      placeholder="user@example.com"
                    />
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">An invitation email will be sent to this address</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Role</label>
                    <select
                      value={newInvitation.role}
                      onChange={(e) => setNewInvitation({ ...newInvitation, role: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                    >
                      <option value="member">Member</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowInviteModal(false);
                      setNewInvitation({ email: '', role: 'member' });
                    }}
                    className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendInvitation}
                    disabled={isSavingUser}
                    className="flex-1 px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50"
                  >
                    {isSavingUser ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Create User Modal */}
          {showCreateUserModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
                <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Create New User</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Email</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Password</label>
                    <input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                      placeholder="Minimum 8 characters"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Role</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                    >
                      <option value="member">Member</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowCreateUserModal(false);
                      setNewUser({ email: '', password: '', role: 'member' });
                    }}
                    className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateUser}
                    disabled={isSavingUser}
                    className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark disabled:opacity-50"
                  >
                    {isSavingUser ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <VersionBadge />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
          <Modal
            isOpen={createCardModal.isOpen}
            onClose={handleCreateCardCancel}
            type="info"
            title="Create New Card"
            message="Enter a user URL for the new card (e.g., 'sarah'):"
            inputLabel="User URL"
            inputPlaceholder="sarah"
            inputValue={createCardModal.slug}
            onInputChange={(value) => setCreateCardModal(prev => ({ ...prev, slug: value }))}
            onConfirm={handleCreateCardConfirm}
            confirmText="Create"
            cancelText="Cancel"
          />
        </>
      )}
      {view === 'admin-editor' && (
        <>
          <EditorView 
            data={data} 
            setData={setData} 
            onBack={() => { navigate('/people'); fetchCardList(); }} 
            onSave={handleSave}
            slug={currentSlug}
            settings={settings}
            csrfToken={csrfToken}
            showAlert={showAlert}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            isSaving={isSaving}
            isSuccess={isSuccess}
            onLogout={userRole === 'member' ? handleLogout : undefined}
          />
          <VersionBadge />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'admin-settings' && (
        <>
          <SettingsView
            settings={settings}
            setSettings={setSettings}
            apiCall={apiCall}
            onBack={() => { navigate('/people'); fetchCardList(); }}
            onSave={async () => {
              await fetchSettings();
              fetchCardList();
              }}
            showAlert={showAlert}
            showConfirm={showConfirm}
          />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
      {view === 'user-management' && (
        <>
          <UserManagementView
            apiCall={apiCall}
            userRole={userRole}
            onBack={() => { navigate('/people'); fetchCardList(); }}
            showAlert={showAlert}
            showConfirm={showConfirm}
          />
          <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
        </>
      )}
    </>
  );

  return (
    <>
      <DemoModeBanner />
      <Routes>
        {/* Admin routes - must come before public routes to prevent matching */}
        <Route path="/login" element={renderAdminViews()} />
        <Route path="/setup" element={renderAdminViews()} />
        <Route path="/people/edit/:slug" element={renderAdminViews()} />
        <Route path="/people" element={renderAdminViews()} />
        <Route path="/settings" element={renderAdminViews()} />
        <Route path="/users" element={renderAdminViews()} />
        <Route path="/cards" element={renderAdminViews()} />
        <Route path="/" element={renderAdminViews()} />
        {/* Invitation acceptance route - must come before public card routes */}
        <Route path="/invite/:token" element={
          <InvitationAcceptance
            apiCall={apiCall}
            showAlert={showAlert}
            API_ENDPOINT={API_ENDPOINT}
          />
        } />
        {/* Public card routes - org-scoped must come before single slug */}
        <Route path="/:orgSlug/:cardSlug" element={
          <>
            <PublicCardRoute
              view={view}
              isPublicLoading={isPublicLoading}
              error={error}
              data={data}
              settings={settings}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              showAlert={showAlert}
              fetchCardByOrgAndSlug={fetchCardByOrgAndSlug}
              fetchCardByShortCode={fetchCardByShortCode}
              fetchPublicCard={fetchPublicCard}
            />
            <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
          </>
        } />
        {/* Public card route - matches short code or legacy slug */}
        <Route path="/:slug" element={
          <>
            <PublicCardRoute
              view={view}
              isPublicLoading={isPublicLoading}
              error={error}
              data={data}
              settings={settings}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              showAlert={showAlert}
              fetchCardByOrgAndSlug={fetchCardByOrgAndSlug}
              fetchCardByShortCode={fetchCardByShortCode}
              fetchPublicCard={fetchPublicCard}
            />
            <Modal isOpen={modal.isOpen} onClose={closeModal} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} confirmText={modal.confirmText} cancelText={modal.cancelText} />
          </>
        } />
        {/* Catch-all for other routes */}
        <Route path="*" element={renderAdminViews()} />
      </Routes>
    </>
  );
}

function PublicCardRoute({ view, isPublicLoading, error, data, settings, darkMode, toggleDarkMode, showAlert, fetchCardByOrgAndSlug, fetchCardByShortCode, fetchPublicCard }) {
  const params = useParams();
  const location = useLocation();
  
  
  // Track the last fetched pathname to prevent duplicate fetches
  const lastFetchedPathRef = useRef(null);
  
  // Apply app theme variant class to body for CSS overrides (swiish|minimal|custom)
  // This ensures the theme is applied to public cards the same way as admin views
  useEffect(() => {
    const variant = settings?.theme_variant || 'swiish';
    document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant);
  }, [settings]);
  
  // Fetch the card when the route changes
  useEffect(() => {
    const path = location.pathname;
    
    // Single guard: skip if we've already fetched for this exact pathname
    if (lastFetchedPathRef.current === path) {
      return;
    }
    
    // Parse route to determine fetch strategy
    const pathParts = path.substring(1).split('/').filter(p => p);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const isOrgScoped = pathParts.length === 2 && pathParts[0] && pathParts[1];
    const isLegacy = pathParts.length === 1 && !isShortCode;
    
    // Mark this path as being fetched BEFORE calling fetch (prevents race conditions)
    lastFetchedPathRef.current = path;
    
    // Fetch based on route type
    if (isShortCode && fetchCardByShortCode) {
      fetchCardByShortCode(pathParts[0]);
    } else if (isOrgScoped && fetchCardByOrgAndSlug) {
      fetchCardByOrgAndSlug(pathParts[0], pathParts[1]);
    } else if (isLegacy && fetchPublicCard) {
      fetchPublicCard(pathParts[0]);
    } else {
      console.error('[PublicCardRoute] No valid fetch strategy for path:', path);
      // Reset ref if we can't fetch (allows retry on next render if functions become available)
      lastFetchedPathRef.current = null;
    }
  }, [location.pathname, fetchCardByOrgAndSlug, fetchCardByShortCode, fetchPublicCard]);
  
  // Show loading state while fetching card
  const pathParts = location.pathname.substring(1).split('/').filter(p => p);
  const hasPublicRouteParams = params.slug || params.cardSlug || params.orgSlug || pathParts.length > 0;
  
  if (isPublicLoading || ((view === 'loading' || view === 'public-loading') && hasPublicRouteParams)) {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-text-muted-subtle dark:text-text-muted-dark">Loading...</div>
      </div>
    );
  }
  
  // Show 404 if card not found
  if (view === '404') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">Card Not Found</h1>
          <p className="text-text-secondary dark:text-text-muted-dark">{error || 'The card you are looking for does not exist.'}</p>
        </div>
      </div>
    );
  }
  
  // Show card when loaded
  if (view === 'public-card' && data && data.personal) {
  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-start lg:items-center p-0 lg:p-8">
      <div className="w-full max-w-md bg-transparent min-h-screen lg:min-h-0 lg:h-auto lg:rounded-page shadow-2xl overflow-hidden relative z-10 animate-in fade-in duration-500 flex flex-col">
        <div className="flex-1">
          <CardDisplay
            data={data}
            settings={settings}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            showAlert={showAlert}
          />
        </div>
      </div>
      </div>
    );
  }
  
  // Default: show loading (shouldn't reach here, but safety net)
  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify_center items-center">
      <div className="text-text-muted-subtle dark:text-text-muted-dark">Loading...</div>
    </div>
  );
}

function CardDisplay({ data, settings, showAlert }) {
  const { personal = {}, contact = {}, social = {}, images = {}, theme = { color: 'indigo' }, links = [], privacy = {}, sendDetails = {}, typography = {}, buttonStyles = {} } = data;
  const effectiveThemeColorName = settings?.allow_theme_customisation === false
    ? (settings?.theme_colors?.[0]?.name || theme.color)
    : theme.color;
  const themeColor = settings?.theme_colors?.find(c => c.name === effectiveThemeColorName);
  const organisationBanner = settings?.organisation_banner || {};
  const organisationAppearance = normalizeCardAppearance(settings?.card_appearance);
  const allowThemeOverrides = settings?.allow_theme_customisation !== false;
  const cardPresetOverride = ['light', 'dark'].includes(theme.cardPreset) ? theme.cardPreset : null;
  const effectiveCardPreset = allowThemeOverrides && cardPresetOverride ? cardPresetOverride : organisationAppearance.preset;
  const effectiveTypography = mergeSparseAppearanceGroup(organisationAppearance.typography, typography, allowThemeOverrides);
  const effectiveButtonStyles = mergeSparseAppearanceGroup(organisationAppearance.buttonStyles, buttonStyles, allowThemeOverrides);
  const cardThemeClass = effectiveCardPreset === 'dark' ? 'card-theme-dark' : 'card-theme-light';
  const semantic = (token) => `var(--color-${token}-${effectiveCardPreset})`;
  const hasOrganisationBanner = !!organisationBanner.banner;
  const allowBannerOverride = settings?.allow_image_customisation !== false && settings?.allow_banner_override !== false;
  const effectiveBannerImages = hasOrganisationBanner && (!allowBannerOverride || !images.banner)
    ? { ...images, ...organisationBanner }
    : images;
  const [showQR, setShowQR] = useState(false);
  const [showMapOptions, setShowMapOptions] = useState(false);
  const [qrMode, setQrMode] = useState(() => {
    if (typeof navigator !== 'undefined') {
      return navigator.onLine ? 'simple' : 'rich';
    }
    return 'simple';
  });
  const [qrSimpleDataUrl, setQrSimpleDataUrl] = useState('');
  const [qrRichDataUrl, setQrRichDataUrl] = useState('');
  const [offlineQrPayload, setOfflineQrPayload] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [qrError, setQrError] = useState(null);
  const [contactRevealed, setContactRevealed] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);
  
  // Lead capture deep links, using the card owner's contact details
  const ownerPhone = contact.phone || '';
  const ownerEmail = contact.email || '';
  // Extract digits only from E.164 format (e.g., +447779331447 -> 447779331447)
  // This works for both E.164 (+44...) and legacy formats
  const ownerPhoneDigits = ownerPhone.replace(/\D/g, '');

  const whatsappLink = ownerPhoneDigits && ownerPhoneDigits.length >= 8
    ? `https://wa.me/${ownerPhoneDigits}?text=${encodeURIComponent(
        'Hi, we met via your Swiish card. My name is ... and my number/email is ...'
      )}`
    : null;

  const emailLink = ownerEmail
    ? `mailto:${ownerEmail}?subject=${encodeURIComponent(
        'My details from Swiish'
      )}&body=${encodeURIComponent(
        'Hi, we met via your Swiish card.\nMy name is ...\nMy phone number is ...\nMy email address is ...'
      )}`
    : null;

  const dropCallLink = ownerPhone ? `tel:${ownerPhone}` : null;

  // "Send your details" visibility, gated by per-card master + per-channel toggles.
  // Use `!== false` so cards saved before this feature existed keep their current
  // (fully enabled) behaviour.
  const sendDetailsEnabled = sendDetails.enabled !== false;
  const showWhatsappOption = sendDetailsEnabled && sendDetails.whatsapp !== false && !!whatsappLink;
  const showEmailSendOption = sendDetailsEnabled && sendDetails.email !== false && !!emailLink;
  const showDropCallOption = sendDetailsEnabled && sendDetails.call !== false && !!dropCallLink;
  const showSendDetailsBlock = showWhatsappOption || showEmailSendOption || showDropCallOption;

  // Location links are generated dynamically from the current card location.
  // Android can hand the generic geo: URI to installed map apps; iOS uses an
  // in-card chooser because normal web pages do not get a generic maps-app chooser.
  const locationText = sanitizeText(personal.location || '').trim();
  const encodedLocation = encodeURIComponent(locationText);
  const googleMapsUrl = locationText
    ? `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`
    : '';
  const appleMapsUrl = locationText
    ? `https://maps.apple.com/?q=${encodedLocation}`
    : '';

  const handleLocationClick = (event) => {
    if (!locationText || typeof navigator === 'undefined') return;
    const userAgent = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(userAgent);

    if (isIOS) {
      event.preventDefault();
      setShowMapOptions(true);
      return;
    }

    if (isAndroid) {
      event.preventDefault();
      window.location.href = `geo:0,0?q=${encodedLocation}`;
    }
    // Desktop keeps the normal anchor behaviour and opens Google Maps web.
  };

  const organisationBodyBackground = organisationAppearance.bodyBackground || getDefaultCardAppearance().bodyBackground;
  const cardBodyMode = ['color', 'image'].includes(theme.bodyBackgroundMode) ? theme.bodyBackgroundMode : null;
  const cardBodyHasUsableOverride = allowThemeOverrides && (
    cardBodyMode === 'color' || (cardBodyMode === 'image' && !!images.bodyBackground)
  );
  const effectiveBodyBackground = cardBodyHasUsableOverride
    ? {
        mode: cardBodyMode,
        color: /^#[0-9a-fA-F]{6}$/.test(theme.bodyBackgroundColor || '') ? theme.bodyBackgroundColor : '#ffffff',
        image: images.bodyBackground || '',
        positionX: Number(images.bodyBackgroundPositionX ?? 50),
        positionY: Number(images.bodyBackgroundPositionY ?? 50),
        zoom: Number(images.bodyBackgroundZoom ?? 1)
      }
    : organisationBodyBackground;
  const effectiveBodyMode = effectiveBodyBackground.mode === 'image' && !effectiveBodyBackground.image
    ? 'theme'
    : effectiveBodyBackground.mode;
  const hasBodyBackgroundImage = effectiveBodyMode === 'image' && !!effectiveBodyBackground.image;
  const effectiveBodyBackgroundImages = {
    bodyBackground: effectiveBodyBackground.image || '',
    bodyBackgroundPositionX: effectiveBodyBackground.positionX ?? 50,
    bodyBackgroundPositionY: effectiveBodyBackground.positionY ?? 50,
    bodyBackgroundZoom: effectiveBodyBackground.zoom ?? 1
  };

  const getDefaultTypographyColor = (field) => {
    if (field === 'title') return themeColor?.textStyle || getTextColor(effectiveThemeColorName, settings);
    if (field === 'name') return semantic('text-primary');
    if (field === 'nativeName' || field === 'bio') return semantic('text-secondary');
    if (field === 'company') return semantic('text-muted');
    if (field === 'location') return effectiveCardPreset === 'dark' ? semantic('text-muted') : semantic('text-muted-subtle');
    return semantic('text-primary');
  };

  const getTypographyStyle = (field) => {
    const config = effectiveTypography?.[field] || {};
    const style = { color: /^#[0-9a-fA-F]{6}$/.test(config.color || '') ? config.color : getDefaultTypographyColor(field) };
    const size = Number(config.size);
    if (TYPOGRAPHY_SIZE_OPTIONS.includes(size)) style.fontSize = `${size}px`;
    return style;
  };

  const getTypographyIconStyle = (field) => {
    const size = Number(effectiveTypography?.[field]?.size);
    if (!TYPOGRAPHY_SIZE_OPTIONS.includes(size)) return undefined;
    const iconSize = Math.max(14, Math.min(26, Math.round(size * 0.9)));
    return { width: `${iconSize}px`, height: `${iconSize}px` };
  };

  const resolveButtonStyle = (group) => {
    const configured = effectiveButtonStyles?.[group] || {};
    let defaultBackground;
    let defaultColor;
    if (group === 'saveContact') {
      defaultBackground = themeColor?.buttonStyle || getButtonColor(theme.color, settings);
      defaultColor = '#ffffff';
    } else if (group === 'sendDetails') {
      defaultBackground = semantic('confirm');
      defaultColor = semantic('confirm-text');
    } else {
      defaultBackground = semantic('surface');
      defaultColor = semantic('text-primary');
    }
    return {
      backgroundColor: /^#[0-9a-fA-F]{6}$/.test(configured.background || '') ? configured.background : defaultBackground,
      color: /^#[0-9a-fA-F]{6}$/.test(configured.color || '') ? configured.color : defaultColor,
      borderColor: group === 'general' ? semantic('border') : 'transparent'
    };
  };

  const generalButtonStyle = resolveButtonStyle('general');
  const saveContactButtonStyle = resolveButtonStyle('saveContact');
  const sendDetailsButtonStyle = resolveButtonStyle('sendDetails');
  const cardBaseBackground = semantic('card');
  const cardSurfaceBackground = semantic('surface');
  const cardBorderColor = semantic('border');

  // Helper functions for obfuscation
  const obfuscateContact = (value) => {
    if (!value) return '';
    return btoa(value);
  };
  
  const deobfuscateContact = (obfuscated) => {
    if (!obfuscated) return '';
    try {
      return atob(obfuscated);
    } catch (e) {
      return '';
    }
  };
  
  // Apply app theme variant class to body for CSS overrides (swiish|minimal|custom)
  // This ensures the theme is applied when CardDisplay renders (both in editor preview and public view)
  useEffect(() => {
    const variant = settings?.theme_variant || 'swiish';
    document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant);
  }, [settings]);

  // Collapse the send-options panel if the block becomes hidden (e.g. while editing
  // toggles live in the editor preview) so it never opens onto an empty panel.
  useEffect(() => {
    if (!showSendDetailsBlock && showSendOptions) {
      setShowSendOptions(false);
    }
  }, [showSendDetailsBlock, showSendOptions]);
  
  // UPDATED: Set Title
  useEffect(() => {
    const firstName = sanitizeText(personal.firstName || '');
    const lastName = sanitizeText(personal.lastName || '');
    if (firstName || lastName) {
      document.title = `${firstName} ${lastName}`;
    }
  }, [personal]);

  // Dynamic per-card manifest link (per-card app name & start_url)
  // Note: The manifest link is updated synchronously in index.html before React loads
  // This useEffect is just a backup to ensure it's correct after navigation
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const path = window.location.pathname || '';
    const pathParts = path.replace(/^\//, '').split('/').filter(Boolean);
    const appRouteRoots = new Set(['login', 'setup', 'people', 'settings', 'users', 'cards', 'invite', 'admin']);
    if (pathParts.length === 0 || appRouteRoots.has((pathParts[0] || '').toLowerCase())) return;

    // Only public-card routes may replace the main-app manifest. This component
    // is also rendered inside /people/edit as a preview, which must remain the main app.
    const manifestIdentifier = data?._shortCode || (pathParts.length === 1 ? pathParts[0] : '');
    if (!manifestIdentifier) return;

    const head = document.head;
    let link = document.querySelector('link[rel="manifest"]');
    const dynamicHref = `/manifest/${manifestIdentifier}.json`;

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'manifest');
      head.appendChild(link);
    }

    if (link.getAttribute('href') !== dynamicHref) {
      link.setAttribute('href', dynamicHref);
    }
  }, [data?._shortCode]);

  // Handle PWA install prompt (keep button visible until actually installed)
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsPwaInstalled(true);
      setDeferredPrompt(null);
    };

    const checkStandalone = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      if (isStandalone) {
        setIsPwaInstalled(true);
      }
    };

    checkStandalone();

    // Set up event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const shouldShowInstallButton = !isPwaInstalled;

  // Track online/offline status for UI
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle robots meta tag
  useEffect(() => {
    if (privacy.blockRobots) {
      let metaRobots = document.querySelector('meta[name="robots"]');
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.remove();
      }
    }
    
    // Cleanup on unmount
    return () => {
      const metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.remove();
      }
    };
  }, [privacy.blockRobots]);

  // Fetch QR code when modal opens; also cache rich payload for offline use
  useEffect(() => {
    if (!showQR) return;

    setQrError(null);

    // Get short code from data (if available) or extract from URL
    const pathParts = typeof window !== 'undefined' ? window.location.pathname.substring(1).split('/').filter(p => p) : [];
    const isShortCodeRoute = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
    const shortCode = data._shortCode || (isShortCodeRoute ? pathParts[0] : null);
    
    // For QR generation, always use short code if available, otherwise fallback to slug
    const qrIdentifier = shortCode || (pathParts.length > 0 ? pathParts[pathParts.length - 1] : '');
    
    const payload = buildQrPayload(shortCode, { personal, contact, social, images, theme });
    // Always cache latest rich payload for offline use
    saveQrPayloadToStorage(payload);

    // Online check – if offline, try to use cached payload instead of hitting API
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    if (!isOnline) {
      // In offline mode, only rich mode can leverage cached payload;
      // simple (link-only) QR is effectively online-only unless previously loaded.
      if (qrMode === 'rich') {
        const cached = loadQrPayloadFromStorage();
        if (cached) {
          setOfflineQrPayload(cached);
        }
      }
      return;
    }

    if (qrMode === 'simple' && !qrSimpleDataUrl) {
      fetch(`${API_ENDPOINT}/qr/${qrIdentifier}`, {
        method: 'GET',
        credentials: 'include'
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`QR request failed: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          if (data?.qrCode) {
            setQrSimpleDataUrl(data.qrCode);
          }
        })
        .catch(err => {
          console.error('Failed to fetch simple QR code:', err);
          setQrError('Unable to load link-only QR right now. Please try again in a moment.');
        });
    } else if (qrMode === 'rich' && !qrRichDataUrl) {
      fetch(`${API_ENDPOINT}/qr/${qrIdentifier}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payload })
      })
        .then(res => {
          if (!res.ok) {
            throw new Error(`QR request failed: ${res.status}`);
          }
          return res.json();
        })
        .then(data => {
          if (data?.qrCode) {
            setQrRichDataUrl(data.qrCode);
          }
          saveQrPayloadToStorage(payload);
          setOfflineQrPayload(payload);
        })
        .catch(err => {
          console.error('Failed to fetch rich QR code:', err);
          const cached = loadQrPayloadFromStorage();
          if (cached) {
            setOfflineQrPayload(cached);
          }
          setQrError('Unable to load full-details QR right now. Your last saved details are still available offline.');
        });
    }
  }, [showQR, qrMode, qrSimpleDataUrl, qrRichDataUrl, personal, contact, social, images, theme, data]);

  const generateVCard = () => {
    const firstName = sanitizeText(personal.firstName || '');
    const lastName = sanitizeText(personal.lastName || '');
    const nativeName = sanitizeText(personal.nativeName || '');
    const company = sanitizeText(personal.company || '');
    const title = sanitizeText(personal.title || '');
    const phone = sanitizeText(contact.phone || '');
    const email = sanitizeText(contact.email || '');
    const website = sanitizeText(contact.website || '');
    const bio = sanitizeText(personal.bio || '');
    const currentPathParts = typeof window !== 'undefined'
      ? window.location.pathname.substring(1).split('/').filter(p => p)
      : [];
    const routeShortCode = currentPathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(currentPathParts[0])
      ? currentPathParts[0]
      : null;
    const cardShortCode = data._shortCode || routeShortCode;
    const cardUrl = typeof window !== 'undefined' && cardShortCode
      ? `${window.location.origin}/${cardShortCode}`
      : '';
    const nativeNameLines = nativeName
      ? `NICKNAME:${nativeName}\nX-NATIVE-NAME:${nativeName}\n`
      : '';
    const urlLines = `${website ? `URL:${website}\n` : ''}${cardUrl && cardUrl !== website ? `URL:${cardUrl}\n` : ''}`;
    
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${firstName} ${lastName}
N:${lastName};${firstName};;;
${nativeNameLines}ORG:${company}
TITLE:${title}
TEL;TYPE=CELL:${phone}
EMAIL;TYPE=WORK:${email}
${urlLines}NOTE:${bio}
END:VCARD`;
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${firstName}_${lastName}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const currentQrDataUrl = qrMode === 'simple' ? qrSimpleDataUrl : qrRichDataUrl;

  // Extract short code for display
  const pathParts = typeof window !== 'undefined' ? window.location.pathname.substring(1).split('/').filter(p => p) : [];
  const isShortCodeRoute = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(pathParts[0]);
  const shortCode = data._shortCode || (isShortCodeRoute ? pathParts[0] : null);
  const shortUrl = shortCode ? `${window.location.origin}/${shortCode}` : '';
  const cardName = `${personal.firstName || ''} ${personal.lastName || ''}`.trim();
  const company = personal.company || '';

  // If QR is shown, render only the QR view (full screen, independent of card)
  if (showQR) {
    return (
      <div className={`fixed inset-0 ${cardThemeClass} bg-card flex flex-col text-center overflow-hidden lg:rounded-[22px] z-50 min-h-screen lg:min-h-0 lg:h-auto`} style={{ backgroundColor: cardBaseBackground }}>
        {/* QR Code section at top */}
        <div className="flex flex-col items-center justify-start pt-8 px-4 pb-8 lg:pt-8 lg:flex-shrink-0">
          <div className="w-[90%]">
            <div className="w-full bg-input-bg dark:bg-input-bg-dark p-3 rounded-input border-thick border-border-subtle dark:border-border-dark flex items-center justify-center overflow-hidden">
              {currentQrDataUrl ? (
                <img src={currentQrDataUrl} className={`w-full aspect-square ${effectiveCardPreset === 'dark' ? 'mix-blend-normal' : 'mix-blend-multiply'}`} alt="QR code" />
              ) : qrMode === 'rich' && offlineQrPayload ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-text-muted-subtle dark:text-text-secondary-dark text-xs space-y-1">
                  <span>{isOnline ? 'Saved details' : 'Offline mode'}</span>
                  <span className="text-[10px] opacity-80 px-1">
                    This code includes your saved Swiish details and a link to your card when scanned with an online device.
                  </span>
                </div>
              ) : (!isOnline && qrMode === 'simple' && !qrSimpleDataUrl) ? (
                <div className="w-full aspect-square flex flex-col items-center justify-center text-text-muted-subtle dark:text-text-secondary-dark text-xs text-center space-y-1">
                  <span>Link-only QR is available when you&apos;re online.</span>
                  <span className="text-[10px] opacity-80 px-1">
                    Switch to \"Full details\" to use your saved offline code.
                  </span>
                </div>
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark text-xs text-center">
                  {isOnline
                    ? (qrError || 'Loading your QR code...')
                    : 'Connect once to generate and save your QR code for offline use.'}
                </div>
              )}
            </div>
          </div>

          {/* Card information display */}
          <div className="mt-6 space-y-2 px-4">
            {cardName && (
              <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">{cardName}</h2>
            )}
            {company && (
              <div className="text-text-muted dark:text-text-muted-dark text-sm">{company}</div>
            )}
            {shortUrl && (
              <div className="text-text-muted-subtle dark:text-text-secondary-dark text-xs font-mono break-all">{shortUrl}</div>
            )}
          </div>

          {/* Offline note */}
          {!isOnline && offlineQrPayload && (
            <p className="text-xs text-text-muted dark:text-text-muted-dark mt-4 px-4">
              Using last saved QR details (offline). The code includes your contact info and a link to your Swiish card.
            </p>
          )}
        </div>

        {/* Controls and logo section at bottom */}
        <div className="mt-auto pb-4 px-4 space-y-3 lg:pb-4 lg:pt-4 lg:flex-shrink-0">
          {/* Toggle buttons */}
          <div className="flex w-full items-center justify-center gap-1 bg-surface dark:bg-surface-dark rounded-full p-1 text-[11px] max-w-md mx-auto">
            <button
              type="button"
              onClick={() => setQrMode('simple')}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${
                qrMode === 'simple'
                  ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                  : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              Link only
            </button>
            <button
              type="button"
              onClick={() => setQrMode('rich')}
              className={`flex-1 px-2.5 py-1 rounded-full font-medium transition-colors ${
                qrMode === 'rich'
                  ? 'bg-card dark:bg-main-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                  : 'text-text-muted dark:text-text-secondary-dark'
              }`}
            >
              Full details
            </button>
          </div>

          {/* Close button */}
          <button onClick={() => setShowQR(false)} className="w-full max-w-md mx-auto py-3 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark font-bold rounded-input hover:bg-surface dark:hover:bg-surface-dark text-sm transition-colors">Close</button>

          {/* Swiish logo */}
          <div className="bg-card dark:bg-card-dark text-center space-y-2 mt-[24px] mb-[12px]">
            <div className="flex justify-center py-4">
              <img src="/graphics/Swiish_Logo.svg" alt="Swiish" className="h-4 w-auto dark:hidden swiish-logo" />
              <img src="/graphics/Swiish_Logo_DarkBg.svg" alt="Swiish" className="h-4 w-auto hidden dark:block swiish-logo" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise, render the normal card view
  return (
    <div
      className={`flex flex-col h-full relative overflow-hidden ${cardThemeClass}`}
      style={{ backgroundColor: effectiveBodyMode === 'color' ? effectiveBodyBackground.color : cardBaseBackground }}
    >
      {hasBodyBackgroundImage && (
        <div className="absolute inset-x-0 top-44 bottom-0 overflow-hidden pointer-events-none">
          <BodyBackgroundImage images={effectiveBodyBackgroundImages} alt="Card body background" />
        </div>
      )}
      <div className="h-44 w-full relative z-10" style={{ backgroundColor: cardSurfaceBackground }}>
        {effectiveBannerImages.banner ? (
          <BannerImage images={effectiveBannerImages} alt="Header banner" />
        ) : (
          (() => {
            const gradientStyle = getThemeGradient(effectiveThemeColorName, settings);
            return <div className="w-full h-full opacity-90" style={{ background: gradientStyle }} />;
          })()
        )}
        <div className="absolute top-4 right-4 flex gap-2">
          <button onClick={() => setShowQR(true)} className="bg-black/55 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-black/70 transition-all border border-white/30 shadow-lg" aria-label="Show QR code" title="Show QR code">
            <Share2 className="w-5 h-5" />
          </button>
          {shouldShowInstallButton && (
            <button
              onClick={async () => {
                if (deferredPrompt) {
                  try {
                    await deferredPrompt.prompt();
                    const { outcome } = await deferredPrompt.userChoice;
                    if (outcome === 'accepted') {
                      setIsPwaInstalled(true);
                    }
                  } catch (e) {
                    console.error('Install prompt failed:', e);
                    if (typeof showAlert === 'function') {
                      showAlert(
                        'Install prompt failed. Please use your browser menu to install: Chrome/Edge (three dots menu > Install app), Firefox (menu > Install), or Safari (Share > Add to Home Screen).',
                        'error',
                        'Install Failed'
                      );
                    }
                  } finally {
                    setDeferredPrompt(null);
                  }
                } else {
                  // Check if app is already installed
                  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                                      window.navigator.standalone === true;
                  
                  if (isStandalone) {
                    if (typeof showAlert === 'function') {
                      showAlert(
                        'This app is already installed on your device.',
                        'info',
                        'Already Installed'
                      );
                    }
                    setIsPwaInstalled(true);
                  } else {
                    // Provide manual installation instructions
                    const userAgent = navigator.userAgent.toLowerCase();
                    let instructions = 'To install this app:\n\n';
                    
                    if (userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('crios') && !userAgent.includes('fxios') && !userAgent.includes('edgios')) {
                      instructions = '1. Tap the Share button in Safari.\n2. Choose "Add to Home Screen".\n3. Tap "Add".';
                    } else if (userAgent.includes('chrome') || userAgent.includes('edge') || userAgent.includes('crios') || userAgent.includes('edgios')) {
                      instructions += 'Chrome/Edge: Open the browser menu, then choose "Install app" or "Add to Home Screen".';
                    } else if (userAgent.includes('firefox') || userAgent.includes('fxios')) {
                      instructions += 'Firefox: Open the browser menu, then choose "Install" or "Add to Home Screen".';
                    } else {
                      instructions += 'Open your browser menu and look for "Install app" or "Add to Home Screen".';
                    }
                    
                    if (typeof showAlert === 'function') {
                      showAlert(
                        instructions,
                        'info',
                        'Install Swiish'
                      );
                    } else {
                      // Fallback if showAlert is not available
                      alert(instructions);
                    }
                  }
                }
              }}
              className="bg-black/55 backdrop-blur-md p-2.5 rounded-full text-white hover:bg-black/70 transition-all border border-white/30 shadow-lg"
              aria-label="Install app for offline access"
              title="Install app for offline access"
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          {!isOnline && offlineQrPayload && (
            <span className="hidden xs:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-semibold bg-amber-500/80 text-white shadow-sm">
              Offline QR ready
            </span>
          )}
        </div>
      </div>

      <div className="px-6 pb-6 -mt-16 relative z-10 flex-1 flex flex-col min-h-0">
        <div className="w-32 h-32 min-h-[8rem] flex-shrink-0 rounded-full border-avatar border-white dark:border-card-dark shadow-xl overflow-hidden bg-card dark:bg-card-dark relative mb-4">
          {images.avatar ? <img src={images.avatar} className="w-full h-full object-cover" alt="avatar" /> : <div className="w-full h-full bg-surface dark:bg-surface-dark flex items-center justify-center text-text-muted-subtle dark:text-text-muted-dark"><User className="w-12 h-12" /></div>}
        </div>

        <div className="space-y-1 mb-8">
          <h1 className="text-3xl font-bold tracking-tight" style={getTypographyStyle('name')}>{sanitizeText(`${personal.firstName || ''} ${personal.lastName || ''}`).trim() || 'Untitled'}</h1>
          {personal.nativeName && (
            <div className="text-lg font-medium" style={getTypographyStyle('nativeName')}>
              {sanitizeText(personal.nativeName)}
            </div>
          )}
          {(() => {
            const color = settings?.theme_colors?.find(c => c.name === theme.color);
            const title = sanitizeText(personal.title || '');
            if (color?.textStyle) {
              return <div className="text-lg font-medium" style={{ color: color.textStyle, ...getTypographyStyle('title') }}>{title}</div>;
            }
            return <div className="text-lg font-medium" style={{ color: getTextColor(theme.color, settings), ...getTypographyStyle('title') }}>{title}</div>;
          })()}
          <div className="flex items-center text-sm gap-2" style={getTypographyStyle('company')}><Briefcase className="w-4 h-4 flex-shrink-0" style={getTypographyIconStyle('company')} /><span>{sanitizeText(personal.company || '')}</span></div>
          {locationText && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLocationClick}
              className="group inline-flex items-start text-sm gap-2 mt-1 transition-opacity hover:opacity-80"
              style={getTypographyStyle('location')}
              title="Open in Maps"
            >
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={getTypographyIconStyle('location')} />
              <span className="group-hover:underline underline-offset-2">{locationText}</span>
              <ExternalLink className="w-3.5 h-3.5 mt-0.5 opacity-0 group-hover:opacity-60 transition-opacity flex-shrink-0" />
            </a>
          )}
        </div>

        {personal.bio && <div className="mb-8"><p className="leading-relaxed text-sm" style={getTypographyStyle('bio')} dangerouslySetInnerHTML={{ __html: sanitizeHTML(personal.bio) }}></p></div>}

        <div className="grid grid-cols-2 gap-3 mb-8">
          {(() => {
            const requireInteraction = privacy.requireInteraction ?? true;
            const shouldShowVCF = !requireInteraction || contactRevealed;
            
            // Only show VCF button if interaction is not required OR contact has been revealed
            if (shouldShowVCF) {
              const color = settings?.theme_colors?.find(c => c.name === theme.color);
              if (color?.buttonStyle) {
                return (
                  <button 
                    onClick={generateVCard} 
                    className="col-span-2 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold shadow-lg transition-all active:scale-[0.98] hover:opacity-90"
                    style={saveContactButtonStyle}
                  >
                    <Save className="w-5 h-5" /> Save Contact
                  </button>
                );
              }
              return (
                <button 
                  onClick={generateVCard} 
                  className="col-span-2 flex items-center justify-center gap-2 py-3.5 rounded-full font-bold shadow-lg transition-all active:scale-[0.98] hover:opacity-90"
                  style={saveContactButtonStyle}
                >
                  <Save className="w-5 h-5" /> Save Contact
                </button>
              );
            }
            return null;
          })()}
          {(() => {
            const requireInteraction = privacy.requireInteraction ?? true;
            const useObfuscation = privacy.clientSideObfuscation ?? false;
            const hasEmail = contact.email;
            const hasPhone = contact.phone;
            
            // If interaction required and not yet revealed, show reveal button
            if (requireInteraction && !contactRevealed && (hasEmail || hasPhone)) {
              return (
                <button
                  onClick={() => setContactRevealed(true)}
                  className="col-span-2 flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold transition-all border hover:opacity-90"
                  style={generalButtonStyle}
                >
                  <Eye className="w-5 h-5" /> See my details
                </button>
              );
            }
            
            // Get actual contact values
            const emailValue = hasEmail ? contact.email : '';
            const phoneValue = hasPhone ? contact.phone : '';
            
            // If obfuscation is enabled, store obfuscated values in data attributes
            const emailData = useObfuscation && emailValue ? obfuscateContact(emailValue) : '';
            const phoneData = useObfuscation && phoneValue ? obfuscateContact(phoneValue) : '';
            
            return (
              <>
                {hasEmail && (
                  <a
                    href={useObfuscation ? '#' : `mailto:${emailValue}`}
                    data-email={useObfuscation ? emailData : undefined}
                    onClick={(e) => {
                      if (useObfuscation) {
                        e.preventDefault();
                        const actualEmail = deobfuscateContact(e.currentTarget.dataset.email);
                        window.location.href = `mailto:${actualEmail}`;
                      }
                    }}
                    className="flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold transition-all border hover:opacity-90"
                    style={generalButtonStyle}
                  >
                    <Mail className="w-5 h-5" /> Email
                  </a>
                )}
                {hasPhone && (
                  <a
                    href={useObfuscation ? '#' : `tel:${phoneValue}`}
                    data-phone={useObfuscation ? phoneData : undefined}
                    onClick={(e) => {
                      if (useObfuscation) {
                        e.preventDefault();
                        const actualPhone = deobfuscateContact(e.currentTarget.dataset.phone);
                        window.location.href = `tel:${actualPhone}`;
                      }
                    }}
                    className="flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold transition-all border hover:opacity-90"
                    style={generalButtonStyle}
                  >
                    <Phone className="w-5 h-5" /> Call
                  </a>
                )}
              </>
            );
          })()}
        </div>

        {/* Send your details CTA */}
        {showSendDetailsBlock && (
          <div className="mb-8">
            <button
              type="button"
              onClick={() => setShowSendOptions(open => !open)}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold hover:opacity-90 transition-all shadow-lg active:scale-[0.98]"
              style={sendDetailsButtonStyle}
            >
              <MessageCircle className="w-5 h-5" />
              {showSendOptions ? 'Hide send options' : 'Send your details'}
            </button>

            {showSendOptions && (
              <div className="mt-3 space-y-2 rounded-card border border-border dark:border-border-dark bg-surface/60 dark:bg-card-dark/60 p-3 text-left">
                {showWhatsappOption && (
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold bg-success dark:bg-success-dark text-white hover:bg-success-hover dark:hover:bg-success-hover-dark transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp me your number
                  </a>
                )}

                {showEmailSendOption && (
                  <a
                    href={emailLink}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                  >
                    <Mail className="w-5 h-5" />
                    Email me your details
                  </a>
                )}

                {showDropCallOption && (
                  <a
                    href={dropCallLink}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-semibold bg-surface dark:bg-surface-dark text-text-primary dark:text-text-primary-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors border border-border dark:border-border-dark"
                  >
                    <Phone className="w-5 h-5" />
                    Drop call me your number
                  </a>
                )}

                <p className="mt-1 text-[11px] text-text-muted dark:text-text-muted-dark text-center">
                  Only shared with me, never sold.
                </p>
              </div>
            )}
          </div>
        )}

        {links.length > 0 && (
          <div className="flex flex-col gap-3 mb-8">
            {links.map(link => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center p-4 rounded-input border transition-all active:scale-[0.99] hover:opacity-90"
                style={generalButtonStyle}
              >
                <div className="mr-4 p-2 rounded-container shadow-sm transition-transform link-icon-container" style={{ backgroundColor: generalButtonStyle.backgroundColor, color: generalButtonStyle.color }}>
                  <LinkGlyph link={link} inheritColor />
                </div>
                <span className="font-semibold text-sm flex-1">{sanitizeText(link.title || '')}</span>
                <ExternalLink className="w-4 h-4 opacity-50" />
              </a>
            ))}
          </div>
        )}

        <div className="grid grid-cols-4 gap-3 mb-8">
           <SocialIcon url={contact.website} icon={Globe} label="Web" buttonStyle={generalButtonStyle} />
           <SocialIcon url={social.linkedin} icon={Linkedin} label="LinkedIn" buttonStyle={generalButtonStyle} />
           <SocialIcon url={social.twitter} icon={Twitter} label="X" buttonStyle={generalButtonStyle} />
           <SocialIcon url={social.instagram} icon={Instagram} label="Insta" buttonStyle={generalButtonStyle} />
           <SocialIcon url={social.github} icon={Github} label="Git" buttonStyle={generalButtonStyle} />
        </div>

        {/* Swiish logo */}
        <div className="pb-4 text-center space-y-2 mt-auto lg:pb-4">
          <div className="flex justify-center">
            <img src={effectiveCardPreset === 'dark' ? "/graphics/Swiish_Logo_DarkBg.svg" : "/graphics/Swiish_Logo.svg"} alt="Swiish" className="h-4 w-auto swiish-logo" />
          </div>
        </div>
      </div>

      {showMapOptions && (
        <div
          className="fixed inset-0 z-[70] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowMapOptions(false)}
        >
          <div
            className="w-full max-w-sm bg-card dark:bg-card-dark rounded-container border border-border dark:border-border-dark shadow-2xl p-5 animate-in fade-in slide-in-from-bottom-3 duration-200"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-bold text-text-primary dark:text-text-primary-dark">Open location</h3>
                <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1 leading-relaxed">{locationText}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowMapOptions(false)}
                className="p-2 rounded-full text-text-muted dark:text-text-muted-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
                aria-label="Close maps menu"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid gap-2">
              <a
                href={appleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowMapOptions(false)}
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark hover:border-action dark:hover:border-action-dark transition-colors"
              >
                <span className="flex items-center gap-3 font-medium"><MapPin className="w-5 h-5" /> Apple Maps</span>
                <ExternalLink className="w-4 h-4 text-text-muted dark:text-text-muted-dark" />
              </a>
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowMapOptions(false)}
                className="flex items-center justify-between gap-3 px-4 py-3 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark hover:border-action dark:hover:border-action-dark transition-colors"
              >
                <span className="flex items-center gap-3 font-medium"><Globe className="w-5 h-5" /> Google Maps</span>
                <ExternalLink className="w-4 h-4 text-text-muted dark:text-text-muted-dark" />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortableLinkItem({ link, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: link.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return children({ setNodeRef, style, attributes, listeners });
}

function EditorView({ data, setData, onBack, onSave, slug, settings, csrfToken, showAlert, darkMode, toggleDarkMode, isSaving, isSuccess, onLogout }) {
  const [activeTab, setActiveTab] = useState('details');
  const [isUploading, setIsUploading] = useState(false);
  const organisationBanner = settings?.organisation_banner || {};
  const hasOrganisationBanner = !!organisationBanner.banner;
  const organisationBannerEnforced = hasOrganisationBanner && settings?.allow_banner_override === false;
  const inheritsOrganisationBanner = hasOrganisationBanner && !data.images?.banner;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );
  
  const handleInputChange = (section, field, value) => {
    setData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const uploadImageFile = async (file, purpose) => {
    const formData = new FormData();
    formData.append('file', file);
    if (purpose) formData.append('purpose', purpose);
    const res = await fetch(`${API_ENDPOINT}/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRF-Token': csrfToken
      },
      body: formData
    });
    if (!res.ok) throw new Error('Upload failed');
    const { url } = await res.json();
    return url;
  };

  const handleImageUpload = async (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadImageFile(file);
      setData(prev => ({
        ...prev,
        images: {
          ...prev.images,
          ...(type === 'banner'
            ? { banner: url, bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 }
            : type === 'bodyBackground'
              ? { bodyBackground: url, bodyBackgroundPositionX: 50, bodyBackgroundPositionY: 50, bodyBackgroundZoom: 1 }
              : { [type]: url })
        }
      }));
    } catch (error) {
      if (showAlert) showAlert('Upload failed', 'error');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleLinkIconUpload = async (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadImageFile(file, 'link-icon');
      updateLink(id, 'iconUrl', url);
    } catch (error) {
      if (showAlert) showAlert('Upload failed', 'error');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const addLink = () => {
    const newLink = { id: Date.now(), title: '', url: '', icon: 'link' };
    setData(prev => ({ ...prev, links: [...prev.links, newLink] }));
  };
  const removeLink = (id) => {
    setData(prev => ({ ...prev, links: prev.links.filter(l => l.id !== id) }));
  };
  const updateLink = (id, field, value) => {
    const patch = typeof field === 'object' ? field : { [field]: value };
    setData(prev => ({ ...prev, links: prev.links.map(l => l.id === id ? { ...l, ...patch } : l) }));
  };

  const reorderLinks = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;
    setData(prev => ({ ...prev, links: arrayMove(prev.links, oldIndex, newIndex) }));
  };

  const moveLinkUp = (index) => {
    if (index <= 0) return;
    reorderLinks(index, index - 1);
  };

  const moveLinkDown = (index) => {
    if (index >= data.links.length - 1) return;
    reorderLinks(index, index + 1);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = data.links.findIndex(link => link.id === active.id);
    const newIndex = data.links.findIndex(link => link.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    reorderLinks(oldIndex, newIndex);
  };

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 bg-card dark:bg-card-dark border-r border-border dark:border-border-dark h-auto lg:h-screen overflow-y-auto flex flex-col">
        <div className="p-4 sm:p-6 border-b border-border-subtle dark:border-border-dark flex items-center justify-between gap-2 bg-card dark:bg-card-dark sticky top-0 z-10">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
             <button onClick={onBack} aria-label="Back to cards" className="p-2 flex-shrink-0 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark"><ArrowLeft className="w-5 h-5"/></button>
             <div className="min-w-0">
               <h1 className="text-base sm:text-xl font-bold truncate text-text-primary dark:text-text-primary-dark">Editing: {slug}</h1>
             </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onLogout && (
              <button
                onClick={onLogout}
                className="px-2 sm:px-4 py-2 rounded-full text-sm font-medium text-text-muted dark:text-text-muted-dark bg-card dark:bg-card-dark border border-border dark:border-border-dark hover:bg-surface dark:hover:bg-surface-dark transition-colors"
              >
                Logout
              </button>
            )}
            <button
              onClick={onSave}
              disabled={isSaving}
              className="px-3 sm:px-5 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : isSuccess ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        </div>

        <div className="flex-1 p-6 space-y-8">
           <div className="flex p-1 bg-surface dark:bg-surface-dark rounded-input mb-6">
              {['details', 'links', 'images', 'style', 'sharing', 'privacy'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-2 text-sm font-medium rounded-button capitalize transition-all ${activeTab === tab ? 'bg-card dark:bg-surface-dark shadow text-text-primary dark:text-text-primary-dark' : 'text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark'}`}>{tab}</button>
              ))}
           </div>

           {activeTab === 'details' && (
             <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="First Name" value={data.personal.firstName} onChange={v => handleInputChange('personal', 'firstName', v)} />
                  <Input label="Last Name" value={data.personal.lastName} onChange={v => handleInputChange('personal', 'lastName', v)} />
                  <div className="md:col-span-2">
                    <Input label="Native Name" placeholder="e.g. 李啟明 / 山田太郎" value={data.personal.nativeName} onChange={v => handleInputChange('personal', 'nativeName', v)} />
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Optional native-script name, shown on your card and included in contact exports.</p>
                  </div>
                  <Input label="Job Title" value={data.personal.title} onChange={v => handleInputChange('personal', 'title', v)} />
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Organisation</label>
                    <div className="relative">
                      <input 
                        type="text" 
                        value={settings?.default_organisation || data.personal.company || ''} 
                        disabled
                        className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-muted-dark cursor-not-allowed" 
                        placeholder="Organisation Name"
                      />
                      <div className="absolute inset-0 flex items-center justify-end pr-3 pointer-events-none">
                        <Lock className="w-4 h-4 text-text-muted-subtle dark:text-text-muted-dark" />
                      </div>
                    </div>
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Organisation name is set by your organisation</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label htmlFor="card-location" className="block text-sm font-medium text-text-primary dark:text-text-secondary-dark">Location</label>
                  <select
                    id="card-location"
                    value={HIROSE_LOCATION_PRESETS.includes(data.personal.location) ? data.personal.location : '__other__'}
                    onChange={e => handleInputChange(
                      'personal', 'location',
                      e.target.value === '__other__' ? '' : e.target.value
                    )}
                    className="w-full min-w-0 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark"
                  >
                    <option value={HIROSE_OFFICE_ADDRESS}>{HIROSE_OFFICE_ADDRESS}</option>
                    <option value={HIROSE_FACTORY_ADDRESS}>{HIROSE_FACTORY_ADDRESS}</option>
                    <option value="__other__">Others</option>
                  </select>
                  {!HIROSE_LOCATION_PRESETS.includes(data.personal.location) && (
                    <Input
                      label="Other address"
                      placeholder="Enter your address"
                      value={data.personal.location || ''}
                      onChange={v => handleInputChange('personal', 'location', v)}
                    />
                  )}
                </div>
                <TextArea label="Bio" value={data.personal.bio} onChange={v => handleInputChange('personal', 'bio', v)} />
                <div className="h-px bg-surface dark:bg-surface-dark" />
                <div className="space-y-4">
                  <Input icon={Mail} placeholder="Email" value={data.contact.email} onChange={v => handleInputChange('contact', 'email', v)} type="email" />
                  <div className="space-y-1">
                    <PhoneInput
                      international
                      defaultCountry="GB"
                      value={data.contact.phone || ''}
                      onChange={(value) => handleInputChange('contact', 'phone', value || '')}
                      placeholder="Phone"
                      flags={flags}
                    />
                  </div>
                  <Input icon={Globe} placeholder="Website" value={data.contact.website} onChange={v => handleInputChange('contact', 'website', v)} type="url" />
                  <Input icon={Linkedin} placeholder="LinkedIn" value={data.social.linkedin} onChange={v => handleInputChange('social', 'linkedin', v)} type="url" />
                  <Input icon={Twitter} placeholder="Twitter / X" value={data.social.twitter} onChange={v => handleInputChange('social', 'twitter', v)} type="url" />
                  <Input icon={Instagram} placeholder="Instagram" value={data.social.instagram} onChange={v => handleInputChange('social', 'instagram', v)} type="url" />
                  <Input icon={Github} placeholder="Github" value={data.social.github} onChange={v => handleInputChange('social', 'github', v)} type="url" />
                </div>
             </div>
           )}

           {activeTab === 'links' && (
             <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Custom Links</h3>
                    {settings?.allow_links_customisation !== false ? (
                      <button onClick={addLink} className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1"><Plus className="w-4 h-4" /> Add Link</button>
                    ) : (
                      <div className="flex items-center gap-2 text-text-muted dark:text-text-muted-dark">
                        <Lock className="w-4 h-4" />
                        <span className="text-sm">Locked</span>
                      </div>
                    )}
                </div>
                {settings?.allow_links_customisation === false ? (
                  <LockedOption message="Your organisation has disabled custom links. Contact your administrator to enable this feature.">
                    <div className="space-y-4">
                      {data.links.map((link, index) => (
                        <div key={link.id} className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark">
                          <div className="grid gap-3">
                            <div className="flex gap-3 items-center">
                              <div className="w-10 h-10 rounded-container bg-card dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center shrink-0 overflow-hidden">
                                <LinkGlyph link={link} />
                              </div>
                              <input type="text" value={link.title} disabled className="flex-1 bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark rounded-input px-3 py-2 text-sm cursor-not-allowed" />
                            </div>
                            <input type="text" value={link.url} disabled className="w-full bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-muted dark:text-text-muted-dark rounded-input px-3 py-2 text-sm cursor-not-allowed" />
                          </div>
                        </div>
                      ))}
                      {data.links.length === 0 && (
                        <div className="text-center py-8 text-text-muted-subtle dark:text-text-muted-dark text-sm border-thick border-dashed border-border dark:border-border-dark rounded-input">
                          No custom links yet.
                        </div>
                      )}
                    </div>
                  </LockedOption>
                ) : (
                <div className="space-y-4">
                    {isUploading && <div className="text-sm text-indigo-600 dark:text-indigo-400 animate-pulse">Uploading image...</div>}
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={data.links.map(link => link.id)} strategy={verticalListSortingStrategy}>
                        {data.links.map((link, index) => (
                          <SortableLinkItem key={link.id} link={link}>
                            {({ setNodeRef, style, attributes, listeners }) => (
                              <div
                                ref={setNodeRef}
                                style={style}
                                className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark relative"
                              >
                                <div className="absolute top-2 left-2 flex items-center gap-2">
                                  <button
                                    type="button"
                                    className="p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action"
                                    {...attributes}
                                    {...listeners}
                                    aria-label="Drag to reorder"
                                  >
                                    <GripVertical className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="absolute top-2 right-10 flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() => moveLinkUp(index)}
                                    disabled={index === 0}
                                    className={`p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action disabled:opacity-40 disabled:cursor-not-allowed`}
                                    aria-label="Move link up"
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveLinkDown(index)}
                                    disabled={index === data.links.length - 1}
                                    className={`p-1 rounded-button border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-muted dark:text-text-muted-dark hover:border-action-dark hover:text-action-dark dark:hover:border-action dark:hover:text-action disabled:opacity-40 disabled:cursor-not-allowed`}
                                    aria-label="Move link down"
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </button>
                                </div>
                                <button onClick={() => removeLink(link.id)} className="absolute top-2 right-2 text-text-muted-subtle dark:text-text-muted-dark hover:text-error-text dark:hover:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark rounded-full p-1 transition-colors" aria-label="Remove link">
                                  <X className="w-4 h-4"/>
                                </button>
                                <div className="grid gap-3 pt-6">
                                  <div className="flex gap-3 items-center">
                                    <label
                                      htmlFor={`link-icon-upload-${link.id}`}
                                      className="w-10 h-10 rounded-container bg-card dark:bg-surface-dark border border-border dark:border-border-dark flex items-center justify-center shrink-0 overflow-hidden cursor-pointer hover:border-action dark:hover:border-action-dark"
                                      title="Upload custom icon"
                                    >
                                      <LinkGlyph link={link} />
                                    </label>
                                    <input 
                                      type="text" 
                                      placeholder="Link Title (e.g. Download CV)"
                                      value={link.title}
                                      onChange={(e) => updateLink(link.id, 'title', e.target.value)}
                                      className="flex-1 bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input px-3 py-2 text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                                    />
                                  </div>
                                  <input 
                                    type="text" 
                                    placeholder="https://..."
                                    value={link.url}
                                    onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                                    className="w-full bg-card dark:bg-surface-dark border border-border dark:border-border-dark text-text-primary dark:text-text-primary-dark rounded-input px-3 py-2 text-sm focus:outline-none focus:border-action dark:focus:border-action-dark"
                                  />
                                  <input
                                    id={`link-icon-upload-${link.id}`}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={(e) => handleLinkIconUpload(link.id, e)}
                                    className="hidden"
                                  />
                                  <div className="flex gap-2 overflow-x-auto pb-2 pt-1 no-scrollbar">
                                    <label
                                      htmlFor={`link-icon-upload-${link.id}`}
                                      className={`p-2 rounded-button border flex-shrink-0 cursor-pointer transition-all ${link.iconUrl ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-300' : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-muted-subtle dark:text-text-muted-dark hover:border-border dark:hover:border-border-dark'}`}
                                      title="Upload custom icon"
                                    >
                                      <Upload className="w-4 h-4" />
                                    </label>
                                    {Object.keys(ICON_MAP).map(iconKey => (
                                      <button 
                                        key={iconKey}
                                        type="button"
                                        onClick={() => updateLink(link.id, { icon: iconKey, iconUrl: '' })}
                                        className={`p-2 rounded-button border flex-shrink-0 transition-all ${!link.iconUrl && link.icon === iconKey ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400 text-indigo-600 dark:text-indigo-300' : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-muted-subtle dark:text-text-muted-dark hover:border-border dark:hover:border-border-dark'}`}
                                        title={iconKey}
                                      >
                                        {React.createElement(ICON_MAP[iconKey], { className: "w-4 h-4" })}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </SortableLinkItem>
                        ))}
                      </SortableContext>
                    </DndContext>
                    {data.links.length === 0 && (
                        <div className="text-center py-8 text-text-muted-subtle dark:text-text-muted-dark text-sm border-thick border-dashed border-border dark:border-border-dark rounded-input">
                            No custom links yet.
                        </div>
                    )}
                </div>
                )}
             </div>
           )}

           {activeTab === 'images' && (
              <div className="space-y-8">
                {settings?.allow_image_customisation === false ? (
                  <LockedOption message="Your organisation has disabled custom image uploads. Contact your administrator to enable this feature.">
                    <div className="space-y-8">
                      <ImageUpload label="Profile Picture" image={data.images.avatar} onUpload={() => {}} onRemove={() => {}} disabled={true} />
                      <BannerEditor label={hasOrganisationBanner ? 'Organisation Banner' : 'Header Banner'} images={hasOrganisationBanner ? organisationBanner : data.images} onUpload={() => {}} onChange={() => {}} onRemove={() => {}} disabled={true} />
                    </div>
                  </LockedOption>
                ) : (
                  <>
                    {isUploading && <div className="text-center text-sm text-indigo-600 dark:text-indigo-400 animate-pulse">Uploading image...</div>}
                    <ImageUpload label="Profile Picture" image={data.images.avatar} onUpload={e => handleImageUpload('avatar', e)} onRemove={() => handleInputChange('images', 'avatar', null)} />
                    {organisationBannerEnforced ? (
                      <LockedOption message="Your organisation banner is enforced. Your personal banner remains stored and will be available again if overrides are re-enabled.">
                        <BannerEditor label="Organisation Banner" images={organisationBanner} onUpload={() => {}} onChange={() => {}} onRemove={() => {}} disabled={true} />
                      </LockedOption>
                    ) : (
                      <>
                        {inheritsOrganisationBanner && (
                          <div className="rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark p-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <div>
                                <div className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">Using organisation banner</div>
                                <div className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Upload a custom banner below to override it for this card.</div>
                              </div>
                              <Lock className="w-4 h-4 text-text-muted dark:text-text-muted-dark flex-shrink-0" />
                            </div>
                            <div className="w-full aspect-[12/5] rounded-input overflow-hidden">
                              <BannerImage images={organisationBanner} alt="Organisation banner preview" />
                            </div>
                          </div>
                        )}
                        <BannerEditor
                          label={data.images?.banner ? 'Custom Header Banner' : 'Custom Header Banner (optional)'}
                          images={data.images}
                          onUpload={e => handleImageUpload('banner', e)}
                          onChange={(field, value) => handleInputChange('images', field, value)}
                          onRemove={() => setData(prev => ({ ...prev, images: { ...prev.images, banner: null, bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 } }))}
                        />
                        {hasOrganisationBanner && data.images?.banner && (
                          <p className="-mt-5 text-xs text-text-muted dark:text-text-muted-dark">Remove the custom banner to fall back to your organisation banner.</p>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
           )}

            {activeTab === 'style' && (
              <div className="space-y-8">
                <section>
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Card Preset</h3>
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Sets the card's own Light or Dark appearance independently from the visitor's browser theme.</p>
                  </div>
                  {settings?.allow_theme_customisation === false ? (
                    <LockedOption message="Your organisation has disabled theme customisation. The organisation card preset is enforced.">
                      <CardPresetControls value={normalizeCardAppearance(settings?.card_appearance).preset} onChange={() => {}} disabled={true} />
                    </LockedOption>
                  ) : (
                    <CardPresetControls
                      value={data.theme?.cardPreset || 'inherit'}
                      organisationPreset={normalizeCardAppearance(settings?.card_appearance).preset}
                      allowInherit={true}
                      onChange={(value) => handleInputChange('theme', 'cardPreset', value)}
                    />
                  )}
                </section>

                <div className="h-px bg-border-subtle dark:bg-border-dark" />

                <section>
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Accent Colour</h3>
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Controls the fallback header gradient and accent defaults such as Job Title and Save Contact.</p>
                  </div>
                  {settings?.allow_theme_customisation === false ? (
                    <LockedOption message="Your organisation has disabled theme customisation. Your card will use the organisation-approved appearance.">
                      <div className="flex flex-wrap gap-4">
                        {(settings?.theme_colors || []).map(color => (
                          <div
                            key={color.name}
                            className={`w-12 h-12 rounded-full relative ${settings?.theme_colors?.[0]?.name === color.name ? 'ring-4 ring-slate-200 scale-110' : ''}`}
                            style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                          >
                            {data.theme.color === color.name && <Check className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" />}
                          </div>
                        ))}
                      </div>
                    </LockedOption>
                  ) : (
                    <div className="flex flex-wrap gap-4">
                      {(settings?.theme_colors || []).map(color => (
                        <button
                          key={color.name}
                          onClick={() => handleInputChange('theme', 'color', color.name)}
                          className={`w-12 h-12 rounded-full relative hover:scale-110 transition-transform ${data.theme.color === color.name ? 'ring-4 ring-slate-200 scale-110' : ''}`}
                          style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                        >
                          {data.theme.color === color.name && <Check className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" />}
                        </button>
                      ))}
                    </div>
                  )}
                </section>

                <div className="h-px bg-border-subtle dark:bg-border-dark" />

                <section>
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Card Body Background</h3>
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Inherit your organisation default, use a solid colour, or frame your own background image.</p>
                  </div>

                  {settings?.allow_theme_customisation === false ? (
                    <LockedOption message="Your organisation has disabled theme customisation, including custom card backgrounds.">
                      <BodyBackgroundControls data={appearanceToEditorData(settings?.card_appearance)} onThemeChange={() => {}} onImageUpload={() => {}} onImageChange={() => {}} onImageRemove={() => {}} imageCustomisationAllowed={true} disabled={true} defaultLabel="Swiish Default" />
                    </LockedOption>
                  ) : (
                    <BodyBackgroundControls
                      data={data}
                      onThemeChange={(field, value) => handleInputChange('theme', field, value)}
                      onImageUpload={(e) => handleImageUpload('bodyBackground', e)}
                      onImageChange={(field, value) => handleInputChange('images', field, value)}
                      onImageRemove={() => setData(prev => ({ ...prev, images: { ...prev.images, bodyBackground: null, bodyBackgroundPositionX: 50, bodyBackgroundPositionY: 50, bodyBackgroundZoom: 1 } }))}
                      imageCustomisationAllowed={true}
                      defaultLabel="Organisation Default"
                    />
                  )}
                </section>

                <div className="h-px bg-border-subtle dark:bg-border-dark" />

                <section>
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Typography</h3>
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Fine-tune the visible card fields. Default always follows Swiish's current font size and theme/light-dark colour behaviour.</p>
                  </div>
                  {settings?.allow_theme_customisation === false ? (
                    <LockedOption message="Your organisation has disabled theme customisation, including typography overrides.">
                      <TypographyControls data={appearanceToEditorData(settings?.card_appearance)} onChange={() => {}} disabled={true} />
                    </LockedOption>
                  ) : (
                    <TypographyControls
                      data={data}
                      defaultLabel="Organisation / Card preset"
                      inheritedTypography={normalizeCardAppearance(settings?.card_appearance).typography}
                      onChange={(field, property, value) => {
                        setData(prev => {
                          const typography = { ...(prev.typography || {}) };
                          const nextField = { ...(typography[field] || {}) };
                          if (value === null || value === '' || value === undefined) delete nextField[property];
                          else nextField[property] = value;
                          if (Object.keys(nextField).length === 0) delete typography[field];
                          else typography[field] = nextField;
                          return { ...prev, typography };
                        });
                      }}
                    />
                  )}
                </section>

                <div className="h-px bg-border-subtle dark:bg-border-dark" />

                <section>
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Button Colours</h3>
                    <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">General controls share one palette; Save Contact and Send your details can each have their own. Default inherits organisation and card-preset styling.</p>
                  </div>
                  {settings?.allow_theme_customisation === false ? (
                    <LockedOption message="Your organisation has disabled theme customisation, including button colour overrides.">
                      <ButtonAppearanceControls styles={normalizeCardAppearance(settings?.card_appearance).buttonStyles} onChange={() => {}} disabled={true} defaultLabel="Swiish Default" />
                    </LockedOption>
                  ) : (
                    <ButtonAppearanceControls
                      styles={data.buttonStyles || {}}
                      defaultLabel="Organisation / Theme default"
                      onChange={(group, property, value) => {
                        setData(prev => {
                          const buttonStyles = { ...(prev.buttonStyles || {}) };
                          const nextGroup = { ...(buttonStyles[group] || {}) };
                          if (value === null || value === '' || value === undefined) delete nextGroup[property];
                          else nextGroup[property] = value;
                          if (Object.keys(nextGroup).length === 0) delete buttonStyles[group];
                          else buttonStyles[group] = nextGroup;
                          return { ...prev, buttonStyles };
                        });
                      }}
                    />
                  )}
                </section>
              </div>
            )}

            {activeTab === 'sharing' && (
              <div className="space-y-6">
                {settings?.allow_send_details_customisation === false ? (
                  <LockedOption message="Your organisation has disabled send-your-details customisation. This is controlled by your organisation.">
                    <SendDetailsToggles data={data} onChange={() => {}} />
                  </LockedOption>
                ) : (
                  <SendDetailsToggles
                    data={data}
                    onChange={(field, checked) => handleInputChange('sendDetails', field, checked)}
                  />
                )}
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="space-y-6">
                {settings?.allow_privacy_customisation === false ? (
                  <LockedOption message="Your organisation has disabled privacy settings customisation. Privacy settings are controlled by your organisation.">
                    <div className="space-y-6">
                      <Toggle
                        label="Require Interaction"
                        description="Requires users to click a button to reveal email and phone. Prevents basic bots from seeing contact info in the initial page load."
                        checked={data.privacy?.requireInteraction ?? true}
                        onChange={() => {}}
                      />
                      <div className="h-px bg-border-subtle" />
                      <Toggle
                        label="Client-Side Obfuscation"
                        description="Encodes email and phone in the HTML to make scraping harder. Note: Determined scrapers can still decode this."
                        checked={data.privacy?.clientSideObfuscation ?? false}
                        onChange={() => {}}
                      />
                      <div className="h-px bg-border-subtle" />
                      <Toggle
                        label="Block Search Engines"
                        description="Adds meta robots tag to prevent search engines from indexing this card."
                        checked={data.privacy?.blockRobots ?? false}
                        onChange={() => {}}
                      />
                    </div>
                  </LockedOption>
                ) : (
                  <div className="space-y-6">
                    <Toggle
                      label="Require Interaction"
                      description="Requires users to click a button to reveal email and phone. Prevents basic bots from seeing contact info in the initial page load."
                      checked={data.privacy?.requireInteraction ?? true}
                      onChange={(checked) => handleInputChange('privacy', 'requireInteraction', checked)}
                    />
                    <div className="h-px bg-border-subtle" />
                    <Toggle
                      label="Client-Side Obfuscation"
                      description="Encodes email and phone in the HTML to make scraping harder. Note: Determined scrapers can still decode this."
                      checked={data.privacy?.clientSideObfuscation ?? false}
                      onChange={(checked) => handleInputChange('privacy', 'clientSideObfuscation', checked)}
                    />
                    <div className="h-px bg-border-subtle" />
                    <Toggle
                      label="Block Search Engines"
                      description="Adds meta robots tag to prevent search engines from indexing this card."
                      checked={data.privacy?.blockRobots ?? false}
                      onChange={(checked) => handleInputChange('privacy', 'blockRobots', checked)}
                    />
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
      
      <div className="hidden lg:flex w-1/2 bg-border-subtle dark:bg-card-dark items-center justify-center p-10 relative">
          <div className="w-[375px] h-[750px] bg-card dark:bg-main-dark rounded-[3rem] shadow-2xl border-device border-text-primary dark:border-border-dark overflow-hidden relative">
            <CardDisplay data={data} settings={settings} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          </div>
      </div>
      <div className="fixed bottom-4 right-4 z-10 text-center group">
        <div className="flex justify-center">
          <img src="/graphics/Swiish_Logo.svg" alt="Swiish" className="h-4 w-auto dark:hidden swiish-logo" />
          <img src="/graphics/Swiish_Logo_DarkBg.svg" alt="Swiish" className="h-4 w-auto hidden dark:block swiish-logo" />
        </div>
      </div>
    </div>
  );
}

function Input({ label, icon: Icon, value, onChange, type = "text", placeholder }) {
  return (
    <div className="space-y-1">
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>}
      <div className="relative">
        {Icon && <Icon className="absolute left-4 top-3 w-5 h-5 text-text-muted-subtle dark:text-text-muted-dark" />}
        <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`w-full ${Icon ? 'pl-11' : 'px-4'} py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark placeholder:text-text-muted-subtle dark:placeholder:text-text-muted-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark`} />
      </div>
    </div>
  );
}

function LockedOption({ message, children }) {
  return (
    <div className="relative">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-surface/80 dark:bg-card-dark/80 rounded-container border-thick border-dashed border-border dark:border-border-dark">
        <div className="bg-input-bg dark:bg-input-bg-dark rounded-container p-4 border border-border dark:border-border-dark shadow-lg max-w-sm mx-4">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-text-muted dark:text-text-muted-dark" />
            <span className="text-sm font-semibold text-text-primary dark:text-text-secondary-dark">Locked by Organisation</span>
          </div>
          <p className="text-xs text-text-secondary dark:text-text-muted-dark">{message}</p>
        </div>
      </div>
    </div>
  );
}

function TextArea({ label, value, onChange }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark resize-none" />
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</label>
          {description && <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">{description}</p>}
        </div>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:ring-offset-2 ${
            checked ? 'bg-action dark:bg-action-dark' : 'bg-border dark:bg-surface-dark'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              checked ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function SendDetailsToggles({ data, onChange }) {
  const sendDetails = data.sendDetails || {};
  const masterEnabled = sendDetails.enabled ?? true;
  const hasPhone = !!(data.contact?.phone || '').trim();

  return (
    <div className="space-y-6">
      <Toggle
        label="Send your details"
        description="Shows a button on your public card that lets visitors send you their own contact details."
        checked={masterEnabled}
        onChange={(checked) => onChange('enabled', checked)}
      />
      <div className="h-px bg-border-subtle" />
      <div className={`space-y-6 ${masterEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
        <div>
          <Toggle
            label="WhatsApp"
            description="Lets visitors open WhatsApp with a pre-filled message to your number. Requires a WhatsApp-capable phone number."
            checked={sendDetails.whatsapp ?? true}
            onChange={(checked) => onChange('whatsapp', checked)}
          />
          {!hasPhone && (
            <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Add a phone number in Details to use this.</p>
          )}
        </div>
        <div className="h-px bg-border-subtle" />
        <Toggle
          label="Email"
          description="Lets visitors open their email client with a pre-filled message to your email address."
          checked={sendDetails.email ?? true}
          onChange={(checked) => onChange('email', checked)}
        />
        <div className="h-px bg-border-subtle" />
        <div>
          <Toggle
            label="Drop call"
            description="Lets visitors call your number so you have a missed-call record of their number."
            checked={sendDetails.call ?? true}
            onChange={(checked) => onChange('call', checked)}
          />
          {!hasPhone && (
            <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Add a phone number in Details to use this.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function clampBannerValue(value, min, max, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, numeric));
}

function BannerImage({ images = {}, alt = "Header banner", className = "" }) {
  const positionX = clampBannerValue(images.bannerPositionX, 0, 100, 50);
  const positionY = clampBannerValue(images.bannerPositionY, 0, 100, 50);
  const zoom = clampBannerValue(images.bannerZoom, 1, 2.5, 1);

  return (
    <img
      src={images.banner}
      alt={alt}
      draggable="false"
      className={`w-full h-full object-cover select-none pointer-events-none ${className}`}
      style={{
        objectPosition: `${positionX}% ${positionY}%`,
        transform: `scale(${zoom})`,
        transformOrigin: `${positionX}% ${positionY}%`
      }}
    />
  );
}

function BannerEditor({ label = 'Header Banner', images = {}, onUpload, onChange, onRemove, disabled = false }) {
  const dragRef = useRef(null);
  const positionX = clampBannerValue(images.bannerPositionX, 0, 100, 50);
  const positionY = clampBannerValue(images.bannerPositionY, 0, 100, 50);
  const zoom = clampBannerValue(images.bannerZoom, 1, 2.5, 1);
  const hasBanner = !!images.banner;

  const updatePosition = (x, y) => {
    onChange('bannerPositionX', Math.round(clampBannerValue(x, 0, 100, 50) * 10) / 10);
    onChange('bannerPositionY', Math.round(clampBannerValue(y, 0, 100, 50) * 10) / 10);
  };

  const handlePointerDown = (event) => {
    if (disabled || !hasBanner) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      startX: positionX,
      startY: positionY
    };
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dx = ((event.clientX - dragRef.current.clientX) / rect.width) * 100 / zoom;
    const dy = ((event.clientY - dragRef.current.clientY) / rect.height) * 100 / zoom;
    updatePosition(dragRef.current.startX - dx, dragRef.current.startY - dy);
  };

  const stopDragging = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  };

  const resetFraming = () => {
    onChange('bannerPositionX', 50);
    onChange('bannerPositionY', 50);
    onChange('bannerZoom', 1);
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">{label}</h3>
          <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Landscape around 2.3:1 works best. Keep important content away from the extreme edges.</p>
        </div>
        {hasBanner && !disabled && (
          <button type="button" onClick={resetFraming} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-xs font-medium text-text-secondary dark:text-text-secondary-dark hover:border-action dark:hover:border-action-dark hover:text-action dark:hover:text-action-dark transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Reset
          </button>
        )}
      </div>

      {hasBanner ? (
        <>
          <div
            className={`relative w-full aspect-[12/5] rounded-input overflow-hidden bg-surface dark:bg-surface-dark border border-border dark:border-border-dark shadow-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'} touch-none`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
          >
            <BannerImage images={images} alt="Header banner framing preview" />
            {!disabled && (
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center pb-3 pointer-events-none">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-white shadow-sm">
                  <Move className="w-3.5 h-3.5" /> Drag to reposition
                </div>
              </div>
            )}
          </div>

          {!disabled && (
            <div className="mt-4 rounded-input border border-border-subtle dark:border-border-dark bg-surface/60 dark:bg-surface-dark/60 p-4 space-y-4">
              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label htmlFor="banner-zoom" className="inline-flex items-center gap-2 text-sm font-medium text-text-primary dark:text-text-secondary-dark">
                    <ZoomIn className="w-4 h-4 text-text-muted dark:text-text-muted-dark" /> Zoom
                  </label>
                  <span className="text-xs font-mono text-text-muted dark:text-text-muted-dark">{zoom.toFixed(2)}×</span>
                </div>
                <input
                  id="banner-zoom"
                  type="range"
                  min="1"
                  max="2.5"
                  step="0.05"
                  value={zoom}
                  onChange={(e) => onChange('bannerZoom', Number(e.target.value))}
                  className="w-full accent-indigo-600 dark:accent-indigo-400"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-card dark:bg-card-dark border border-border dark:border-border-dark text-sm font-medium text-text-secondary dark:text-text-secondary-dark hover:border-action dark:hover:border-action-dark hover:text-action dark:hover:text-action-dark cursor-pointer transition-colors">
                  <Upload className="w-4 h-4" /> Replace image
                  <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
                </label>
                <button type="button" onClick={onRemove} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-error-border dark:border-error-border-dark text-sm font-medium text-error-text dark:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark transition-colors">
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
              </div>
              <p className="text-xs text-text-muted dark:text-text-muted-dark">Framing is non-destructive. The original upload is kept, and the card preview updates live as you drag or zoom.</p>
            </div>
          )}
        </>
      ) : (
        <label className={`relative w-full h-32 rounded-input bg-surface dark:bg-surface-dark border-thick border-dashed border-border dark:border-border-dark flex items-center justify-center overflow-hidden ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-action dark:hover:border-action-dark cursor-pointer'} transition-colors`}>
          <div className="text-center text-text-muted-subtle dark:text-text-muted-dark pointer-events-none">
            <Upload className="w-6 h-6 mx-auto mb-1" />
            <span className="text-xs font-medium">Upload banner image</span>
            <span className="block text-[11px] mt-1">Recommended 1600 × 700 px or larger</span>
          </div>
          <input type="file" accept="image/*" onChange={onUpload} disabled={disabled} className="absolute inset-0 opacity-0 cursor-pointer appearance-none bg-transparent focus:outline-none disabled:cursor-not-allowed" />
        </label>
      )}
    </section>
  );
}

function TypographyControls({ data, onChange, disabled = false, defaultLabel = 'Theme / app default', inheritedTypography = {} }) {
  const typography = data.typography || {};

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {TYPOGRAPHY_FIELDS.map(field => {
        const config = typography[field.key] || {};
        const customColor = /^#[0-9a-fA-F]{6}$/.test(config.color || '');
        const sizeValue = TYPOGRAPHY_SIZE_OPTIONS.includes(Number(config.size)) ? Number(config.size) : '';
        const inherited = inheritedTypography?.[field.key] || {};
        const inheritedSize = TYPOGRAPHY_SIZE_OPTIONS.includes(Number(inherited.size)) ? Number(inherited.size) : null;
        const inheritedColor = /^#[0-9a-fA-F]{6}$/.test(inherited.color || '') ? inherited.color : null;
        const defaultSizeLabel = inheritedSize ? `Default (Org: ${inheritedSize}px)` : `Default (${field.defaultSize}px)`;
        const defaultColorLabel = inheritedColor ? `Organisation ${inheritedColor}` : defaultLabel;

        return (
          <div key={field.key} className="rounded-container border border-border dark:border-border-dark bg-surface/60 dark:bg-surface-dark/60 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">{field.label}</div>
                <div className="text-[11px] text-text-muted dark:text-text-muted-dark mt-0.5">Default: {field.defaultHint}</div>
              </div>
              {(sizeValue || customColor) && !disabled && (
                <button
                  type="button"
                  onClick={() => { onChange(field.key, 'size', null); onChange(field.key, 'color', null); }}
                  className="text-[11px] font-medium text-action dark:text-action-dark hover:underline underline-offset-2"
                >
                  Reset
                </button>
              )}
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-3">
              <div>
                <label className="block text-[11px] font-medium text-text-muted dark:text-text-muted-dark mb-1.5">Font size</label>
                <select
                  value={sizeValue}
                  disabled={disabled}
                  onChange={(event) => onChange(field.key, 'size', event.target.value ? Number(event.target.value) : null)}
                  className="w-full px-3 py-2 rounded-input border border-border dark:border-border-dark bg-card dark:bg-card-dark text-sm text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark disabled:opacity-60"
                >
                  <option value="">{defaultSizeLabel}</option>
                  {TYPOGRAPHY_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size}px</option>)}
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-medium text-text-muted dark:text-text-muted-dark mb-1.5">Font colour</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(field.key, 'color', null)}
                    className={`flex-1 px-3 py-2 rounded-input border text-xs font-semibold transition-all ${!customColor ? 'border-action dark:border-action-dark bg-card dark:bg-card-dark text-action dark:text-action-dark shadow-sm' : 'border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-muted dark:text-text-muted-dark hover:border-action dark:hover:border-action-dark'} disabled:opacity-60`}
                  >
                    Default
                  </button>
                  <label className={`relative h-9 w-10 rounded-input border border-border dark:border-border-dark overflow-hidden bg-card dark:bg-card-dark ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-action dark:hover:border-action-dark'} transition-colors`} title="Choose custom colour">
                    <input
                      type="color"
                      disabled={disabled}
                      value={customColor ? config.color : '#4f46e5'}
                      onChange={(event) => onChange(field.key, 'color', event.target.value)}
                      className="absolute -inset-2 w-14 h-14 cursor-pointer disabled:cursor-not-allowed"
                    />
                  </label>
                </div>
                <div className="mt-1.5 text-[10px] font-mono text-text-muted-subtle dark:text-text-muted-dark uppercase truncate">{customColor ? config.color : defaultColorLabel}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function CardPresetControls({ value, onChange, disabled = false, allowInherit = false, organisationPreset = 'light' }) {
  const selected = ['light', 'dark'].includes(value) ? value : (allowInherit ? 'inherit' : 'light');
  const options = [
    ...(allowInherit ? [{ value: 'inherit', label: `Organisation Default (${organisationPreset === 'dark' ? 'Dark' : 'Light'})`, icon: Settings }] : []),
    { value: 'light', label: 'Light Card', icon: Sun },
    { value: 'dark', label: 'Dark Card', icon: Moon }
  ];
  return (
    <div className={`grid gap-2 ${allowInherit ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'}`}>
      {options.map(option => {
        const Icon = option.icon;
        const active = selected === option.value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`flex items-center justify-center gap-2 px-3 py-3 rounded-input border text-sm font-semibold transition-all ${active ? 'border-action dark:border-action-dark bg-card dark:bg-card-dark text-action dark:text-action-dark shadow-sm' : 'border-border dark:border-border-dark bg-surface/60 dark:bg-surface-dark/60 text-text-muted dark:text-text-muted-dark hover:border-action dark:hover:border-action-dark'} disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <Icon className="w-4 h-4" /> {option.label}
          </button>
        );
      })}
    </div>
  );
}

function ColourOverrideControl({ label, value, onChange, disabled = false, defaultLabel = 'Default' }) {
  const custom = /^#[0-9a-fA-F]{6}$/.test(value || '');
  return (
    <div>
      <label className="block text-[11px] font-medium text-text-muted dark:text-text-muted-dark mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(null)}
          className={`flex-1 px-3 py-2 rounded-input border text-xs font-semibold transition-all ${!custom ? 'border-action dark:border-action-dark bg-card dark:bg-card-dark text-action dark:text-action-dark shadow-sm' : 'border-border dark:border-border-dark bg-card dark:bg-card-dark text-text-muted dark:text-text-muted-dark hover:border-action dark:hover:border-action-dark'} disabled:opacity-60`}
        >
          Default
        </button>
        <label className={`relative h-9 w-10 rounded-input border border-border dark:border-border-dark overflow-hidden bg-card dark:bg-card-dark ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-action dark:hover:border-action-dark'} transition-colors`} title={`Choose custom ${label.toLowerCase()}`}>
          <input
            type="color"
            disabled={disabled}
            value={custom ? value : '#4f46e5'}
            onChange={(event) => onChange(event.target.value)}
            className="absolute -inset-2 w-14 h-14 cursor-pointer disabled:cursor-not-allowed"
          />
        </label>
      </div>
      <div className="mt-1.5 text-[10px] font-mono text-text-muted-subtle dark:text-text-muted-dark uppercase truncate">{custom ? value : defaultLabel}</div>
    </div>
  );
}

function ButtonAppearanceControls({ styles = {}, onChange, disabled = false, defaultLabel = 'Theme default' }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
      {CARD_BUTTON_GROUPS.map(group => {
        const config = styles?.[group.key] || {};
        const hasOverride = /^#[0-9a-fA-F]{6}$/.test(config.background || '') || /^#[0-9a-fA-F]{6}$/.test(config.color || '');
        return (
          <div key={group.key} className={`rounded-container border border-border dark:border-border-dark bg-surface/60 dark:bg-surface-dark/60 p-4 shadow-sm ${group.key === 'general' ? 'xl:col-span-2' : ''}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">{group.label}</div>
                <div className="text-[11px] text-text-muted dark:text-text-muted-dark mt-0.5">{group.description}</div>
              </div>
              {hasOverride && !disabled && (
                <button type="button" onClick={() => { onChange(group.key, 'background', null); onChange(group.key, 'color', null); }} className="text-[11px] font-medium text-action dark:text-action-dark hover:underline underline-offset-2">Reset</button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ColourOverrideControl label="Background colour" value={config.background} onChange={(value) => onChange(group.key, 'background', value)} disabled={disabled} defaultLabel={defaultLabel} />
              <ColourOverrideControl label="Font / icon colour" value={config.color} onChange={(value) => onChange(group.key, 'color', value)} disabled={disabled} defaultLabel={defaultLabel} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const appearanceToEditorData = (appearanceInput) => {
  const appearance = normalizeCardAppearance(appearanceInput);
  return {
    theme: {
      bodyBackgroundMode: appearance.bodyBackground.mode,
      bodyBackgroundColor: appearance.bodyBackground.color
    },
    images: {
      bodyBackground: appearance.bodyBackground.image,
      bodyBackgroundPositionX: appearance.bodyBackground.positionX,
      bodyBackgroundPositionY: appearance.bodyBackground.positionY,
      bodyBackgroundZoom: appearance.bodyBackground.zoom
    },
    typography: appearance.typography || {},
    buttonStyles: appearance.buttonStyles || {}
  };
};

function BodyBackgroundImage({ images = {}, alt = "Card body background", className = "" }) {
  const positionX = clampBannerValue(images.bodyBackgroundPositionX, 0, 100, 50);
  const positionY = clampBannerValue(images.bodyBackgroundPositionY, 0, 100, 50);
  const zoom = clampBannerValue(images.bodyBackgroundZoom, 1, 2.5, 1);

  return (
    <img
      src={images.bodyBackground}
      alt={alt}
      draggable="false"
      className={`w-full h-full object-cover select-none pointer-events-none ${className}`}
      style={{
        objectPosition: `${positionX}% ${positionY}%`,
        transform: `scale(${zoom})`,
        transformOrigin: `${positionX}% ${positionY}%`
      }}
    />
  );
}

function BodyBackgroundEditor({ images = {}, onUpload, onChange, onRemove, disabled = false }) {
  const dragRef = useRef(null);
  const positionX = clampBannerValue(images.bodyBackgroundPositionX, 0, 100, 50);
  const positionY = clampBannerValue(images.bodyBackgroundPositionY, 0, 100, 50);
  const zoom = clampBannerValue(images.bodyBackgroundZoom, 1, 2.5, 1);
  const hasImage = !!images.bodyBackground;

  const updatePosition = (x, y) => {
    onChange('bodyBackgroundPositionX', Math.round(clampBannerValue(x, 0, 100, 50) * 10) / 10);
    onChange('bodyBackgroundPositionY', Math.round(clampBannerValue(y, 0, 100, 50) * 10) / 10);
  };

  const handlePointerDown = (event) => {
    if (disabled || !hasImage) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      startX: positionX,
      startY: positionY
    };
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dx = ((event.clientX - dragRef.current.clientX) / rect.width) * 100 / zoom;
    const dy = ((event.clientY - dragRef.current.clientY) / rect.height) * 100 / zoom;
    updatePosition(dragRef.current.startX - dx, dragRef.current.startY - dy);
  };

  const stopDragging = (event) => {
    if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
  };

  const resetFraming = () => {
    onChange('bodyBackgroundPositionX', 50);
    onChange('bodyBackgroundPositionY', 50);
    onChange('bodyBackgroundZoom', 1);
  };

  if (!hasImage) {
    return (
      <label className={`relative w-full h-40 rounded-input bg-surface dark:bg-surface-dark border-thick border-dashed border-border dark:border-border-dark flex items-center justify-center overflow-hidden ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-action dark:hover:border-action-dark cursor-pointer'} transition-colors`}>
        <div className="text-center text-text-muted-subtle dark:text-text-muted-dark pointer-events-none px-4">
          <Upload className="w-6 h-6 mx-auto mb-1" />
          <span className="text-xs font-medium">Upload body background image</span>
          <span className="block text-[11px] mt-1">Large portrait or square images give the most framing flexibility</span>
        </div>
        <input type="file" accept="image/*" onChange={onUpload} disabled={disabled} className="absolute inset-0 opacity-0 cursor-pointer appearance-none bg-transparent focus:outline-none disabled:cursor-not-allowed" />
      </label>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Image framing</div>
          <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Drag to choose the focal area. The crop adapts as card height changes.</p>
        </div>
        {!disabled && (
          <button type="button" onClick={resetFraming} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-xs font-medium text-text-secondary dark:text-text-secondary-dark hover:border-action dark:hover:border-action-dark hover:text-action dark:hover:text-action-dark transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Reset
          </button>
        )}
      </div>

      <div
        className={`relative w-full aspect-[4/5] max-h-[380px] rounded-input overflow-hidden bg-surface dark:bg-surface-dark border border-border dark:border-border-dark shadow-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'} touch-none`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
      >
        <BodyBackgroundImage images={images} alt="Card body background framing preview" />
        {!disabled && (
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center pb-3 pointer-events-none">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-white shadow-sm">
              <Move className="w-3.5 h-3.5" /> Drag to reposition
            </div>
          </div>
        )}
      </div>

      {!disabled && (
        <div className="rounded-input border border-border-subtle dark:border-border-dark bg-surface/60 dark:bg-surface-dark/60 p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <label htmlFor="body-background-zoom" className="inline-flex items-center gap-2 text-sm font-medium text-text-primary dark:text-text-secondary-dark">
                <ZoomIn className="w-4 h-4 text-text-muted dark:text-text-muted-dark" /> Zoom
              </label>
              <span className="text-xs font-mono text-text-muted dark:text-text-muted-dark">{zoom.toFixed(2)}×</span>
            </div>
            <input
              id="body-background-zoom"
              type="range"
              min="1"
              max="2.5"
              step="0.05"
              value={zoom}
              onChange={(event) => onChange('bodyBackgroundZoom', Number(event.target.value))}
              className="w-full accent-indigo-600 dark:accent-indigo-400"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-card dark:bg-card-dark border border-border dark:border-border-dark text-sm font-medium text-text-secondary dark:text-text-secondary-dark hover:border-action dark:hover:border-action-dark hover:text-action dark:hover:text-action-dark cursor-pointer transition-colors">
              <Upload className="w-4 h-4" /> Replace image
              <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
            </label>
            <button type="button" onClick={onRemove} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-error-border dark:border-error-border-dark text-sm font-medium text-error-text dark:text-error-text-dark hover:bg-error-bg dark:hover:bg-error-bg-dark transition-colors">
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          </div>
          <p className="text-xs text-text-muted dark:text-text-muted-dark">Framing is non-destructive. The original upload is kept and the preview updates live.</p>
        </div>
      )}
    </div>
  );
}

function BodyBackgroundControls({ data, onThemeChange, onImageUpload, onImageChange, onImageRemove, imageCustomisationAllowed = true, disabled = false, defaultLabel = 'Theme Default' }) {
  const mode = ['theme', 'color', 'image'].includes(data.theme?.bodyBackgroundMode)
    ? data.theme.bodyBackgroundMode
    : 'theme';
  const color = /^#[0-9a-fA-F]{6}$/.test(data.theme?.bodyBackgroundColor || '')
    ? data.theme.bodyBackgroundColor
    : '#ffffff';

  const options = [
    { value: 'theme', label: defaultLabel },
    { value: 'color', label: 'Solid Colour' },
    { value: 'image', label: 'Image', unavailable: !imageCustomisationAllowed }
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-1 p-1 rounded-input bg-surface dark:bg-surface-dark border border-border-subtle dark:border-border-dark">
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            disabled={disabled || option.unavailable}
            onClick={() => onThemeChange('bodyBackgroundMode', option.value)}
            className={`px-3 py-2 rounded-button text-xs font-semibold transition-all ${
              mode === option.value
                ? 'bg-card dark:bg-card-dark text-text-primary dark:text-text-primary-dark shadow-sm'
                : 'text-text-muted dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {mode === 'theme' && (
        <div className="rounded-input border border-border-subtle dark:border-border-dark bg-surface/70 dark:bg-surface-dark/70 p-4 text-xs text-text-muted dark:text-text-muted-dark">
          Uses the normal Swiish card background and follows light/dark mode automatically.
        </div>
      )}

      {mode === 'color' && (
        <div className="rounded-input border border-border dark:border-border-dark bg-surface/60 dark:bg-surface-dark/60 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-text-primary dark:text-text-secondary-dark">Background colour</div>
              <div className="text-xs font-mono text-text-muted dark:text-text-muted-dark mt-1 uppercase">{color}</div>
            </div>
            <label className="relative w-14 h-14 rounded-container overflow-hidden border border-border dark:border-border-dark shadow-sm cursor-pointer" style={{ backgroundColor: color }}>
              <input
                type="color"
                value={color}
                disabled={disabled}
                onChange={(event) => onThemeChange('bodyBackgroundColor', event.target.value)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                aria-label="Choose card body background colour"
              />
            </label>
          </div>
          <p className="text-xs text-text-muted dark:text-text-muted-dark mt-3">This explicit colour overrides the card preset background. Use the live preview to check text contrast.</p>
        </div>
      )}

      {mode === 'image' && !imageCustomisationAllowed && (
        <div className="rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark p-4 text-xs text-text-muted dark:text-text-muted-dark flex items-start gap-2">
          <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" /> Your organisation has disabled custom image uploads. Choose Theme Default or Solid Colour instead.
        </div>
      )}

      {mode === 'image' && imageCustomisationAllowed && (
        <BodyBackgroundEditor
          images={data.images || {}}
          onUpload={onImageUpload}
          onChange={onImageChange}
          onRemove={onImageRemove}
          disabled={disabled}
        />
      )}
    </div>
  );
}

function ImageUpload({ label, image, onUpload, onRemove, isBanner, disabled = false }) {
  return (
    <section>
      <h3 className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-3">{label}</h3>
      <div className={`relative ${isBanner ? 'w-full h-32' : 'w-24 h-24'} rounded-input bg-surface dark:bg-surface-dark border-thick border-dashed border-border dark:border-border-dark flex items-center justify-center overflow-hidden group ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-border-dark dark:hover:border-border-dark'} transition-colors`}>
        {image ? <img src={image} className="w-full h-full object-cover" alt="upload" /> : <div className="text-center text-text-muted-subtle dark:text-text-muted-dark pointer-events-none"><Upload className="w-6 h-6 mx-auto mb-1" /><span className="text-xs">Upload</span></div>}
        <input type="file" accept="image/*" onChange={onUpload} disabled={disabled} className="absolute inset-0 opacity-0 cursor-pointer appearance-none bg-transparent focus:outline-none disabled:cursor-not-allowed" />
      </div>
      {image && !disabled && <button onClick={onRemove} className="mt-2 text-sm text-red-500 dark:text-red-400 font-medium hover:text-red-600 dark:hover:text-red-300">Remove</button>}
    </section>
  );
}

function SocialIcon({ url, icon: Icon, label, buttonStyle }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 group" style={{ color: buttonStyle?.color }}>
      <div
        className="w-full aspect-square rounded-card border flex items-center justify-center group-hover:scale-105 transition-all group-hover:shadow-md group-hover:opacity-90"
        style={{ backgroundColor: buttonStyle?.backgroundColor, borderColor: buttonStyle?.borderColor, color: buttonStyle?.color }}
      >
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[10px] uppercase font-bold tracking-wider" style={{ color: buttonStyle?.color }}>{label}</span>
    </a>
  );
}

// Color Selector Component
function ColorSelector({ selectedColor, onSelect, label, showAuto = false, autoLabel = "Auto (complementary)" }) {
  const colorGradients = {
    indigo: 'from-indigo-500 to-indigo-700',
    blue: 'from-blue-500 to-blue-700',
    rose: 'from-rose-500 to-rose-700',
    emerald: 'from-emerald-500 to-emerald-700',
    slate: 'from-slate-500 to-slate-700',
    purple: 'from-purple-500 to-purple-700',
    cyan: 'from-cyan-500 to-cyan-700',
    teal: 'from-teal-500 to-teal-700',
    orange: 'from-orange-500 to-orange-700',
    pink: 'from-pink-500 to-pink-700',
    violet: 'from-violet-500 to-violet-700',
    fuchsia: 'from-fuchsia-500 to-fuchsia-700',
    amber: 'from-amber-500 to-amber-700',
    lime: 'from-lime-500 to-lime-700',
    green: 'from-green-500 to-green-700',
    yellow: 'from-yellow-500 to-yellow-700',
    red: 'from-red-500 to-red-700'
  };

  return (
    <div>
      {label && <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">{label}</label>}
      <div className="grid grid-cols-8 gap-2">
        {showAuto && (
          <button
            onClick={() => onSelect(null)}
            className={`w-10 h-10 rounded-full border-thick flex flex-col items-center justify-center text-[10px] font-medium transition-all ${
              selectedColor === null 
                ? 'border-indigo-500 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-2 ring-indigo-200 dark:ring-indigo-800' 
                : 'border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark hover:border-border-dark dark:hover:border-border-dark'
            }`}
          >
            <span>Auto</span>
          </button>
        )}
        {TAILWIND_COLORS.map(color => (
          <button
            key={color}
            onClick={() => onSelect(color)}
            className={`w-10 h-10 rounded-full border-thick transition-all overflow-hidden ${
              selectedColor === color
                ? 'border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-200 dark:ring-indigo-800 scale-105'
                : 'border-border dark:border-border-dark hover:border-border-dark dark:hover:border-border-dark'
            }`}
            title={color.charAt(0).toUpperCase() + color.slice(1)}
          >
            <div className={`w-full h-full bg-gradient-to-br ${colorGradients[color] || 'from-gray-500 to-gray-700'}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

function UserManagementView({ apiCall, userRole, onBack, showAlert, showConfirm }) {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'member' });
  const [newInvitation, setNewInvitation] = useState({ email: '', role: 'member' });
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
    fetchInvitations();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/auth/me`);
      if (res.ok) {
        const userData = await res.json();
        setCurrentUserId(userData.id);
      }
    } catch (e) {
      console.error('Failed to fetch current user:', e);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        if (showAlert) showAlert('Failed to load users', 'error');
      }
    } catch (e) {
      console.error('Failed to fetch users:', e);
      if (showAlert) showAlert('Error loading users', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`);
      if (res.ok) {
        const data = await res.json();
        setInvitations(data.invitations);
      }
    } catch (e) {
      console.error('Failed to fetch invitations:', e);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      if (showAlert) showAlert('Email and password are required', 'error');
      return;
    }

    if (newUser.password.length < 8) {
      if (showAlert) showAlert('Password must be at least 8 characters long', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users`, {
        method: 'POST',
        body: JSON.stringify(newUser)
      });
      
      if (res.ok) {
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        setShowCreateUserModal(false);
        setNewUser({ email: '', password: '', role: 'member' });
        fetchUsers();
      } else {
        // Try to parse error response
        let errorMessage = 'Failed to create user';
        try {
          const errorData = await res.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('User creation failed:', {
            status: res.status,
            statusText: res.statusText,
            error: errorData
          });
        } catch (parseError) {
          // Response is not JSON, use status text
          console.error('User creation failed - non-JSON response:', {
            status: res.status,
            statusText: res.statusText
          });
          errorMessage = `Failed to create user: ${res.status} ${res.statusText}`;
        }
        if (showAlert) showAlert(errorMessage, 'error');
      }
    } catch (e) {
      console.error('Error creating user:', e);
      const errorMessage = e.message || 'Error creating user. Please try again.';
      if (showAlert) showAlert(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!newInvitation.email) {
      if (showAlert) showAlert('Email is required', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations`, {
        method: 'POST',
        body: JSON.stringify(newInvitation)
      });
      if (res.ok) {
        const data = await res.json();
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        setShowInviteModal(false);
        setNewInvitation({ email: '', role: 'member' });

        // Show warning if email failed
        if (data.status === 'failed' && showAlert) {
          showAlert('Invitation created but email failed to send. You can retry from the invitations list below.', 'warning');
        }

        // Reload invitations list
        fetchInvitations();
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Failed to send invitation', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Error sending invitation', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        if (showAlert) showAlert('User role updated successfully', 'success');
        fetchUsers();
        setEditingUserId(null);
      } else {
        const errorData = await res.json().catch(() => ({}));
        if (showAlert) showAlert(errorData.error || 'Failed to update role', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Error updating role', 'error');
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (showConfirm) {
      showConfirm(
        `Are you sure you want to permanently delete ${userEmail}? This will delete the user and all their cards. This action cannot be undone.`,
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/users/${userId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert('User deleted successfully', 'success');
              fetchUsers();
            } else {
              const errorData = await res.json().catch(() => ({}));
              if (showAlert) showAlert(errorData.error || 'Failed to delete user', 'error');
            }
          } catch (e) {
            if (showAlert) showAlert('Error deleting user', 'error');
          }
        },
        'Delete User',
        'Delete',
        'Cancel'
      );
    }
  };

  const handleRetryInvitation = async (invitationId) => {
    try {
      const res = await apiCall(`${API_ENDPOINT}/admin/invitations/${invitationId}/retry`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        if (showAlert) {
          showAlert(data.success ? 'Invitation email sent successfully' : 'Failed to send invitation email',
                    data.success ? 'success' : 'error');
        }
        fetchInvitations();
      }
    } catch (e) {
      if (showAlert) showAlert('Error retrying invitation', 'error');
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (showConfirm) {
      showConfirm(
        'Are you sure you want to delete this invitation?',
        async () => {
          try {
            const res = await apiCall(`${API_ENDPOINT}/admin/invitations/${invitationId}`, {
              method: 'DELETE'
            });
            if (res.ok) {
              if (showAlert) showAlert('Invitation deleted', 'success');
              fetchInvitations();
            }
          } catch (e) {
            if (showAlert) showAlert('Error deleting invitation', 'error');
          }
        },
        'Delete Invitation',
        'Delete',
        'Cancel'
      );
    }
  };

  if (userRole !== 'owner') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark flex items-center justify-center p-6">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-4">Access Denied</h2>
          <p className="text-text-secondary dark:text-text-muted-dark mb-6">You don't have permission to manage users.</p>
          <button onClick={onBack} className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col">
      <div className="w-full bg-card dark:bg-card-dark border-b border-border dark:border-border-dark">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
              <ArrowLeft className="w-5 h-5"/>
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">User Management</h1>
              <p className="text-sm text-text-secondary dark:text-text-muted-dark">Manage users and invitations for your organisation</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 bg-action dark:bg-action-dark text-white rounded-full text-sm font-bold flex items-center gap-2 hover:bg-action-hover dark:hover:bg-action-hover-dark"
            >
              <Plus className="w-4 h-4" /> Invite User
            </button>
            <button
              onClick={() => setShowCreateUserModal(true)}
              className="px-4 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark"
            >
              <Plus className="w-4 h-4" /> Create User
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 max-w-6xl mx-auto w-full">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-text-secondary dark:text-text-muted-dark">Loading users...</div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
              <div className="p-6 border-b border-border dark:border-border-dark">
                <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">Organisation Users</h2>
                <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Users currently in your organisation</p>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {users.length === 0 ? (
                  <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                    No users found
                  </div>
                ) : (
                  users.map((user) => {
                    const isCurrentUser = user.id === currentUserId;
                    return (
                      <div key={user.id} className="p-6 flex items-center justify-between hover:bg-surface dark:hover:bg-surface-dark/50 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                              <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                              <div className="font-medium text-text-primary dark:text-text-primary-dark flex items-center gap-2">
                                {user.email}
                                {isCurrentUser && (
                                  <span className="text-xs text-text-muted dark:text-text-muted-dark">(You)</span>
                                )}
                              </div>
                              <div className="text-sm text-text-muted dark:text-text-muted-dark">
                                {user.role === 'owner' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                                    Owner
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark">
                                    Member
                                  </span>
                                )}
                                <span className="ml-2">Joined {new Date(user.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {isCurrentUser ? (
                            <span className="text-xs text-text-muted dark:text-text-muted-dark italic">Cannot modify yourself</span>
                          ) : editingUserId === user.id ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={user.role}
                                onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                                className="px-3 py-1.5 text-sm rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark"
                              >
                                <option value="member">Member</option>
                                <option value="owner">Owner</option>
                              </select>
                              <button
                                onClick={() => setEditingUserId(null)}
                                className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingUserId(user.id)}
                                className="px-3 py-1.5 text-sm bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1"
                              >
                                <Edit3 className="w-3 h-3" /> Change Role
                              </button>
                              <button
                                onClick={() => handleRemoveUser(user.id, user.email)}
                                className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" /> Remove
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pending Invitations Section */}
            <div className="bg-card dark:bg-card-dark rounded-input shadow-sm border border-border dark:border-border-dark overflow-hidden">
              <div className="p-6 border-b border-border dark:border-border-dark">
                <h2 className="text-lg font-semibold text-text-primary dark:text-text-primary-dark">Pending Invitations</h2>
                <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Invitations sent but not yet accepted</p>
              </div>
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {invitations.filter(inv => inv.status !== 'accepted' && !inv.accepted_at).length === 0 ? (
                  <div className="p-6 text-center text-text-muted dark:text-text-muted-dark">
                    No pending invitations
                  </div>
                ) : (
                  invitations.filter(inv => inv.status !== 'accepted' && !inv.accepted_at).map(inv => (
                    <div key={inv.id} className="p-6 flex items-center justify-between hover:bg-surface dark:hover:bg-surface-dark/50 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <div className="font-medium text-text-primary dark:text-text-primary-dark">
                              {inv.email}
                            </div>
                            <div className="text-sm text-text-muted dark:text-text-muted-dark flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium ${
                                inv.status === 'sent' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                                inv.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                                inv.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                inv.status === 'expired' ? 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300' :
                                'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              }`}>
                                {inv.status === 'sent' && 'Sent'}
                                {inv.status === 'failed' && 'Failed'}
                                {inv.status === 'pending' && 'Pending'}
                                {inv.status === 'expired' && 'Expired'}
                              </span>
                              {inv.role === 'owner' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                                  Owner
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge text-xs font-medium bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark">
                                  Member
                                </span>
                              )}
                              {inv.invited_by_email && (
                                <span className="text-xs">invited by {inv.invited_by_email}</span>
                              )}
                              <span className="text-xs">• {new Date(inv.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(inv.status === 'failed' || inv.status === 'pending') && (
                          <button
                            onClick={() => handleRetryInvitation(inv.id)}
                            className="px-3 py-1.5 text-sm bg-action dark:bg-action-dark text-white rounded-button hover:bg-action-hover dark:hover:bg-action-hover-dark flex items-center gap-1"
                            title="Retry sending email"
                          >
                            <RefreshCw className="w-3 h-3" /> Retry
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteInvitation(inv.id)}
                          className="px-3 py-1.5 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark flex items-center gap-1"
                          title="Delete invitation"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Create New User</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder="Minimum 8 characters"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                >
                  <option value="member">Member</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateUserModal(false);
                  setNewUser({ email: '', password: '', role: 'member' });
                }}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={isSaving}
                className="flex-1 px-4 py-2.5 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-button font-bold hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : isSuccess ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card dark:bg-card-dark rounded-card shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Invite User</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Email</label>
                <input
                  type="email"
                  value={newInvitation.email}
                  onChange={(e) => setNewInvitation({ ...newInvitation, email: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                  placeholder="user@example.com"
                />
                <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">An invitation email will be sent to this address</p>
              </div>
              <div>
                <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Role</label>
                <select
                  value={newInvitation.role}
                  onChange={(e) => setNewInvitation({ ...newInvitation, role: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                >
                  <option value="member">Member</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setNewInvitation({ email: '', role: 'member' });
                }}
                className="flex-1 px-4 py-2.5 bg-surface dark:bg-surface-dark text-text-primary dark:text-text-secondary-dark rounded-button font-medium hover:bg-surface dark:hover:bg-surface-dark"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvitation}
                disabled={isSaving || isSuccess}
                className={`flex-1 px-4 py-2.5 rounded-button font-bold flex items-center justify-center gap-2 transition-all ${
                  isSuccess
                    ? 'bg-green-500 dark:bg-green-600 text-white hover:bg-green-600 dark:hover:bg-green-700'
                    : 'bg-action dark:bg-action-dark text-white hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50'
                }`}
              >
                {isSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : isSuccess ? (
                  <>
                    <Check className="w-4 h-4" />
                    Sent!
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvitationAcceptance({ apiCall, showAlert, API_ENDPOINT }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  // Fetch invitation details on mount
  useEffect(() => {
    // Don't fetch if invitation has already been accepted
    if (hasAccepted) return;

    const fetchInvitation = async () => {
      try {
        const res = await apiCall(`${API_ENDPOINT}/invitations/${token}`, { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          setInvitation(data);
          setError(null);
        } else {
          const errorData = await res.json().catch(() => ({}));
          // If already accepted, treat as success and redirect
          if (errorData.error && errorData.error.includes('already been accepted')) {
            if (showAlert) showAlert('Welcome! Your account has been created.', 'success');
            setTimeout(() => navigate('/'), 500);
          } else {
            setError(errorData.error || 'Invitation not found or has expired');
            setInvitation(null);
          }
        }
      } catch (e) {
        setError('Error loading invitation');
        setInvitation(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchInvitation();
    }
  }, [token, apiCall, API_ENDPOINT, hasAccepted, showAlert, navigate]);

  const validatePasswords = () => {
    setPasswordError(null);

    if (!password) {
      setPasswordError('Password is required');
      return false;
    }

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleAcceptInvitation = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await apiCall(`${API_ENDPOINT}/invitations/${token}/accept`, {
        method: 'POST',
        body: JSON.stringify({ password })
      });

      if (res.ok) {
        const data = await res.json();
        // Set flag immediately to prevent re-fetching during redirect window
        setHasAccepted(true);
        if (showAlert) showAlert('Welcome! Your account has been created.', 'success');
        // Redirect to dashboard
        setTimeout(() => navigate('/'), 1000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setPasswordError(errorData.error || 'Failed to accept invitation');
      }
    } catch (e) {
      setPasswordError('Error accepting invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-action dark:text-action-dark mx-auto mb-4" />
          <p className="text-text-primary dark:text-text-primary-dark">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark p-4">
        <div className="max-w-md w-full bg-card dark:bg-card-dark rounded-card shadow-lg p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark mb-2">Invitation Invalid</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">{error || 'This invitation link is not valid or has expired.'}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-medium hover:bg-action-hover dark:hover:bg-action-hover-dark"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-bg dark:bg-bg-dark p-4">
      <div className="max-w-md w-full">
        <div className="bg-card dark:bg-card-dark rounded-card shadow-lg p-8 mb-4">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">Join {invitation.organisationName}</h1>
          <p className="text-text-secondary dark:text-text-secondary-dark mb-6">
            You've been invited to join as a <span className="font-semibold capitalize">{invitation.role}</span>
          </p>

          <form onSubmit={handleAcceptInvitation} className="space-y-4">
            {/* Email display */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">Email</label>
              <input
                type="email"
                value={invitation.email}
                disabled
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text-secondary dark:text-text-secondary-dark"
              />
            </div>

            {/* Password input */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder="Enter password (min 8 characters)"
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              />
            </div>

            {/* Confirm password input */}
            <div>
              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark block mb-2">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError(null);
                }}
                placeholder="Confirm password"
                className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
              />
            </div>

            {/* Error message */}
            {passwordError && (
              <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-input text-red-700 dark:text-red-200 text-sm">
                {passwordError}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-2.5 bg-action dark:bg-action-dark text-white rounded-button font-bold hover:bg-action-hover dark:hover:bg-action-hover-dark disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Accept Invitation
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-text-muted dark:text-text-muted-dark text-center mt-4">
            By accepting this invitation, you agree to join the organization
          </p>
        </div>
      </div>
    </div>
  );
}

function SettingsView({ settings, setSettings, onBack, onSave, apiCall, showAlert, showConfirm }) {
  // Initialize local settings with extracted base colors from existing data
  const initializeColorData = (colors) => {
    return colors.map(color => {
      let hexBase, baseColor, colorType;
      
      // Determine if this is a standard color (has Tailwind gradient or baseColor) or custom hex
      const hasValidTailwindGradient = color.gradient && typeof color.gradient === 'string' && color.gradient.startsWith('from-');
      const hasHexBase = color.hexBase && typeof color.hexBase === 'string' && color.hexBase.startsWith('#');
      
      if (hasValidTailwindGradient) {
        // Convert from Tailwind gradient to hex
        const extracted = extractBaseColorFromGradient(color.gradient);
        if (extracted) {
          baseColor = extracted.baseColor;
          hexBase = getTailwindColorHex(extracted.baseColor, 600); // Always use shade 600
          colorType = 'standard';
        } else {
          // Fallback if extraction fails
          baseColor = color.baseColor || 'indigo';
          hexBase = hasHexBase ? color.hexBase : getTailwindColorHex(baseColor, 600);
          colorType = color.colorType === 'custom' ? 'custom' : 'standard';
        }
      } else if (hasHexBase) {
        // Has hexBase - determine if standard or custom
        if (color.baseColor && color.colorType !== 'custom') {
          // Standard color with hexBase
          baseColor = color.baseColor;
          hexBase = color.hexBase;
          colorType = 'standard';
        } else {
          // Custom hex color
          baseColor = null;
          hexBase = color.hexBase;
          colorType = 'custom';
        }
      } else if (color.gradientStyle) {
        // Has gradientStyle but no hexBase - extract from gradientStyle or use default
        baseColor = null;
        hexBase = '#4f46e5'; // Default
        colorType = 'custom';
      } else {
        // Fallback - treat as standard with default
        baseColor = color.baseColor || 'indigo';
        hexBase = getTailwindColorHex(baseColor, 600);
        colorType = color.colorType === 'custom' ? 'custom' : 'standard';
      }
      
      // Always auto-generate complementary secondary color
      const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
      const hexSecondary = color.hexSecondary || (complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase);
      
      // Generate all inline styles
      const gradientStyle = `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`;
      const buttonStyle = hexBase;
      const linkStyle = hexBase;
      const textStyle = hexBase;
      
      // Build clean hex-only color object
      return {
        name: color.name,
        colorType: colorType,
        baseColor: baseColor, // null for custom, Tailwind name for standard
        hexBase: hexBase,
        hexSecondary: hexSecondary,
        gradientStyle: gradientStyle,
        buttonStyle: buttonStyle,
        linkStyle: linkStyle,
        textStyle: textStyle
      };
    });
  };

  const [localSettings, setLocalSettings] = useState({
    ...settings,
    theme_colors: initializeColorData(settings.theme_colors || []),
    theme_variant: settings.theme_variant || 'default',
    // Initialize override toggles - default to true if undefined, but preserve false
    allow_theme_customisation: settings.allow_theme_customisation !== undefined ? Boolean(settings.allow_theme_customisation) : true,
    allow_image_customisation: settings.allow_image_customisation !== undefined ? Boolean(settings.allow_image_customisation) : true,
    organisation_banner: settings.organisation_banner || { banner: '', bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 },
    card_appearance: normalizeCardAppearance(settings.card_appearance),
    allow_banner_override: settings.allow_banner_override !== undefined ? Boolean(settings.allow_banner_override) : true,
    allow_links_customisation: settings.allow_links_customisation !== undefined ? Boolean(settings.allow_links_customisation) : true,
    allow_privacy_customisation: settings.allow_privacy_customisation !== undefined ? Boolean(settings.allow_privacy_customisation) : true,
    allow_send_details_customisation: settings.allow_send_details_customisation !== undefined ? Boolean(settings.allow_send_details_customisation) : true
  });
  const [editingColorIndex, setEditingColorIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isOrganisationNameOpen, setIsOrganisationNameOpen] = useState(false);
  const [isOrganisationBannerOpen, setIsOrganisationBannerOpen] = useState(false);
  const [isUploadingOrganisationBanner, setIsUploadingOrganisationBanner] = useState(false);
  const [isCardAppearanceOpen, setIsCardAppearanceOpen] = useState(false);
  const [isUploadingCardBackground, setIsUploadingCardBackground] = useState(false);
  const [isCustomizationControlsOpen, setIsCustomizationControlsOpen] = useState(false);
  const [isThemeColorsOpen, setIsThemeColorsOpen] = useState(false);
  const [isAppThemesOpen, setIsAppThemesOpen] = useState(false);

  const applyThemePreset = (variant) => {
    setEditingColorIndex(null);
    const preset = THEME_PRESETS?.[variant];
    setLocalSettings(prev => ({
      ...prev,
      theme_variant: preset ? variant : 'custom',
      theme_colors: preset ? initializeColorData(preset) : prev.theme_colors
    }));
  };

  // Re-initialize when settings prop changes
  useEffect(() => {
    setLocalSettings({
      ...settings,
      theme_colors: initializeColorData(settings.theme_colors || []),
      theme_variant: settings.theme_variant || localSettings.theme_variant || 'swiish',
      // Initialize override toggles - default to true if undefined, but preserve false
      allow_theme_customisation: settings.allow_theme_customisation !== undefined ? Boolean(settings.allow_theme_customisation) : true,
      allow_image_customisation: settings.allow_image_customisation !== undefined ? Boolean(settings.allow_image_customisation) : true,
      organisation_banner: settings.organisation_banner || { banner: '', bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 },
      card_appearance: normalizeCardAppearance(settings.card_appearance),
      allow_banner_override: settings.allow_banner_override !== undefined ? Boolean(settings.allow_banner_override) : true,
      allow_links_customisation: settings.allow_links_customisation !== undefined ? Boolean(settings.allow_links_customisation) : true,
      allow_privacy_customisation: settings.allow_privacy_customisation !== undefined ? Boolean(settings.allow_privacy_customisation) : true,
      allow_send_details_customisation: settings.allow_send_details_customisation !== undefined ? Boolean(settings.allow_send_details_customisation) : true
    });
  }, [settings]);

  // Apply app theme variant class to body for CSS overrides (swiish|minimal|custom)
  useEffect(() => {
    const variant = localSettings.theme_variant || settings.theme_variant || 'swiish';
    document.body.classList.remove('theme-swiish', 'theme-minimal', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant);
  }, [localSettings.theme_variant, settings.theme_variant]);

  const handleSave = async () => {
    setIsSaving(true);
    const startTime = Date.now();
    try {
      // Save hex-only color structure
      const colorsToSave = localSettings.theme_colors.map(color => {
        return {
          name: color.name,
          colorType: color.colorType || 'standard',
          baseColor: color.baseColor || null, // null for custom, Tailwind name for standard
          hexBase: color.hexBase || '#4f46e5',
          hexSecondary: color.hexSecondary || color.hexBase || '#4f46e5',
          gradientStyle: color.gradientStyle || `linear-gradient(135deg, ${color.hexBase || '#4f46e5'}, ${color.hexSecondary || color.hexBase || '#4f46e5'})`,
          buttonStyle: color.buttonStyle || color.hexBase || '#4f46e5',
          linkStyle: color.linkStyle || color.hexBase || '#4f46e5',
          textStyle: color.textStyle || color.hexBase || '#4f46e5'
        };
      });

      const res = await apiCall(`${API_ENDPOINT}/admin/settings`, {
        method: 'POST',
        body: JSON.stringify({
          default_organisation: localSettings.default_organisation,
          theme_colors: colorsToSave,
          theme_variant: localSettings.theme_variant || 'swiish',
          allow_theme_customisation: Boolean(localSettings.allow_theme_customisation),
          card_appearance: normalizeCardAppearance(localSettings.card_appearance),
          allow_image_customisation: Boolean(localSettings.allow_image_customisation),
          organisation_banner: localSettings.organisation_banner || { banner: '', bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 },
          allow_banner_override: Boolean(localSettings.allow_banner_override),
          allow_links_customisation: Boolean(localSettings.allow_links_customisation),
          allow_privacy_customisation: Boolean(localSettings.allow_privacy_customisation),
          allow_send_details_customisation: Boolean(localSettings.allow_send_details_customisation)
        })
      });

      // Ensure at least 500ms has passed
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 500) {
        await new Promise(resolve => setTimeout(resolve, 500 - elapsedTime));
      }

      if (res.ok) {
        // Refetch from server to get the latest saved data
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 2000);
        await onSave();
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Save failed:', errorData);
        if (showAlert) showAlert('Failed to save settings', 'error');
      }
    } catch (e) {
      if (showAlert) showAlert('Error saving settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOrganisationBannerUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploadingOrganisationBanner(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiCall(`${API_ENDPOINT}/upload`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      setLocalSettings(prev => ({
        ...prev,
        organisation_banner: { banner: url, bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 }
      }));
    } catch (error) {
      if (showAlert) showAlert('Organisation banner upload failed', 'error');
    } finally {
      setIsUploadingOrganisationBanner(false);
      event.target.value = '';
    }
  };

  const updateOrganisationBanner = (field, value) => {
    setLocalSettings(prev => ({
      ...prev,
      organisation_banner: { ...(prev.organisation_banner || {}), [field]: value }
    }));
  };


  const updateCardAppearance = (updater) => {
    setLocalSettings(prev => {
      const current = normalizeCardAppearance(prev.card_appearance);
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, card_appearance: normalizeCardAppearance(next) };
    });
  };

  const handleCardBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploadingCardBackground(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiCall(`${API_ENDPOINT}/upload`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();
      updateCardAppearance(current => ({
        ...current,
        bodyBackground: { ...current.bodyBackground, mode: 'image', image: url, positionX: 50, positionY: 50, zoom: 1 }
      }));
    } catch (error) {
      if (showAlert) showAlert('Organisation card background upload failed', 'error');
    } finally {
      setIsUploadingCardBackground(false);
      event.target.value = '';
    }
  };

  const updateOrganisationTypography = (field, property, value) => {
    updateCardAppearance(current => {
      const typography = { ...(current.typography || {}) };
      const nextField = { ...(typography[field] || {}) };
      if (value === null || value === '' || value === undefined) delete nextField[property];
      else nextField[property] = value;
      if (Object.keys(nextField).length === 0) delete typography[field];
      else typography[field] = nextField;
      return { ...current, typography };
    });
  };

  const updateOrganisationButtonStyle = (group, property, value) => {
    updateCardAppearance(current => {
      const buttonStyles = { ...(current.buttonStyles || {}) };
      const nextGroup = { ...(buttonStyles[group] || {}) };
      if (value === null || value === '' || value === undefined) delete nextGroup[property];
      else nextGroup[property] = value;
      if (Object.keys(nextGroup).length === 0) delete buttonStyles[group];
      else buttonStyles[group] = nextGroup;
      return { ...current, buttonStyles };
    });
  };

  const addColor = () => {
    const baseColor = 'indigo';
    const hexBase = getTailwindColorHex(baseColor, 600);
    const complementary = getComplementaryColor(baseColor);
    const hexSecondary = getTailwindColorHex(complementary, 600);
    
    const newColor = {
      name: `color${localSettings.theme_colors.length + 1}`,
      colorType: 'standard',
      baseColor: baseColor,
      hexBase: hexBase,
      hexSecondary: hexSecondary,
      gradientStyle: `linear-gradient(135deg, ${hexBase}, ${hexSecondary})`,
      buttonStyle: hexBase,
      linkStyle: hexBase,
      textStyle: hexBase
    };
    setLocalSettings(prev => {
      const newColors = [...prev.theme_colors, newColor];
      setEditingColorIndex(newColors.length - 1);
      return {
        ...prev,
        theme_colors: newColors,
        theme_variant: 'custom'
      };
    });
  };

  const updateColor = (colorIndex, field, value) => {
    setLocalSettings(prev => {
      const updated = prev.theme_colors.map((c, idx) => {
        if (idx === colorIndex) {
          const updatedColor = { ...c, [field]: value };
          const currentColorType = updatedColor.colorType || c.colorType || 'standard';
          
          // Handle colorType changes
          if (field === 'colorType') {
            if (value === 'standard' && !updatedColor.baseColor) {
              // Switching to standard - set default baseColor if missing
              updatedColor.baseColor = c.baseColor || 'indigo';
              updatedColor.hexBase = c.hexBase || getTailwindColorHex(updatedColor.baseColor, 600);
            } else if (value === 'custom') {
              // Switching to custom - clear baseColor
              updatedColor.baseColor = null;
            }
          }
          
          // For standard colors: when baseColor changes, update hexBase
          if (currentColorType === 'standard' && field === 'baseColor') {
            updatedColor.hexBase = getTailwindColorHex(value, 600);
          }
          
          // For custom colors: when hexBase changes, ensure colorType is custom
          if (field === 'hexBase') {
            if (currentColorType !== 'custom') {
              updatedColor.colorType = 'custom';
              updatedColor.baseColor = null;
            }
          }
          
          // Handle hexBase
          let hexBase = updatedColor.hexBase || c.hexBase;
          let baseColor = updatedColor.baseColor || c.baseColor;
          
          // If hexBase is missing, generate it
          if (!hexBase) {
            if (baseColor) {
              hexBase = getTailwindColorHex(baseColor, 600);
              updatedColor.hexBase = hexBase;
            } else {
              hexBase = '#4f46e5'; // Default
              updatedColor.hexBase = hexBase;
            }
          }
          
          // Handle hexSecondary
          let hexSecondary;
          
          if (currentColorType === 'standard') {
            // Standard colors always use auto-complementary
            const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
            hexSecondary = complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase;
            updatedColor.hexSecondary = hexSecondary;
          } else if (currentColorType === 'custom') {
            // Custom colors: allow manual setting
            if (field === 'hexSecondary') {
              // User is manually setting hexSecondary
              hexSecondary = value || null;
              updatedColor.hexSecondary = hexSecondary;
            } else if (field === 'baseColor' || field === 'hexBase' || field === 'colorType') {
              // When baseColor/hexBase changes, only auto-generate if hexSecondary is not manually set
              const existingHexSecondary = c.hexSecondary;
              if (!existingHexSecondary || existingHexSecondary === '') {
                // Auto-generate complementary
                const complementaryColor = baseColor ? getComplementaryColor(baseColor) : null;
                hexSecondary = complementaryColor ? getTailwindColorHex(complementaryColor, 600) : hexBase;
                updatedColor.hexSecondary = hexSecondary;
              } else {
                // Keep existing manual value
                hexSecondary = existingHexSecondary;
                updatedColor.hexSecondary = hexSecondary;
              }
            } else {
              // Keep existing value
              hexSecondary = updatedColor.hexSecondary || c.hexSecondary || hexBase;
              updatedColor.hexSecondary = hexSecondary;
            }
          } else {
            // Fallback
            hexSecondary = updatedColor.hexSecondary || c.hexSecondary || hexBase;
            updatedColor.hexSecondary = hexSecondary;
          }
          
          // Always regenerate inline styles when relevant fields change
          if (field === 'baseColor' || field === 'colorType' || field === 'hexBase' || field === 'hexSecondary') {
            const finalHexSecondary = hexSecondary || hexBase;
            updatedColor.gradientStyle = `linear-gradient(135deg, ${hexBase}, ${finalHexSecondary})`;
            updatedColor.buttonStyle = hexBase;
            updatedColor.linkStyle = hexBase;
            updatedColor.textStyle = hexBase;
          }
          
          return updatedColor;
        }
        return c;
      });
      return { ...prev, theme_colors: updated, theme_variant: 'custom' };
    });
  };

  const removeColor = (colorIndex) => {
    if (localSettings.theme_colors.length <= 1) {
      if (showAlert) showAlert('You must have at least one color', 'error');
      return;
    }
    const colorName = localSettings.theme_colors[colorIndex]?.name;
    if (showConfirm) {
      showConfirm(
        `Delete color "${colorName}"?`,
        () => {
          setLocalSettings(prev => ({
            ...prev,
            theme_colors: prev.theme_colors.filter((c, idx) => idx !== colorIndex),
            theme_variant: 'custom'
          }));
          if (editingColorIndex === colorIndex) {
            setEditingColorIndex(null);
          } else if (editingColorIndex !== null && editingColorIndex > colorIndex) {
            // Adjust index if we deleted a color before the one being edited
            setEditingColorIndex(editingColorIndex - 1);
          }
        },
        'Delete Color',
        'Delete',
        'Cancel'
      );
    }
  };

  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 bg-card dark:bg-card-dark border-r border-border dark:border-border-dark h-auto lg:h-screen overflow-y-auto flex flex-col">
        <div className="p-6 border-b border-border-subtle dark:border-border-dark flex items-center justify-between bg-card dark:bg-card-dark sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-surface dark:hover:bg-surface-dark rounded-full text-text-muted dark:text-text-muted-dark">
              <ArrowLeft className="w-5 h-5"/>
            </button>
            <div>
              <h1 className="text-xl font-bold text-text-primary dark:text-text-primary-dark">Organisation Settings</h1>
            </div>
          </div>
          <button 
            onClick={handleSave} 
            disabled={isSaving}
            className="px-5 py-2 bg-confirm dark:bg-confirm-dark text-confirm-text dark:text-confirm-text-dark rounded-full text-sm font-bold flex items-center gap-2 hover:bg-confirm-hover dark:hover:bg-confirm-hover-dark transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : isSuccess ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>

        <div className="flex-1 p-6 space-y-6">
          <div className="space-y-6">
            {/* Organisation Name Section */}
            <div>
              <button
                onClick={() => setIsOrganisationNameOpen(!isOrganisationNameOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Organisation Name</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">This organisation name will be applied to all cards in your organisation. Users cannot change this.</p>
                </div>
                {isOrganisationNameOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>
              
              {isOrganisationNameOpen && (
                <div className="mt-4 p-4 bg-surface dark:bg-card-dark/50 rounded-input">
                  <input
                    type="text"
                    value={localSettings.default_organisation || ''}
                    onChange={(e) => setLocalSettings(prev => ({ ...prev, default_organisation: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-input-bg dark:bg-input-bg-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark"
                    placeholder="Organisation Name"
                  />
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Organisation Banner */}
            <div>
              <button
                onClick={() => setIsOrganisationBannerOpen(!isOrganisationBannerOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Organisation Banner</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Set a default header banner inherited by every card in your organisation.</p>
                </div>
                {isOrganisationBannerOpen ? <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" /> : <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />}
              </button>
              {isOrganisationBannerOpen && (
                <div className="mt-4 p-5 bg-surface dark:bg-card-dark/50 rounded-input space-y-5">
                  {isUploadingOrganisationBanner && <div className="text-center text-sm text-indigo-600 dark:text-indigo-400 animate-pulse">Uploading organisation banner...</div>}
                  <BannerEditor
                    label="Organisation Banner"
                    images={localSettings.organisation_banner || {}}
                    onUpload={handleOrganisationBannerUpload}
                    onChange={updateOrganisationBanner}
                    onRemove={() => setLocalSettings(prev => ({ ...prev, organisation_banner: { banner: '', bannerPositionX: 50, bannerPositionY: 50, bannerZoom: 1 } }))}
                  />
                  <div className="h-px bg-border-subtle dark:bg-border-dark" />
                  <Toggle
                    label="Allow users to override organisation banner"
                    description="When enabled, users may upload their own banner. When disabled, the organisation banner is enforced while personal banners remain stored."
                    checked={localSettings.allow_banner_override === true}
                    onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_banner_override: checked }))}
                  />
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Organisation Card Appearance */}
            <div>
              <button
                onClick={() => setIsCardAppearanceOpen(!isCardAppearanceOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Card Appearance Defaults</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Set the organisation baseline for card preset, body background, typography and button colours.</p>
                </div>
                {isCardAppearanceOpen ? <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" /> : <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />}
              </button>
              {isCardAppearanceOpen && (
                <div className="mt-4 p-5 bg-surface dark:bg-card-dark/50 rounded-input space-y-7">
                  <div className="rounded-input border border-info-border dark:border-info-border-dark bg-info-bg dark:bg-info-bg-dark p-4 text-xs text-info-text dark:text-info-text-dark leading-relaxed">
                    Cards inherit these values unless they have their own override. Turning off <strong>Theme Customisation</strong> below enforces these defaults without deleting stored card overrides.
                  </div>

                  <section>
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">Default Card Preset</h3>
                      <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Controls the built-in light/dark semantic colours used by cards that inherit organisation settings.</p>
                    </div>
                    <CardPresetControls
                      value={normalizeCardAppearance(localSettings.card_appearance).preset}
                      onChange={(value) => updateCardAppearance(current => ({ ...current, preset: value }))}
                    />
                  </section>

                  <div className="h-px bg-border-subtle dark:bg-border-dark" />

                  <section>
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">Default Card Body Background</h3>
                      <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Use Swiish defaults, a corporate colour, or a centrally managed image with non-destructive framing.</p>
                    </div>
                    {isUploadingCardBackground && <div className="text-center text-sm text-indigo-600 dark:text-indigo-400 animate-pulse mb-3">Uploading organisation card background...</div>}
                    <BodyBackgroundControls
                      data={appearanceToEditorData(localSettings.card_appearance)}
                      defaultLabel="Swiish Default"
                      imageCustomisationAllowed={true}
                      onThemeChange={(field, value) => updateCardAppearance(current => ({
                        ...current,
                        bodyBackground: {
                          ...current.bodyBackground,
                          ...(field === 'bodyBackgroundMode' ? { mode: value } : { color: value })
                        }
                      }))}
                      onImageUpload={handleCardBackgroundUpload}
                      onImageChange={(field, value) => updateCardAppearance(current => ({
                        ...current,
                        bodyBackground: {
                          ...current.bodyBackground,
                          ...({
                            bodyBackgroundPositionX: { positionX: value },
                            bodyBackgroundPositionY: { positionY: value },
                            bodyBackgroundZoom: { zoom: value }
                          }[field] || {})
                        }
                      }))}
                      onImageRemove={() => updateCardAppearance(current => ({ ...current, bodyBackground: { ...current.bodyBackground, image: '' } }))}
                    />
                  </section>

                  <div className="h-px bg-border-subtle dark:bg-border-dark" />

                  <section>
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">Default Typography</h3>
                      <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Leave fields at Default to retain Swiish sizing and the selected card preset's semantic colours.</p>
                    </div>
                    <TypographyControls data={appearanceToEditorData(localSettings.card_appearance)} onChange={updateOrganisationTypography} defaultLabel="Card preset / Swiish" />
                  </section>

                  <div className="h-px bg-border-subtle dark:bg-border-dark" />

                  <section>
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark">Default Button Colours</h3>
                      <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">General controls share one palette. Save Contact and Send your details each keep their own palette.</p>
                    </div>
                    <ButtonAppearanceControls styles={normalizeCardAppearance(localSettings.card_appearance).buttonStyles} onChange={updateOrganisationButtonStyle} defaultLabel="Swiish / Card preset" />
                  </section>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Authentication */}
            <div>
              <OidcSettings apiCall={apiCall} showAlert={showAlert} />
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Organization Override Toggles */}
            <div className="space-y-6">
              <button
                onClick={() => setIsCustomizationControlsOpen(!isCustomizationControlsOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">User Customisation Controls</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Control what users in your organisation can customise on their cards.</p>
                </div>
                {isCustomizationControlsOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>
              
              {isCustomizationControlsOpen && (
                <div className="mt-4 space-y-4">
                  {/* Theme Customization Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Theme Customisation</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Control whether users can override your organisation card appearance defaults.</p>
                </div>
                <Toggle
                  label="Allow users to customise card appearance"
                  description="When enabled, users can override card preset, background, typography, button colours and accent. When disabled, organisation appearance defaults are enforced while personal overrides remain stored."
                  checked={localSettings.allow_theme_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_theme_customisation: checked }))}
                />
              </div>

              {/* Image Customization Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Image Customisation</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Control avatar and custom link icon uploads. Organisation banner enforcement is configured separately under Organisation Banner.</p>
                </div>
                <Toggle
                  label="Allow users to upload custom profile and link images"
                  description="When enabled, users can upload avatars and custom link icons, plus personal banners only when organisation banner overrides are permitted. An enforced organisation banner stays locked. Card body backgrounds are governed by Theme Customisation."
                  checked={localSettings.allow_image_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_image_customisation: checked }))}
                />
              </div>

              {/* Links Customization Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Links Customisation</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Control whether users can add custom links to their cards.</p>
                </div>
                <Toggle
                  label="Allow users to add custom links"
                  description="When enabled, users can add custom links to their cards. When disabled, the links section will be locked."
                  checked={localSettings.allow_links_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_links_customisation: checked }))}
                />
              </div>

              {/* Privacy Settings Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Privacy Settings</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Control whether users can change privacy settings on their cards.</p>
                </div>
                <Toggle
                  label="Allow users to change privacy settings"
                  description="When enabled, users can modify privacy options (require interaction, obfuscation, block robots). When disabled, privacy settings will be locked to organisation defaults."
                  checked={localSettings.allow_privacy_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_privacy_customisation: checked }))}
                />
              </div>

              {/* Send Your Details Group */}
              <div className="bg-surface dark:bg-card-dark/50 rounded-input p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary-dark mb-2">Send Your Details</h3>
                  <p className="text-xs text-text-secondary dark:text-text-muted-dark mb-4">Control whether users can change the send-your-details options on their cards (the WhatsApp/email/call CTA shown to visitors).</p>
                </div>
                <Toggle
                  label="Allow users to change send-your-details options"
                  description="When enabled, users can enable/disable the send-your-details CTA and its individual channels. When disabled, these options are locked to their current values."
                  checked={localSettings.allow_send_details_customisation === true}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, allow_send_details_customisation: checked }))}
                />
              </div>
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* App Themes */}
            <div>
              <button
                onClick={() => setIsAppThemesOpen(!isAppThemesOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">App Themes</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Choose the look for the app UI (backgrounds, chrome, buttons).</p>
                </div>
                {isAppThemesOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>

              {isAppThemesOpen && (
                <div className="mt-4 space-y-3">
                  {[
                    { id: 'swiish', title: 'Swiish', desc: 'Original Swiish theme with colors and textures.' },
                    { id: 'minimal', title: 'Minimal', desc: 'Black/white, few grays, no textures, border-only accents.' },
                  ].map(opt => (
                    <label
                      key={opt.id}
                      className="flex items-start gap-3 p-3 rounded-input border border-border dark:border-border-dark bg-surface dark:bg-surface-dark hover:bg-card dark:hover:bg-card-dark transition-colors cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="app-theme"
                        className="mt-1"
                        value={opt.id}
                        checked={(localSettings.theme_variant || 'swiish') === opt.id}
                        onChange={(e) => applyThemePreset(e.target.value)}
                      />
                      <div>
                        <div className="font-medium text-text-primary dark:text-text-primary-dark">{opt.title}</div>
                        <p className="text-sm text-text-secondary dark:text-text-muted-dark">{opt.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="h-px bg-surface dark:bg-surface-dark" />

            {/* Profile Colors */}
            <div>
              <button
                onClick={() => setIsThemeColorsOpen(!isThemeColorsOpen)}
                className="w-full flex items-center justify-between p-4 bg-surface dark:bg-card-dark/50 rounded-input hover:bg-surface dark:hover:bg-card-dark transition-colors"
              >
                <div className="text-left">
                  <h2 className="text-base font-semibold text-text-primary dark:text-text-primary-dark">Profile Colors</h2>
                  <p className="text-sm text-text-secondary dark:text-text-muted-dark mt-1">Manage the color palette available for user cards.</p>
                </div>
                {isThemeColorsOpen ? (
                  <ChevronUp className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-secondary dark:text-text-muted-dark" />
                )}
              </button>
              
              {isThemeColorsOpen && (
                <div className="mt-4 space-y-4">
                  <div className="flex justify-end">
                      <button 
                        onClick={addColor}
                        className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-badge hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Add Color
                      </button>
                    </div>

              <div className="space-y-4">
                {localSettings.theme_colors?.map((color, index) => (
                  <div key={index} className="bg-surface dark:bg-surface-dark p-4 rounded-input border border-border dark:border-border-dark">
                    {editingColorIndex === index ? (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium text-text-primary dark:text-text-primary-dark">Editing: {color.name}</h4>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingColorIndex(null)}
                              className="px-3 py-1 text-sm bg-card dark:bg-surface-dark border border-border dark:border-border-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark text-text-primary dark:text-text-primary-dark"
                            >
                              Done
                            </button>
                            <button
                              onClick={() => removeColor(index)}
                              className="px-3 py-1 text-sm bg-error-bg dark:bg-error-bg-dark text-error dark:text-error-text-dark border border-error-border dark:border-error-border-dark rounded-badge hover:bg-error-bg dark:hover:bg-error-bg-dark"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        
                        <Input
                          label="Color Name"
                          value={color.name}
                          onChange={(v) => updateColor(index, 'name', v)}
                        />

                        <div>
                          <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Color Type</label>
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateColor(index, 'colorType', 'standard')}
                              className={`flex-1 px-4 py-2 rounded-button border transition-all ${
                                (color.colorType || 'standard') === 'standard'
                                  ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300 font-medium'
                                  : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'
                              }`}
                            >
                              Standard Colors
                            </button>
                            <button
                              onClick={() => updateColor(index, 'colorType', 'custom')}
                              className={`flex-1 px-4 py-2 rounded-button border transition-all ${
                                color.colorType === 'custom'
                                  ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-400 text-indigo-700 dark:text-indigo-300 font-medium'
                                  : 'bg-card dark:bg-surface-dark border-border dark:border-border-dark text-text-secondary dark:text-text-secondary-dark hover:bg-surface dark:hover:bg-surface-dark'
                              }`}
                            >
                              Custom Colours
                            </button>
                          </div>
                        </div>

                        {(color.colorType || 'standard') === 'standard' ? (
                          <>
                            <ColorSelector
                              label="Base Color"
                              selectedColor={color.baseColor || 'indigo'}
                              onSelect={(selected) => updateColor(index, 'baseColor', selected)}
                            />
                            <p className="text-xs text-text-muted dark:text-text-muted-dark">Secondary: Auto (complementary)</p>
                          </>
                        ) : (
                          <>
                            <div>
                              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Base Color</label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={color.hexBase || '#4f46e5'}
                                  onChange={(e) => updateColor(index, 'hexBase', e.target.value)}
                                  className="w-16 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={color.hexBase || '#4f46e5'}
                                  onChange={(e) => updateColor(index, 'hexBase', e.target.value)}
                                  placeholder="#4f46e5"
                                  className="flex-1 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark font-mono text-sm"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Secondary Color (optional)</label>
                              <div className="flex gap-2 items-center">
                                <input
                                  type="color"
                                  value={color.hexSecondary || color.hexBase || '#7c3aed'}
                                  onChange={(e) => updateColor(index, 'hexSecondary', e.target.value)}
                                  className="w-16 h-10 rounded-input border border-border dark:border-border-dark cursor-pointer"
                                />
                                <input
                                  type="text"
                                  value={color.hexSecondary || ''}
                                  onChange={(e) => updateColor(index, 'hexSecondary', e.target.value || null)}
                                  placeholder="Leave blank for auto (complementary)"
                                  className="flex-1 px-4 py-2.5 rounded-input border border-border dark:border-border-dark bg-card dark:bg-surface-dark text-text-primary dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-2 focus:ring-focus-ring dark:focus:ring-focus-ring-dark focus:border-action dark:focus:border-action-dark font-mono text-sm"
                                />
                              </div>
                              <p className="text-xs text-text-muted dark:text-text-muted-dark mt-1">Leave blank to use auto (complementary) color</p>
                            </div>
                          </>
                        )}

                        {/* Preview of generated gradient */}
                        <div>
                          <label className="text-sm font-medium text-text-primary dark:text-text-secondary-dark mb-2 block">Preview</label>
                          <div 
                            className="w-full h-16 rounded-container overflow-hidden"
                            style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                          />
                        </div>

                        {/* Collapsible advanced view */}
                        <details className="text-sm">
                          <summary className="cursor-pointer text-text-secondary dark:text-text-muted-dark hover:text-text-primary dark:hover:text-text-primary-dark font-medium mb-2">
                            Advanced: Generated Styles
                          </summary>
                          <div className="bg-card dark:bg-surface-dark p-3 rounded-container border border-border dark:border-border-dark space-y-2 text-xs font-mono">
                            <div><span className="text-text-muted dark:text-text-muted-dark">Gradient Style:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.gradientStyle || 'N/A'}</span></div>
                            <div><span className="text-text-muted dark:text-text-muted-dark">Button Style:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.buttonStyle || 'N/A'}</span></div>
                            <div><span className="text-text-muted dark:text-text-muted-dark">Link Style:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.linkStyle || 'N/A'}</span></div>
                            <div><span className="text-text-muted dark:text-text-muted-dark">Text Style:</span> <span className="text-text-primary dark:text-text-primary-dark">{color.textStyle || 'N/A'}</span></div>
                          </div>
                        </details>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-full"
                            style={{ background: color.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                          />
                          <div>
                            <div className="font-medium text-text-primary dark:text-text-primary-dark">
                              {color.baseColor ? (color.baseColor.charAt(0).toUpperCase() + color.baseColor.slice(1)) : color.name}
                            </div>
                            <div className="text-xs text-text-muted dark:text-text-muted-dark">
                              {color.baseColor ? 'Standard' : 'Custom'} (Secondary: Auto)
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setEditingColorIndex(index)}
                          className="px-3 py-1 text-sm bg-card dark:bg-surface-dark border border-border dark:border-border-dark rounded-button hover:bg-surface dark:hover:bg-surface-dark flex items-center gap-1 text-text-primary dark:text-text-primary-dark"
                        >
                          <Edit3 className="w-3 h-3" /> Edit
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex w-1/2 bg-main dark:bg-main-dark items-center justify-center p-10">
        <div className="bg-card dark:bg-card-dark rounded-card p-8 shadow-lg max-w-md w-full border border-border dark:border-border-dark relative z-10 isolate">
          <h3 className="text-lg font-bold text-text-primary dark:text-text-primary-dark mb-4">Preview</h3>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-text-secondary dark:text-text-muted-dark mb-2">Default Organisation:</div>
              <div className="font-medium text-text-primary dark:text-text-primary-dark">{localSettings.default_organisation || 'Not set'}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary dark:text-text-muted-dark mb-3">Color Effects:</div>
              {(() => {
                const previewColor = editingColorIndex !== null && localSettings.theme_colors?.[editingColorIndex]
                  ? localSettings.theme_colors[editingColorIndex]
                  : localSettings.theme_colors?.[0];
                
                return (
                  <div className="space-y-3">
                    {/* Text example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Text</div>
                      <div 
                        className="text-lg font-medium"
                        style={{ color: previewColor?.textStyle || '#4f46e5' }}
                      >
                        Example Text
                      </div>
                    </div>
                    
                    {/* Link example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Link</div>
                      <a 
                        href="#" 
                        className="inline-block px-4 py-2 rounded-input border border-border dark:border-border-dark transition-all hover:opacity-80"
                        style={{ color: previewColor?.linkStyle || '#4f46e5' }}
                        onClick={(e) => e.preventDefault()}
                      >
                        Example Link
                      </a>
                    </div>
                    
                    {/* Button example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Button</div>
                      <button 
                        className="px-4 py-2 rounded-full font-medium text-white shadow-md transition-transform active:scale-[0.98]"
                        style={{ backgroundColor: previewColor?.buttonStyle || '#4f46e5' }}
                      >
                        Example Button
                      </button>
                    </div>
                    
                    {/* Gradient background example */}
                    <div>
                      <div className="text-xs text-text-muted dark:text-text-muted-dark mb-1">Gradient Background</div>
                      <div 
                        className="w-full h-12 rounded-container overflow-hidden"
                        style={{ background: previewColor?.gradientStyle || 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      <div className="hidden lg:block fixed bottom-4 right-4 z-10 text-center group">
        <div className="flex justify-center">
          <img src="/graphics/Swiish_Logo.svg" alt="Swiish" className="h-4 w-auto dark:hidden swiish-logo" />
          <img src="/graphics/Swiish_Logo_DarkBg.svg" alt="Swiish" className="h-4 w-auto hidden dark:block swiish-logo" />
        </div>
      </div>
    </div>
  );
}
